import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, FileText, Download, Clock, CheckCircle,
  ChevronRight, RefreshCw, Printer, Car, User, DollarSign,
  Package, Shield, Calendar, Trash2, Pencil, FileSignature, Mail, Users, UserCheck, Eye,
  Plus, Send, IdCard, Building2
} from 'lucide-react';
import { US_STATES } from '@/constants/usStates';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import LeadDetailsContent, { getLeadTypeLabel, getLeadStatusColor, TradeInVehicleFields, SensitiveNpiField } from '@/components/admin/LeadDetailsContent';
import { getDealStatusLabel } from '@/constants/adminVocabulary';
import LeadEditModal from '@/components/admin/LeadEditModal';
import SalesTaxRatePanel from '@/components/admin/SalesTaxRatePanel';
import { formatDateMdY, formatDateTimeMdY } from '@/utils/dateFormat';
import AdminTabScroller from '@/components/admin/AdminTabScroller';
import { adminOverlay } from '@/components/admin/adminUi';
import {
  CREDIT_APP_SOURCE_LABELS,
  creditAppSectionHasData,
  creditAppSupplementHasAnyData,
  creditAppSectionSource,
  creditAppHeadingSource,
  creditAppSourcesDiffer,
} from '@/utils/creditAppSupplement';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const TAX_FILING_DEADLINE_DAYS = 20;
const INTERNAL_DOCUMENT_TEMPLATE = 'st_556_ws';
const SPANISH_ONLY_TEMPLATE = 'spanish_transaction_format';

const BILINGUAL_TEMPLATE_BASES = {
  buyers_guide: "Buyer's Guide",
  consent_testimonials: 'Consent Testimonials',
  customer_info_verification: 'Customer Info Verification',
  maintenance_disclosure: 'Maintenance Disclosure',
};

const TEMPLATE_LABEL_OVERRIDES = {
  [INTERNAL_DOCUMENT_TEMPLATE]: 'ST-556 Worksheet',
  [SPANISH_ONLY_TEMPLATE]: 'Spanish Transaction Format',
  st_556_ws: 'ST-556 Worksheet',
  gps_agreement: 'GPS Agreement',
  bill_of_sale: 'Bill of Sale',
  credit_application: 'Credit Application',
  damage_disclosure: 'Damage Disclosure',
  arbitration_agreement: 'Arbitration Agreement',
  odometer_disclosure: 'Odometer Disclosure',
  physical_damage_insurance: 'Physical Damage Insurance',
  extended_warranty_offer: 'Extended Warranty Offer',
  retail_installment: 'Retail Installment',
  spot_delivery: 'Spot Delivery',
  power_of_attorney: 'Power of Attorney',
};

const getTemplateBaseKey = (name) => {
  if (name.endsWith('_en') || name.endsWith('_es')) return name.slice(0, -3);
  return name;
};

const formatMonolingualTemplateLabel = (name) => {
  if (TEMPLATE_LABEL_OVERRIDES[name]) return TEMPLATE_LABEL_OVERRIDES[name];
  return name
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const buildTemplateSelectOptions = (templates) => {
  const byValue = new Map();
  templates.forEach((t) => {
    const base = getTemplateBaseKey(t.name);
    if (BILINGUAL_TEMPLATE_BASES[base]) {
      if (!byValue.has(base)) {
        byValue.set(base, {
          value: base,
          label: `${BILINGUAL_TEMPLATE_BASES[base]} (Bilingual)`,
        });
      }
      return;
    }
    if (!byValue.has(t.name)) {
      byValue.set(t.name, {
        value: t.name,
        label: formatMonolingualTemplateLabel(t.name),
      });
    }
  });
  return [...byValue.values()].sort((a, b) => a.label.localeCompare(b.label));
};

const resolveTemplateNameForApi = (selectValue, language) => {
  if (!selectValue) return '';
  if (BILINGUAL_TEMPLATE_BASES[selectValue]) {
    return `${selectValue}_${language === 'es' ? 'es' : 'en'}`;
  }
  return selectValue;
};

const normalizeDealDocumentsResponse = (data) => {
  if (Array.isArray(data)) {
    const packetDocs = data.filter((doc) => doc.packet_type);
    const singleTemplates = data.filter((doc) => !doc.packet_type);
    const batchMap = new Map();
    packetDocs.forEach((doc) => {
      const batchId = doc.packet_batch_id
        || `legacy:${doc.packet_type}:${(doc.generated_at || '').slice(0, 16)}`;
      if (!batchMap.has(batchId)) {
        const packetType = doc.packet_type;
        const packetMeta = PACKET_TYPES.find((pt) => pt.value === packetType);
        batchMap.set(batchId, {
          batch_id: batchId,
          packet_type: packetType,
          packet_label: packetMeta?.label || formatMonolingualTemplateLabel(packetType),
          generated_at: doc.generated_at,
          requested_language: doc.packet_requested_language || doc.language,
          document_count: 0,
          documents: [],
        });
      }
      const batch = batchMap.get(batchId);
      batch.documents.push(doc);
      batch.document_count = batch.documents.length;
      if (doc.generated_at && (!batch.generated_at || doc.generated_at > batch.generated_at)) {
        batch.generated_at = doc.generated_at;
      }
    });
    const packets = [...batchMap.values()].sort(
      (a, b) => new Date(b.generated_at || 0) - new Date(a.generated_at || 0),
    );
    return {
      packets,
      single_templates: singleTemplates.sort(
        (a, b) => new Date(b.generated_at || 0) - new Date(a.generated_at || 0),
      ),
      total_count: data.length,
    };
  }
  return {
    packets: data.packets || [],
    single_templates: data.single_templates || [],
    total_count: data.total_count ?? 0,
  };
};

const flattenDealDocuments = (groups) => [
  ...(groups.packets || []).flatMap((packet) => packet.documents || []),
  ...(groups.single_templates || []),
];

const formatDocumentTemplateLabel = (templateName) => {
  if (!templateName) return 'Document';
  const base = getTemplateBaseKey(templateName);
  if (BILINGUAL_TEMPLATE_BASES[base] && (templateName.endsWith('_en') || templateName.endsWith('_es'))) {
    const lang = templateName.endsWith('_es') ? 'Spanish' : 'English';
    return `${BILINGUAL_TEMPLATE_BASES[base]} (${lang})`;
  }
  return formatMonolingualTemplateLabel(templateName);
};

const getSingleTemplateLanguageOptions = (templateSelectValue) => {
  if (!templateSelectValue) return [];
  if (templateSelectValue === SPANISH_ONLY_TEMPLATE) {
    return [{ value: 'es', label: 'Spanish' }];
  }
  if (BILINGUAL_TEMPLATE_BASES[templateSelectValue]) {
    return [
      { value: 'en', label: 'English' },
      { value: 'es', label: 'Spanish' },
    ];
  }
  return [{ value: 'en', label: 'English' }];
};

const statusStep = (value) => ({
  value,
  label: getDealStatusLabel(value),
  short: getDealStatusLabel(value),
});

const STATUS_PIPELINE = [
  statusStep('approval_pending'),
  statusStep('approved'),
  statusStep('ready_to_close'),
  statusStep('closing'),
  statusStep('funded'),
  statusStep('delivered'),
  statusStep('filed_taxes'),
  statusStep('archived'),
];

const STATUS_COLORS = {
  approval_pending: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  approved: 'bg-green-500/15 text-green-400 border-green-500/30',
  ready_to_close: 'bg-purple-500/15 text-purple-400 border-purple-500/30',
  closing: 'bg-indigo-500/15 text-indigo-400 border-indigo-500/30',
  funded: 'bg-cyan-500/15 text-cyan-400 border-cyan-500/30',
  delivered: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  filed_taxes: 'bg-teal-500/15 text-teal-400 border-teal-500/30',
  archived: 'bg-gray-500/15 text-gray-400 border-gray-500/30',
};

const PACKET_TYPES = [
  { value: 'approval_pending', label: 'Approval Pending', desc: 'Credit application + customer info verification' },
  { value: 'ready_to_close', label: 'Ready to Close', desc: 'Pre-closing disclosures (damage, GPS, odometer, etc.)' },
  { value: 'full_closing_packet', label: 'Full Closing Packet', desc: 'Bill of sale, retail installment (financing), optional closing docs' },
  { value: 'spot_delivery', label: 'Spot Delivery', desc: 'Spot delivery agreement (after Ready to Close)' },
  { value: 'internal_documents', label: 'Internal Documents', desc: 'ST-556 worksheet — dealership internal use only' },
];

// Single-template generate UI — extend as each template's required inputs are defined.
const SINGLE_TEMPLATE_FIELD_CONFIG = {
  arbitration_agreement: {
    chargeErtFee: false,
  },
  buyers_guide_en: {
    chargeErtFee: false,
    insuranceFields: false,
    warrantyFields: true,
  },
  buyers_guide_es: {
    chargeErtFee: false,
    insuranceFields: false,
    warrantyFields: true,
  },
  credit_application: {
    chargeErtFee: false,
    creditApplicationFields: true,
  },
  damage_disclosure: {
    chargeErtFee: false,
    damageDisclosureFields: true,
    vehicleConditionFields: true,
  },
  extended_warranty_offer: {
    chargeErtFee: false,
    vehicleConditionFields: true,
  },
  retail_installment: {
    chargeErtFee: false,
    retailInstallmentFields: true,
    vehicleConditionFields: true,
    insuranceFields: false,
    warrantyFields: false,
  },
  bill_of_sale: {
    chargeErtFee: true,
    insuranceFields: true,
    billOfSaleInsuranceFields: true,
    warrantyFields: false,
    vehicleConditionFields: true,
  },
  odometer_disclosure: {
    chargeErtFee: false,
    odometerDisclosureFields: true,
  },
  physical_damage_insurance: {
    chargeErtFee: false,
    insuranceFields: true,
    physicalDamageInsuranceFields: true,
    warrantyFields: false,
  },
};

const DAMAGE_DISCLOSURE_QUESTIONS = [
  { key: 'salvaged_vehicle', label: 'Salvaged Vehicle?' },
  { key: 'flood_damage', label: 'Flood Damage?' },
  { key: 'frame_damage', label: 'Frame Damage?' },
  { key: 'reconstructed_vehicle', label: 'Reconstructed Vehicle?' },
  { key: 'clean_title', label: 'Clean Title?' },
];

const VEHICLE_CONDITION_MAX = 3;
const VEHICLE_CONDITION_CUSTOM_MAX = 120;

const PACKET_FIELD_CONFIG = {
  approval_pending: { chargeErtFee: false, creditApplicationFields: true },
  ready_to_close: {
    chargeErtFee: false,
    insuranceFields: true,
    warrantyFields: false,
    damageDisclosureFields: true,
    odometerDisclosureFields: true,
    vehicleConditionFields: true,
  },
  full_closing_packet: {
    chargeErtFee: false,
    insuranceFields: true,
    billOfSaleInsuranceFields: true,
    warrantyFields: false,
    vehicleConditionFields: true,
  },
  spot_delivery: { chargeErtFee: false },
  internal_documents: { chargeErtFee: false },
};

const dealHasFinancing = (deal) => Boolean(deal?.financing_id || deal?.application_id);

const computeRetailFinanceCharge = (amountFinanced, taxRate, months) => {
  const amt = parseFloat(amountFinanced) || 0;
  const rate = parseFloat(taxRate) || 0;
  const mos = parseInt(months, 10) || 0;
  if (!amt || !rate || !mos) return 0;
  return Math.round(amt * (rate / 100) * (mos / 12));
};

const formatMoneyInput = (n) => (Math.round((Number(n) || 0) * 100) / 100).toFixed(2);

const computeInstallmentSplit = (totalCreditsDue, months) => {
  const total = Math.round((Number(totalCreditsDue) || 0) * 100) / 100;
  const mos = parseInt(months, 10) || 0;
  if (!total || mos <= 0) return null;
  if (mos === 1) {
    const single = formatMoneyInput(total);
    return { monthly: single, final: single };
  }
  const monthly = Math.round((total / mos) * 100) / 100;
  const final = Math.round((total - monthly * (mos - 1)) * 100) / 100;
  return { monthly: formatMoneyInput(monthly), final: formatMoneyInput(Math.max(0, final)) };
};

const formatUsd = (n) => {
  const val = Number(n) || 0;
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val);
};

const formatInsuranceCompanyCityStateZip = (city, state, zip) => {
  const parts = [city, state, zip].map((part) => (part || '').trim()).filter(Boolean);
  return parts.join(', ');
};

const resolveGenerateUiConfig = (mode, packetType, templateName, deal, formOptions = {}) => {
  const base = {
    showLanguage: true,
    showSalesperson: true,
    showChargeErtFee: false,
    showInsuranceFields: false,
    showWarrantyFields: false,
    showCreditApplicationFields: false,
    showDamageDisclosureFields: false,
    showOdometerDisclosureFields: false,
    showVehicleConditionFields: false,
    showRetailInstallmentFields: false,
    billOfSaleInsuranceFields: false,
    physicalDamageInsuranceFields: false,
  };

  const financingLinked = dealHasFinancing(deal);

  if (mode === 'packet') {
    const packetCfg = PACKET_FIELD_CONFIG[packetType] || {};
    const legacyExtended = packetCfg.extendedFields ?? false;
    const showRetailInstallmentFields = packetType === 'full_closing_packet' && financingLinked;
    const showChargeErtFee = packetType === 'full_closing_packet'
      ? !financingLinked
      : (packetCfg.chargeErtFee ?? false) && !showRetailInstallmentFields;
    const showInsuranceFields = packetCfg.insuranceFields ?? legacyExtended;
    const physicalDamageInsuranceFields = packetType === 'ready_to_close'
      && Boolean(formOptions.includePhysicalDamageInsurance);

    return {
      ...base,
      showChargeErtFee,
      showInsuranceFields,
      billOfSaleInsuranceFields: Boolean(showInsuranceFields && packetCfg.billOfSaleInsuranceFields),
      physicalDamageInsuranceFields,
      showWarrantyFields: packetCfg.warrantyFields ?? legacyExtended,
      showCreditApplicationFields: packetCfg.creditApplicationFields ?? false,
      showDamageDisclosureFields: packetCfg.damageDisclosureFields ?? false,
      showOdometerDisclosureFields: packetCfg.odometerDisclosureFields ?? false,
      showVehicleConditionFields: Boolean(packetCfg.vehicleConditionFields),
      showRetailInstallmentFields,
    };
  }

  const templateCfg = SINGLE_TEMPLATE_FIELD_CONFIG[templateName] || {};
  const defaultWarranty = ['buyers_guide_en', 'buyers_guide_es'].includes(templateName);
  const showRetailInstallmentFields = Boolean(templateCfg.retailInstallmentFields);
  const showInsuranceFields = templateCfg.insuranceFields ?? false;

  return {
    ...base,
    showLanguage: templateCfg.language ?? true,
    showSalesperson: templateCfg.salespersonName ?? true,
    showChargeErtFee: (templateCfg.chargeErtFee ?? false) && !showRetailInstallmentFields,
    showInsuranceFields,
    billOfSaleInsuranceFields: Boolean(showInsuranceFields && templateCfg.billOfSaleInsuranceFields),
    physicalDamageInsuranceFields: Boolean(showInsuranceFields && templateCfg.physicalDamageInsuranceFields),
    showWarrantyFields: templateCfg.warrantyFields ?? defaultWarranty,
    showCreditApplicationFields: templateCfg.creditApplicationFields ?? false,
    showDamageDisclosureFields: templateCfg.damageDisclosureFields ?? false,
    showOdometerDisclosureFields: templateCfg.odometerDisclosureFields ?? false,
    showVehicleConditionFields: Boolean(templateCfg.vehicleConditionFields),
    showRetailInstallmentFields,
  };
};

const validateVehicleConditionSelections = (issues, custom) => {
  const customText = (custom || '').trim();
  if (issues.length > VEHICLE_CONDITION_MAX) {
    return `At most ${VEHICLE_CONDITION_MAX} predefined vehicle conditions may be selected.`;
  }
  if (customText && issues.length >= VEHICLE_CONDITION_MAX) {
    return `Custom vehicle condition can only be added when fewer than ${VEHICLE_CONDITION_MAX} predefined options are selected.`;
  }
  if (customText.length > VEHICLE_CONDITION_CUSTOM_MAX) {
    return `Custom vehicle condition must be ${VEHICLE_CONDITION_CUSTOM_MAX} characters or fewer.`;
  }
  return null;
};

const formatIsoDateForApi = (isoDate) => {
  if (!isoDate) return null;
  const [year, month, day] = isoDate.split('-');
  if (!year || !month || !day) return isoDate;
  return `${month}/${day}/${year}`;
};

const parseApiDateForForm = (dateStr) => {
  if (!dateStr) return '';
  if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) return dateStr.slice(0, 10);
  const match = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (match) {
    return `${match[3]}-${match[1].padStart(2, '0')}-${match[2].padStart(2, '0')}`;
  }
  return '';
};

const parseCityStateZip = (combined) => {
  if (!combined) return { city: '', state: '', zip: '' };
  const parts = combined.split(',').map((part) => part.trim()).filter(Boolean);
  if (parts.length >= 3) {
    return { city: parts[0], state: parts[1], zip: parts.slice(2).join(', ') };
  }
  if (parts.length === 2) {
    return { city: parts[0], state: parts[1], zip: '' };
  }
  return { city: combined.trim(), state: '', zip: '' };
};

const createDefaultGenerateForm = (deal) => ({
  packet_type: '',
  template_name: '',
  language: deal?.language || 'en',
  salesperson_name: '',
  charge_ert_fee: Boolean(deal?.charge_ert_fee),
  insurance_company: '',
  insurance_agent: '',
  insurance_policy_number: '',
  insurance_company_phone: '',
  insurance_company_address: '',
  insurance_company_city: '',
  insurance_company_state: '',
  insurance_company_zip: '',
  fire_thief_cac_coverage: false,
  collision_coverage: false,
  comprehensive_coverage: false,
  insurance_start_date: '',
  insurance_end_date: '',
  insurance_deductible: '',
  warranty_type: '',
  warranty_level: '',
  manufacturer_warranty_still_applies: false,
  manufacturer_used_vehicle_warranty_applies: false,
  other_used_vehicle_warranty_applies: false,
  service_contract: false,
  include_extended_warranty_offer: false,
  include_physical_damage_insurance: false,
  include_buyers_guide: false,
  include_consent_testimonial: false,
  include_power_of_attorney: false,
  amount_requested: deal?.amount_requested != null ? String(deal.amount_requested) : '',
  payment_date_desired: '',
  income_dependent_on_other: false,
  salvaged_vehicle: null,
  flood_damage: null,
  frame_damage: null,
  reconstructed_vehicle: null,
  clean_title: null,
  vehicle_condition_issues: [],
  vehicle_condition_custom: '',
  retail_tax_rate: deal?.retail_tax_rate != null ? String(deal.retail_tax_rate) : '',
  credits_financed: deal?.amount_financed != null ? String(deal.amount_financed) : (deal?.credits_financed != null ? String(deal.credits_financed) : ''),
  payment_due_date: parseApiDateForForm(deal?.payment_due_date),
  months_to_payoff: deal?.months_to_payoff != null ? String(deal.months_to_payoff) : '',
  monthly_payment: deal?.monthly_payment != null ? String(deal.monthly_payment) : '',
  last_monthly_payment: deal?.last_monthly_payment != null ? String(deal.last_monthly_payment) : '',
  business_use: false,
  insurance_term: '',
  insurance_premium: '',
  credit_life_insurance: false,
  credit_life_insurance_premium: '',
  credit_disability_insurance: false,
  credit_disability_insurance_premium: '',
  other_insurance_type: '',
  other_insurance_premium: '',
  other_insurance_term: '',
  mileage_excess_of_mech_limit: false,
  odometer_discrepancy: false,
});

const buildFormFromGenerationInputs = (inputs, doc, deal) => {
  const defaults = createDefaultGenerateForm(deal);
  const source = inputs || {};
  const templateName = source.template_name || doc?.template_name || '';
  const base = getTemplateBaseKey(templateName);
  const templateSelectValue = BILINGUAL_TEMPLATE_BASES[base] ? base : templateName;
  const { city, state, zip } = parseCityStateZip(source.insurance_company_city_state_zip);

  let vehicleConditionIssues = source.vehicle_condition_issues || [];
  let vehicleConditionCustom = source.vehicle_condition_custom || '';
  if (!vehicleConditionIssues.length && !vehicleConditionCustom && doc?.vehicle_condition_selections?.length) {
    vehicleConditionIssues = [...doc.vehicle_condition_selections];
  }

  return {
    ...defaults,
    template_name: templateSelectValue,
    language: source.language || doc?.language || deal?.language || 'en',
    salesperson_name: source.salesperson_name || doc?.salesperson_name || '',
    charge_ert_fee: Boolean(source.charge_ert_fee),
    insurance_company: source.insurance_company || '',
    insurance_agent: source.insurance_agent || '',
    insurance_policy_number: source.insurance_policy_number || '',
    insurance_company_phone: source.insurance_company_phone || '',
    insurance_company_address: source.insurance_company_street_address || '',
    insurance_company_city: city,
    insurance_company_state: state,
    insurance_company_zip: zip,
    fire_thief_cac_coverage: Boolean(source.fire_thief_cac_coverage),
    collision_coverage: Boolean(source.collision_coverage),
    comprehensive_coverage: Boolean(source.comprehensive_coverage),
    insurance_start_date: source.insurance_start_date || '',
    insurance_end_date: source.insurance_end_date || '',
    insurance_deductible: source.insurance_deductible != null ? String(source.insurance_deductible) : '',
    warranty_type: source.warranty_type || '',
    warranty_level: source.warranty_level || '',
    manufacturer_warranty_still_applies: Boolean(source.manufacturer_warranty_still_applies),
    manufacturer_used_vehicle_warranty_applies: Boolean(source.manufacturer_used_vehicle_warranty_applies),
    other_used_vehicle_warranty_applies: Boolean(source.other_used_vehicle_warranty_applies),
    service_contract: Boolean(source.service_contract),
    include_extended_warranty_offer: Boolean(source.include_extended_warranty_offer),
    include_physical_damage_insurance: Boolean(source.include_physical_damage_insurance),
    include_buyers_guide: Boolean(source.include_buyers_guide),
    include_consent_testimonial: Boolean(source.include_consent_testimonial),
    include_power_of_attorney: Boolean(source.include_power_of_attorney),
    amount_requested: source.amount_requested != null ? String(source.amount_requested) : '',
    payment_date_desired: parseApiDateForForm(source.payment_date_desired),
    income_dependent_on_other: Boolean(source.income_dependent_on_other),
    salvaged_vehicle: source.salvaged_vehicle ?? null,
    flood_damage: source.flood_damage ?? null,
    frame_damage: source.frame_damage ?? null,
    reconstructed_vehicle: source.reconstructed_vehicle ?? null,
    clean_title: source.clean_title ?? null,
    vehicle_condition_issues: vehicleConditionIssues,
    vehicle_condition_custom: vehicleConditionCustom || '',
    retail_tax_rate: source.retail_tax_rate != null ? String(source.retail_tax_rate) : '',
    credits_financed: source.credits_financed != null ? String(source.credits_financed) : '',
    payment_due_date: parseApiDateForForm(source.payment_due_date),
    months_to_payoff: source.months_to_payoff != null ? String(source.months_to_payoff) : '',
    monthly_payment: source.monthly_payment != null ? String(source.monthly_payment) : '',
    last_monthly_payment: source.last_monthly_payment != null ? String(source.last_monthly_payment) : '',
    business_use: Boolean(source.business_use),
    insurance_term: source.insurance_term || '',
    insurance_premium: source.insurance_premium != null ? String(source.insurance_premium) : '',
    credit_life_insurance: Boolean(source.credit_life_insurance),
    credit_life_insurance_premium: source.credit_life_insurance_premium != null
      ? String(source.credit_life_insurance_premium)
      : '',
    credit_disability_insurance: Boolean(source.credit_disability_insurance),
    credit_disability_insurance_premium: source.credit_disability_insurance_premium != null
      ? String(source.credit_disability_insurance_premium)
      : '',
    other_insurance_type: source.other_insurance_type || '',
    other_insurance_premium: source.other_insurance_premium != null ? String(source.other_insurance_premium) : '',
    other_insurance_term: source.other_insurance_term || '',
    mileage_excess_of_mech_limit: Boolean(source.mileage_excess_of_mech_limit),
    odometer_discrepancy: Boolean(source.odometer_discrepancy),
  };
};

const formatDate = (ds) => formatDateTimeMdY(ds);

const dealAllowsInternalDocuments = (deal) => ['delivered', 'filed_taxes'].includes(deal?.status);

const dealIsFinancingApplicant = (deal) => {
  if (deal?.payment_type === 'cash') return false;
  if (deal?.payment_type === 'financing') return true;
  return Boolean(
    deal?.financing_id
    || String(deal?.application_id || '').startsWith('fin')
  );
};

const DELIVERED_STATUS_INDEX = STATUS_PIPELINE.findIndex((s) => s.value === 'delivered');

const RETIRED_STATUSES = new Set(['closing']);
const CASH_HIDDEN_STATUSES = new Set(['approval_pending', 'approved', 'closing', 'funded']);
const FINANCING_HIDDEN_STATUSES = new Set(['ready_to_close', 'closing']);

const getVisibleStatusPipeline = (deal) => {
  const hidden = dealIsFinancingApplicant(deal) ? FINANCING_HIDDEN_STATUSES : CASH_HIDDEN_STATUSES;
  return STATUS_PIPELINE.filter((s) => !hidden.has(s.value) && !RETIRED_STATUSES.has(s.value));
};

const getAvailableStatusOptions = (deal) => getVisibleStatusPipeline(deal);

/** Index in the visible pipeline; hidden/retired current statuses map to the last completed visible step. */
const getVisibleCurrentIndex = (deal, visiblePipeline) => {
  const exact = visiblePipeline.findIndex((s) => s.value === deal?.status);
  if (exact >= 0) return exact;
  const fullIdx = STATUS_PIPELINE.findIndex((s) => s.value === deal?.status);
  if (fullIdx < 0) return -1;
  let last = -1;
  visiblePipeline.forEach((s, i) => {
    const vi = STATUS_PIPELINE.findIndex((x) => x.value === s.value);
    if (vi >= 0 && vi < fullIdx) last = i;
  });
  return last;
};

const getStatusPipelineIndex = (status) => STATUS_PIPELINE.findIndex((s) => s.value === status);

const getStatusLabel = (status) => getDealStatusLabel(status);

const analyzeStatusChange = (fromStatus, toStatus) => {
  const fromIdx = getStatusPipelineIndex(fromStatus);
  const toIdx = getStatusPipelineIndex(toStatus);
  return {
    fromIdx,
    toIdx,
    isBackward: fromIdx >= 0 && toIdx >= 0 && toIdx < fromIdx,
    resetsTaxCountdown: toIdx >= 0 && toIdx < DELIVERED_STATUS_INDEX,
  };
};

const shouldShowTaxFilingCountdown = (deal) => (
  deal?.status === 'delivered' && Boolean(deal?.delivered_at) && !deal?.taxes_filed_at
);

const getTaxFilingDeadline = (deal) => {
  if (deal?.tax_filing_deadline_at) return new Date(deal.tax_filing_deadline_at);
  if (!deal?.delivered_at) return null;
  const delivered = new Date(deal.delivered_at);
  delivered.setDate(delivered.getDate() + TAX_FILING_DEADLINE_DAYS);
  return delivered;
};

function TaxFilingCountdownBanner({ deal, onMarkTaxesFiled, markingTaxesFiled }) {
  const deadline = getTaxFilingDeadline(deal);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (!deadline) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [deadline]);

  if (!shouldShowTaxFilingCountdown(deal) || !deadline) return null;

  const remainingMs = deadline.getTime() - now;
  const expired = remainingMs <= 0;
  const totalSeconds = Math.max(0, Math.floor(remainingMs / 1000));
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const urgent = !expired && days < 5;
  const borderClass = expired
    ? 'border-red-500/50 bg-red-500/10'
    : urgent
      ? 'border-amber-500/50 bg-amber-500/10'
      : 'border-emerald-500/40 bg-emerald-500/10';
  const titleClass = expired ? 'text-red-300' : urgent ? 'text-amber-300' : 'text-emerald-300';

  return (
    <div
      className={`mb-6 border p-4 ${borderClass}`}
      data-testid="tax-filing-countdown"
    >
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <div className={`flex items-center gap-2 text-sm font-['Unbounded'] font-semibold ${titleClass}`}>
            <Clock className="w-4 h-4" />
            {expired ? 'ST-556 Tax Filing Deadline Passed' : 'ST-556 Tax Filing Deadline'}
          </div>
          <p className="text-xs font-['Manrope'] text-white/50 mt-1">
            {expired
              ? `This deal was delivered on ${formatDate(deal.delivered_at)}. File ST-556 internal documents immediately.`
              : `Vehicle delivered ${formatDate(deal.delivered_at)}. You have ${TAX_FILING_DEADLINE_DAYS} days to file taxes for this deal.`}
          </p>
          <p className="text-[11px] font-['Manrope'] text-white/35 mt-1">
            Deadline: {formatDate(deadline.toISOString())}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          {!expired && (
            <div className="flex items-center gap-2 sm:gap-3">
              {[
                { label: 'Days', value: days },
                { label: 'Hrs', value: hours },
                { label: 'Min', value: minutes },
                { label: 'Sec', value: seconds },
              ].map((unit) => (
                <div
                  key={unit.label}
                  className={`min-w-[56px] text-center px-2 py-2 border ${
                    urgent ? 'border-amber-500/40 bg-amber-500/15' : 'border-emerald-500/30 bg-black/20'
                  }`}
                >
                  <div className={`text-xl font-['Unbounded'] font-bold tabular-nums ${urgent ? 'text-amber-300' : 'text-emerald-300'}`}>
                    {String(unit.value).padStart(2, '0')}
                  </div>
                  <div className="text-[10px] font-['Manrope'] text-white/40 uppercase">{unit.label}</div>
                </div>
              ))}
            </div>
          )}
          <button
            type="button"
            onClick={onMarkTaxesFiled}
            disabled={markingTaxesFiled}
            data-testid="mark-taxes-filed-btn"
            className="px-3 py-2 bg-teal-600 hover:bg-teal-500 text-white text-xs font-['Manrope'] font-semibold whitespace-nowrap disabled:opacity-50 self-start sm:self-center"
          >
            {markingTaxesFiled ? 'Saving...' : 'Mark Filed Taxes'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function DealDetailPage() {
  const { dealId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [deal, setDeal] = useState(null);
  const [documentGroups, setDocumentGroups] = useState({ packets: [], single_templates: [], total_count: 0 });
  const [deletedDocumentGroups, setDeletedDocumentGroups] = useState({ packets: [], single_templates: [], total_count: 0, retention_days: 30 });
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('customer-information');
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [editDocument, setEditDocument] = useState(null);
  const [timelineEvents, setTimelineEvents] = useState([]);
  const [customerInfo, setCustomerInfo] = useState(null);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [statusConfirmDialog, setStatusConfirmDialog] = useState(null);
  const [markingTaxesFiled, setMarkingTaxesFiled] = useState(false);
  const [pricingEditRequest, setPricingEditRequest] = useState(null);
  const handlePricingEditRequestHandled = useCallback(() => setPricingEditRequest(null), []);

  const fetchDeal = useCallback(async () => {
    try {
      const [dealRes, docsRes, deletedRes, templatesRes, timelineRes, customerRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/deals/${dealId}`, { headers }),
        axios.get(`${BACKEND_URL}/api/admin/deals/${dealId}/documents`, { headers }),
        axios.get(`${BACKEND_URL}/api/admin/deals/${dealId}/documents/deleted`, { headers }),
        axios.get(`${BACKEND_URL}/api/admin/templates`, { headers }),
        axios.get(`${BACKEND_URL}/api/admin/deals/${dealId}/timeline`, { headers }),
        axios.get(`${BACKEND_URL}/api/admin/deals/${dealId}/customer-information`, { headers }),
      ]);
      setDeal(dealRes.data);
      setDocumentGroups(normalizeDealDocumentsResponse(docsRes.data));
      setDeletedDocumentGroups({
        ...normalizeDealDocumentsResponse(deletedRes.data),
        retention_days: deletedRes.data?.retention_days || 30,
      });
      setTemplates(templatesRes.data);
      setTimelineEvents(timelineRes.data?.events || []);
      setCustomerInfo(customerRes.data);
    } catch (err) {
      toast.error('Failed to load deal');
      navigate('/admin/deals');
    } finally {
      setLoading(false);
    }
  }, [dealId, headers, navigate]);

  useEffect(() => { fetchDeal(); }, [fetchDeal]);

  const requestStatusChange = (newStatus) => {
    if (!deal || newStatus === deal.status) return;
    const analysis = analyzeStatusChange(deal.status, newStatus);
    setStatusConfirmDialog({
      newStatus,
      currentStatus: deal.status,
      ...analysis,
    });
  };

  const handleMarkTaxesFiled = async () => {
    setMarkingTaxesFiled(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/deals/${dealId}/mark-taxes-filed`, null, { headers });
      toast.success('Taxes marked as filed — countdown stopped');
      fetchDeal();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to mark taxes as filed');
    } finally {
      setMarkingTaxesFiled(false);
    }
  };

  const confirmStatusChange = async () => {
    if (!statusConfirmDialog?.newStatus) return;
    const { newStatus } = statusConfirmDialog;
    setStatusUpdating(true);
    try {
      await axios.put(`${BACKEND_URL}/api/admin/deals/${dealId}/status`, null, {
        params: { new_status: newStatus },
        headers,
      });
      toast.success(`Status updated to ${getStatusLabel(newStatus)}`);
      setStatusConfirmDialog(null);
      fetchDeal();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update status');
    } finally {
      setStatusUpdating(false);
    }
  };

  if (loading) {
    return <div className="p-12 text-center text-white/40 font-['Manrope']">Loading deal...</div>;
  }
  if (!deal) return null;

  const visiblePipeline = getVisibleStatusPipeline(deal);
  const currentIdx = getVisibleCurrentIndex(deal, visiblePipeline);
  const statusColor = STATUS_COLORS[deal.status] || STATUS_COLORS.approval_pending;
  const nextJob = nextDealJob(deal, customerInfo, documentGroups);

  return (
    <div className="p-4 sm:p-6 max-w-[1400px] mx-auto" data-testid="deal-detail-page">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 mb-4 text-sm font-['Manrope']">
        <button onClick={() => navigate('/admin/deals')} className="text-white/40 hover:text-white transition-colors inline-flex items-center gap-1 min-h-11" data-testid="back-to-deals">
          <ArrowLeft className="w-4 h-4" />
          Deals
        </button>
      </div>

      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-xl font-['Unbounded'] font-bold text-white mb-1" data-testid="deal-customer-name">
            {deal.customer_name}
          </h1>
          {nextJob ? (
            <p className="text-sm text-white/70 font-['Manrope'] mb-2" data-testid="deal-next-job">
              {nextJob}
            </p>
          ) : null}
          <div className="flex items-center gap-3 flex-wrap">
            <span className={`px-3 py-1 text-xs font-['Manrope'] font-medium border ${statusColor}`} data-testid="deal-status-badge">
              {getDealStatusLabel(deal.status)}
            </span>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <select
            value={deal.status}
            onChange={(e) => requestStatusChange(e.target.value)}
            disabled={statusUpdating}
            data-testid="deal-status-select"
            className="bg-white/5 border border-white/10 text-white text-sm px-3 py-2 font-['Manrope'] focus:outline-none focus:border-blue-500/50 [&>option]:bg-gray-900"
          >
            {getAvailableStatusOptions(deal).map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
          <button
            onClick={() => { setEditDocument(null); setShowGenerateModal(true); }}
            data-testid="generate-docs-btn"
            className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-['Manrope'] font-semibold transition-colors"
          >
            <Printer className="w-4 h-4" />
            Generate Documents
          </button>
        </div>
      </div>

      <TaxFilingCountdownBanner
        deal={deal}
        onMarkTaxesFiled={handleMarkTaxesFiled}
        markingTaxesFiled={markingTaxesFiled}
      />

      {statusConfirmDialog && (
        <div className={adminOverlay('z-[70]')} data-testid="deal-status-confirm-dialog">
          <div className="bg-[#0a0a0a] border border-white/10 w-full max-w-lg p-5">
            <h3 className="text-base font-['Unbounded'] font-semibold text-white mb-2">Change Deal Status?</h3>
            <p className="text-sm font-['Manrope'] text-white/70 mb-4">
              Change status from{' '}
              <span className="text-white">{getStatusLabel(statusConfirmDialog.currentStatus)}</span>
              {' '}to{' '}
              <span className="text-white">{getStatusLabel(statusConfirmDialog.newStatus)}</span>?
            </p>
            {statusConfirmDialog.isBackward && (
              <p className="text-sm font-['Manrope'] text-amber-300/90 border border-amber-500/25 bg-amber-500/5 px-3 py-2 mb-4">
                You are moving this deal backward in the workflow.
                {statusConfirmDialog.resetsTaxCountdown
                  ? ' The ST-556 tax filing countdown will be reset and hidden until the deal is marked Delivered again.'
                  : ''}
              </p>
            )}
            {!statusConfirmDialog.isBackward && statusConfirmDialog.newStatus === 'delivered' && (
              <p className="text-sm font-['Manrope'] text-emerald-300/90 border border-emerald-500/25 bg-emerald-500/5 px-3 py-2 mb-4">
                Marking this deal as Delivered will start the {TAX_FILING_DEADLINE_DAYS}-day ST-556 tax filing countdown.
              </p>
            )}
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setStatusConfirmDialog(null)}
                className="px-4 py-2 border border-white/10 text-white/70 text-sm font-['Manrope'] hover:bg-white/5"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmStatusChange}
                disabled={statusUpdating}
                data-testid="deal-status-confirm-btn"
                className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-['Manrope'] font-semibold disabled:opacity-50"
              >
                {statusUpdating ? 'Updating...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Status Pipeline */}
      <div className="bg-white/5 border border-white/10 p-4 mb-6 overflow-x-auto" data-testid="status-pipeline">
        <div className="flex items-center min-w-[600px]">
          {visiblePipeline.map((step, i) => {
            const isCompleted = currentIdx >= 0 && i < currentIdx;
            const isCurrent = i === currentIdx;
            return (
              <React.Fragment key={step.value}>
                <div className="flex flex-col items-center flex-shrink-0">
                  <div className={`w-8 h-8 flex items-center justify-center text-xs font-['Manrope'] font-bold border-2 transition-all ${
                    isCurrent ? 'bg-blue-500 border-blue-500 text-white' :
                    isCompleted ? 'bg-green-500/20 border-green-500 text-green-400' :
                    'bg-white/5 border-white/20 text-white/30'
                  }`}>
                    {isCompleted ? <CheckCircle className="w-4 h-4" /> : i + 1}
                  </div>
                  <span className={`text-[10px] font-['Manrope'] mt-1 ${isCurrent ? 'text-blue-400' : isCompleted ? 'text-green-400/60' : 'text-white/20'}`}>
                    {step.short}
                  </span>
                </div>
                {i < visiblePipeline.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-1 ${isCompleted ? 'bg-green-500/40' : 'bg-white/10'}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      <AdminTabScroller
        activeId={activeTab}
        onChange={setActiveTab}
        tabs={[
          { id: 'customer-information', label: 'Customer Information' },
          { id: 'vehicle-information', label: 'Vehicle Information' },
          { id: 'overview', label: 'Deal Overview' },
          { id: 'documents', label: 'Documents' },
          { id: 'timeline', label: 'Timeline' },
          { id: 'recently-deleted', label: 'Recently Deleted Documents' },
        ]}
      />

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <OverviewTab
          deal={deal}
          dealId={dealId}
          token={token}
          onRefresh={fetchDeal}
          pricingEditRequest={pricingEditRequest}
          onPricingEditRequestHandled={handlePricingEditRequestHandled}
          tradeInLead={(customerInfo?.leads || []).find((lead) => lead.type === 'trade_in') || null}
        />
      )}
      {activeTab === 'vehicle-information' && (
        <VehicleInformationTab
          deal={deal}
          dealId={dealId}
          token={token}
          onRefresh={fetchDeal}
          tradeInLead={(customerInfo?.leads || []).find((lead) => lead.type === 'trade_in') || null}
          onVehicleLinked={(linkedVehicle) => {
            setPricingEditRequest({
              salePrice: linkedVehicle?.price,
            });
            setActiveTab('overview');
          }}
        />
      )}
      {activeTab === 'customer-information' && (
        <CustomerInformationTab
          customerInfo={customerInfo}
          deal={deal}
          dealId={dealId}
          token={token}
          onRefresh={fetchDeal}
        />
      )}
      {activeTab === 'documents' && (
        <DocumentsTab
          documentGroups={documentGroups}
          token={token}
          onRefresh={fetchDeal}
          dealId={deal.id}
          onEdit={(doc) => { setShowGenerateModal(false); setEditDocument(doc); }}
          onGenerate={() => { setEditDocument(null); setShowGenerateModal(true); }}
        />
      )}
      {activeTab === 'timeline' && <TimelineTab events={timelineEvents} />}
      {activeTab === 'recently-deleted' && (
        <RecentlyDeletedDocumentsTab
          documentGroups={deletedDocumentGroups}
          retentionDays={deletedDocumentGroups.retention_days || 30}
          token={token}
          onRefresh={fetchDeal}
        />
      )}

      {/* Generate Document Modal */}
      {(showGenerateModal || editDocument) && (
        <GenerateDocumentModal
          deal={deal}
          dealId={dealId}
          customerInfo={customerInfo}
          templates={templates}
          token={token}
          editDocument={editDocument}
          onClose={() => { setShowGenerateModal(false); setEditDocument(null); }}
          onCustomerInfoRefresh={fetchDeal}
          onSuccess={() => {
            const wasEdit = Boolean(editDocument);
            setShowGenerateModal(false);
            setEditDocument(null);
            fetchDeal();
            toast.success(wasEdit ? 'Document updated successfully' : 'Documents generated successfully');
          }}
        />
      )}
    </div>
  );
}

const LINKED_VIA_LABELS = {
  trade_in_id: 'Trade-in lead',
  financing_id: 'Financing application',
  prequalification_id: 'Pre-qualification',
  application_id: 'Application / lead record',
  lead_id: 'Primary lead',
};

const PAYMENT_TYPE_LABELS = {
  cash: 'Cash Purchase',
  financing: 'Financing',
};

const DL_INFO_SOURCE_LABELS = {
  admin: 'Added by dealership',
  customer_request: 'Submitted by customer',
  application: 'Submitted with financing application',
};

const emptyDriverLicenseForm = {
  driver_license_number: '',
  dl_issuing_county: '',
  dl_issuing_state: '',
  dl_expiry_date: '',
  date_of_birth: '',
};

const CREDIT_APP_SECTION_OPTIONS = [
  { key: 'landlord', label: 'Current Landlord Info' },
  { key: 'previous_residential', label: 'Previous Residential Info' },
  { key: 'previous_employment', label: 'Previous Employment Info' },
];

const emptyCreditAppSupplementForm = {
  landlord_name: '',
  landlord_mobile: '',
  previous_street_address: '',
  previous_residence_years: '',
  previous_residence_months: '',
  previous_city: '',
  previous_state: '',
  previous_zip: '',
  previous_rent_mortgage: '',
  previous_employer: '',
  previous_job_years: '',
  previous_job_months: '',
};

const OTHER_INCOME_FREQUENCY_OPTIONS = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Bi-weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' },
];

const emptyOtherIncomeForm = {
  other_income_amount: '',
  other_income_frequency: '',
  source_of_other_income: '',
  reference: '',
  relationship: '',
  phone_number: '',
  address: '',
  bank_reference: '',
  bank_phone_number: '',
  bank_account_type: '',
};

const otherIncomeHasAnyData = (info) => {
  if (!info) return false;
  const keys = [
    'other_income_amount', 'other_income_frequency', 'source_of_other_income', 'reference',
    'relationship', 'phone_number', 'address', 'bank_reference', 'bank_phone_number', 'bank_account_type',
  ];
  return keys.some((key) => String(info[key] || '').trim());
};

const populateOtherIncomeFormFromInfo = (info) => ({
  other_income_amount: info?.other_income_amount || '',
  other_income_frequency: info?.other_income_frequency || '',
  source_of_other_income: info?.source_of_other_income || '',
  reference: info?.reference || '',
  relationship: info?.relationship || '',
  phone_number: info?.phone_number || '',
  address: info?.address || '',
  bank_reference: info?.bank_reference || '',
  bank_phone_number: info?.bank_phone_number || '',
  bank_account_type: info?.bank_account_type || '',
});

const validateOtherIncomeForm = (form) => {
  const required = [
    form.other_income_amount,
    form.other_income_frequency,
    form.source_of_other_income,
    form.reference,
    form.relationship,
    form.phone_number,
    form.address,
    form.bank_reference,
    form.bank_phone_number,
  ];
  if (required.some((v) => !String(v).trim())) {
    return 'Please complete all other income fields';
  }
  if (!form.bank_account_type) {
    return 'Please select Checking or Saving';
  }
  return null;
};

const buildOtherIncomePayload = (form, markIncomeDependent = false) => ({
  other_income_amount: form.other_income_amount.trim(),
  other_income_frequency: form.other_income_frequency,
  source_of_other_income: form.source_of_other_income.trim(),
  reference: form.reference.trim(),
  relationship: form.relationship.trim(),
  phone_number: form.phone_number.trim(),
  address: form.address.trim(),
  bank_reference: form.bank_reference.trim(),
  bank_phone_number: form.bank_phone_number.trim(),
  bank_account_type: form.bank_account_type,
  ...(markIncomeDependent ? { mark_income_dependent: true } : {}),
});

const CreditAppSourceTag = ({ source }) => {
  if (!source) return null;
  return (
    <span className="text-xs font-['Manrope'] text-white/35">
      {CREDIT_APP_SOURCE_LABELS[source] || source}
    </span>
  );
};

function CardEditButton({ onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="absolute top-4 right-4 p-1.5 text-white/40 hover:text-white hover:bg-white/10 transition-colors"
      data-testid={testId}
      title="Edit information"
    >
      <Pencil className="w-4 h-4" />
    </button>
  );
}

function DriverLicenseInfoCard({ info, onEdit, dealId }) {
  if (!info) return null;
  return (
    <div className="relative bg-white/5 border border-white/10 p-6" data-testid="driver-license-info-card">
      {onEdit && <CardEditButton onClick={onEdit} testId="edit-driver-license-card" />}
      <div className="flex flex-wrap items-center gap-3 mb-4 pb-4 border-b border-white/10 pr-10">
        <span className="px-3 py-1 text-xs font-['Manrope'] border bg-indigo-500/10 text-indigo-400 border-indigo-500/30 flex items-center gap-1.5">
          <IdCard className="w-3.5 h-3.5" />
          Driver License Information
        </span>
        {info.source && (
          <span className="text-xs font-['Manrope'] text-white/35">
            {DL_INFO_SOURCE_LABELS[info.source] || info.source}
          </span>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <SensitiveNpiField label="Driver License Number" last4={info.dl_last4} leadId={dealId} source="deal" field="driver_license" kind="dl" />
        <InfoRow label="DL Issuing County" value={info.dl_issuing_county} />
        <InfoRow label="DL Issuing State" value={info.dl_issuing_state} />
        <InfoRow label="DL Expiring Date" value={formatDateMdY(info.dl_expiry_date)} />
        <InfoRow label="Date of Birth" value={formatDateMdY(info.date_of_birth)} />
      </div>
    </div>
  );
}

function OtherIncomeFormFields({ form, setForm, fieldClassName }) {
  const inputClass = fieldClassName || "w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50";
  const labelClass = "block text-xs font-['Manrope'] text-white/60 mb-1.5";

  return (
    <div className="space-y-4">
      <div>
        <label className={labelClass}>Other Income Amount *</label>
        <input type="number" min="0" value={form.other_income_amount} onChange={(e) => setForm({ ...form, other_income_amount: e.target.value })} data-testid="other-income-amount" className={inputClass} />
      </div>
      <div>
        <label className={labelClass}>Other Income Frequency *</label>
        <select value={form.other_income_frequency} onChange={(e) => setForm({ ...form, other_income_frequency: e.target.value })} data-testid="other-income-frequency" className={`${inputClass} [&>option]:bg-gray-900`}>
          <option value="">Select frequency...</option>
          {OTHER_INCOME_FREQUENCY_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
        </select>
      </div>
      <div>
        <label className={labelClass}>Source of Other Income *</label>
        <input type="text" value={form.source_of_other_income} onChange={(e) => setForm({ ...form, source_of_other_income: e.target.value })} data-testid="other-income-source" className={inputClass} />
      </div>
      <div>
        <label className={labelClass}>Reference *</label>
        <input type="text" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} data-testid="other-income-reference" className={inputClass} />
      </div>
      <div>
        <label className={labelClass}>Relationship *</label>
        <input type="text" value={form.relationship} onChange={(e) => setForm({ ...form, relationship: e.target.value })} data-testid="other-income-relationship" className={inputClass} />
      </div>
      <div>
        <label className={labelClass}>Phone Number *</label>
        <input type="tel" value={form.phone_number} onChange={(e) => setForm({ ...form, phone_number: e.target.value })} data-testid="other-income-phone" className={inputClass} />
      </div>
      <div>
        <label className={labelClass}>Address *</label>
        <input type="text" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="other-income-address" className={inputClass} />
      </div>
      <div>
        <label className={labelClass}>Bank Reference *</label>
        <input type="text" value={form.bank_reference} onChange={(e) => setForm({ ...form, bank_reference: e.target.value })} data-testid="other-income-bank-reference" className={inputClass} />
      </div>
      <div>
        <label className={labelClass}>Bank Phone Number *</label>
        <input type="tel" value={form.bank_phone_number} onChange={(e) => setForm({ ...form, bank_phone_number: e.target.value })} data-testid="other-income-bank-phone" className={inputClass} />
      </div>
      <div>
        <label className={labelClass}>Account Type *</label>
        <div className="flex gap-4 mt-1">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.bank_account_type === 'checking'}
              onChange={() => setForm({ ...form, bank_account_type: 'checking' })}
              data-testid="other-income-checking"
              className="accent-amber-500"
            />
            <span className="text-sm font-['Manrope'] text-white/80">Checking</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.bank_account_type === 'saving'}
              onChange={() => setForm({ ...form, bank_account_type: 'saving' })}
              data-testid="other-income-saving"
              className="accent-amber-500"
            />
            <span className="text-sm font-['Manrope'] text-white/80">Saving</span>
          </label>
        </div>
      </div>
    </div>
  );
}

function OtherIncomeInfoCard({ info, onEdit }) {
  if (!info || !otherIncomeHasAnyData(info)) return null;
  const frequencyLabel = OTHER_INCOME_FREQUENCY_OPTIONS.find((o) => o.value === info.other_income_frequency)?.label
    || info.other_income_frequency_label
    || info.other_income_frequency;

  return (
    <div className="relative bg-white/5 border border-white/10 p-6" data-testid="other-income-info-card">
      {onEdit && <CardEditButton onClick={onEdit} testId="edit-other-income-card" />}
      <div className="flex flex-wrap items-center gap-3 mb-4 pb-4 border-b border-white/10 pr-10">
        <span className="px-3 py-1 text-xs font-['Manrope'] border bg-amber-500/10 text-amber-400 border-amber-500/30 flex items-center gap-1.5">
          <DollarSign className="w-3.5 h-3.5" />
          Other Income Information
        </span>
        {info.source && (
          <span className="text-xs font-['Manrope'] text-white/35">
            {DL_INFO_SOURCE_LABELS[info.source] || info.source}
          </span>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <InfoRow label="Other Income Amount" value={info.other_income_amount} />
        <InfoRow label="Frequency" value={frequencyLabel} />
        <InfoRow label="Source of Other Income" value={info.source_of_other_income} className="md:col-span-2" />
        <InfoRow label="Reference" value={info.reference} />
        <InfoRow label="Relationship" value={info.relationship} />
        <InfoRow label="Phone Number" value={info.phone_number} />
        <InfoRow label="Address" value={info.address} className="md:col-span-2" />
        <InfoRow label="Bank Reference" value={info.bank_reference} />
        <InfoRow label="Bank Phone Number" value={info.bank_phone_number} />
        <InfoRow label="Account Type" value={info.bank_account_type === 'checking' ? 'Checking' : info.bank_account_type === 'saving' ? 'Saving' : ''} />
      </div>
    </div>
  );
}

function CreditAppSupplementInfoCard({ info, onEdit }) {
  if (!info || !creditAppSupplementHasAnyData(info)) return null;
  const landlord = info.landlord || {};
  const prevRes = info.previous_residential || {};
  const prevEmp = info.previous_employment || {};
  const mixedSources = creditAppSourcesDiffer(info);
  const headingSource = creditAppHeadingSource(info);

  return (
    <div className="relative bg-white/5 border border-white/10 p-6" data-testid="credit-app-supplement-info-card">
      {onEdit && <CardEditButton onClick={onEdit} testId="edit-credit-app-card" />}
      <div className="flex flex-wrap items-center gap-3 mb-4 pb-4 border-b border-white/10 pr-10">
        <span className="px-3 py-1 text-xs font-['Manrope'] border bg-violet-500/10 text-violet-400 border-violet-500/30 flex items-center gap-1.5">
          <FileText className="w-3.5 h-3.5" />
          Credit Application — Additional Information
        </span>
        <CreditAppSourceTag source={headingSource} />
      </div>

      {creditAppSectionHasData(info, 'landlord') && (
        <div className="mb-5">
          <h4 className="text-xs font-['Unbounded'] text-white/60 mb-3 flex flex-wrap items-center gap-2">
            Current Landlord
            {mixedSources && <CreditAppSourceTag source={creditAppSectionSource(info, 'landlord')} />}
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InfoRow label="Landlord Name" value={landlord.landlord_name} />
            <InfoRow label="Landlord Mobile" value={landlord.landlord_mobile} />
          </div>
        </div>
      )}

      {creditAppSectionHasData(info, 'previous_residential') && (
        <div className="mb-5">
          <h4 className="text-xs font-['Unbounded'] text-white/60 mb-3 flex flex-wrap items-center gap-2">
            Previous Residence
            {mixedSources && <CreditAppSourceTag source={creditAppSectionSource(info, 'previous_residential')} />}
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InfoRow label="Street Address" value={prevRes.previous_street_address} className="md:col-span-2" />
            <InfoRow label="Years Stayed" value={prevRes.previous_residence_years} />
            <InfoRow label="Months Stayed" value={prevRes.previous_residence_months} />
            <InfoRow label="City" value={prevRes.previous_city} />
            <InfoRow label="State" value={prevRes.previous_state} />
            <InfoRow label="ZIP" value={prevRes.previous_zip} />
            <InfoRow label="Monthly Rent/Mortgage" value={prevRes.previous_rent_mortgage} />
          </div>
        </div>
      )}

      {creditAppSectionHasData(info, 'previous_employment') && (
        <div>
          <h4 className="text-xs font-['Unbounded'] text-white/60 mb-3 flex flex-wrap items-center gap-2">
            Previous Employment
            {mixedSources && <CreditAppSourceTag source={creditAppSectionSource(info, 'previous_employment')} />}
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InfoRow label="Previous Employer" value={prevEmp.previous_employer} className="md:col-span-2" />
            <InfoRow label="Employment Years" value={prevEmp.previous_job_years} />
            <InfoRow label="Employment Months" value={prevEmp.previous_job_months} />
          </div>
        </div>
      )}
    </div>
  );
}

function StandaloneCustomerCard({ customer, onEdit }) {
  if (!customer) return null;
  const address = [customer.address, customer.address_line2, customer.city, customer.state, customer.zip].filter(Boolean).join(', ');
  const hasDetails = Boolean(customer.email || customer.phone || address);
  if (!hasDetails) return null;

  return (
    <div className="relative bg-white/5 border border-white/10 p-6">
      {onEdit && <CardEditButton onClick={onEdit} testId="edit-standalone-customer-card" />}
      <div className="flex flex-wrap items-center gap-3 mb-5 pb-4 border-b border-white/10 pr-10">
        <span className="px-3 py-1 text-xs font-['Manrope'] border bg-emerald-500/10 text-emerald-400 border-emerald-500/30">
          Deal Customer
        </span>
        {customer.payment_type && (
          <span className="px-3 py-1 text-xs font-['Manrope'] border bg-white/5 text-white/60 border-white/10">
            {PAYMENT_TYPE_LABELS[customer.payment_type] || customer.payment_type}
          </span>
        )}
      </div>
      <h4 className="text-base font-['Unbounded'] font-semibold text-white mb-4">{customer.name}</h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <InfoRow label="Email" value={customer.email} />
        <InfoRow label="Phone" value={customer.phone} />
        {address && <InfoRow label="Address" value={address} />}
      </div>
    </div>
  );
}

/* ===== CUSTOMER INFORMATION TAB ===== */
function CustomerInformationTab({ customerInfo, deal, dealId, token, onRefresh }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [showRequestMenu, setShowRequestMenu] = useState(false);
  const [showAddDlModal, setShowAddDlModal] = useState(false);
  const [showRequestDlModal, setShowRequestDlModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [dlForm, setDlForm] = useState(emptyDriverLicenseForm);
  const [requestEmail, setRequestEmail] = useState('');
  const [showAddCreditAppMenu, setShowAddCreditAppMenu] = useState(false);
  const [showRequestCreditAppMenu, setShowRequestCreditAppMenu] = useState(false);
  const [showAddCreditAppModal, setShowAddCreditAppModal] = useState(false);
  const [showRequestCreditAppModal, setShowRequestCreditAppModal] = useState(false);
  const [creditAppSections, setCreditAppSections] = useState({ landlord: false, previous_residential: false, previous_employment: false });
  const [creditAppForm, setCreditAppForm] = useState(emptyCreditAppSupplementForm);
  const [showAddOtherIncomeModal, setShowAddOtherIncomeModal] = useState(false);
  const [showRequestOtherIncomeModal, setShowRequestOtherIncomeModal] = useState(false);
  const [otherIncomeForm, setOtherIncomeForm] = useState(emptyOtherIncomeForm);
  const [editingLead, setEditingLead] = useState(null);
  const [showStandaloneEdit, setShowStandaloneEdit] = useState(false);
  const [standaloneForm, setStandaloneForm] = useState({
    name: '', email: '', phone: '', address: '', city: '', state: '', zip: '',
  });

  const leads = customerInfo?.leads || [];
  const financingLead = leads.find((lead) => lead.type === 'financing');
  const tradeInLead = leads.find((lead) => lead.type === 'trade_in');
  const mergePersonal = Boolean(financingLead && tradeInLead);
  const standaloneCustomer = customerInfo?.standalone_customer;
  const driverLicenseInfo = customerInfo?.driver_license_info;
  const pendingRequest = customerInfo?.pending_driver_license_request;
  const isFinancingApplicant = Boolean(customerInfo?.is_financing_applicant);
  const isIncomeDependentApplicant = Boolean(customerInfo?.is_income_dependent_applicant);
  const creditAppSupplement = customerInfo?.credit_app_supplement;
  const pendingCreditAppRequest = customerInfo?.pending_credit_app_supplement_request;
  const otherIncomeInfo = customerInfo?.other_income_info;
  const pendingOtherIncomeRequest = customerInfo?.pending_other_income_request;

  const selectedCreditAppSections = CREDIT_APP_SECTION_OPTIONS
    .filter((opt) => creditAppSections[opt.key])
    .map((opt) => opt.key);

  const resetCreditAppSections = () => setCreditAppSections({
    landlord: false,
    previous_residential: false,
    previous_employment: false,
  });

  const populateCreditAppFormFromInfo = (info) => {
    const landlord = info?.landlord || {};
    const prevRes = info?.previous_residential || {};
    const prevEmp = info?.previous_employment || {};
    setCreditAppForm({
      landlord_name: landlord.landlord_name || '',
      landlord_mobile: landlord.landlord_mobile || '',
      previous_street_address: prevRes.previous_street_address || '',
      previous_residence_years: prevRes.previous_residence_years || '',
      previous_residence_months: prevRes.previous_residence_months || '',
      previous_city: prevRes.previous_city || '',
      previous_state: prevRes.previous_state || '',
      previous_zip: prevRes.previous_zip || '',
      previous_rent_mortgage: prevRes.previous_rent_mortgage || '',
      previous_employer: prevEmp.previous_employer || '',
      previous_job_years: prevEmp.previous_job_years || '',
      previous_job_months: prevEmp.previous_job_months || '',
    });
  };

  const buildCreditAppPayload = (sections) => {
    const payload = { sections };
    if (sections.includes('landlord')) {
      payload.landlord = {
        landlord_name: creditAppForm.landlord_name.trim(),
        landlord_mobile: creditAppForm.landlord_mobile.trim(),
      };
    }
    if (sections.includes('previous_residential')) {
      payload.previous_residential = {
        previous_street_address: creditAppForm.previous_street_address.trim(),
        previous_residence_years: creditAppForm.previous_residence_years.trim(),
        previous_residence_months: creditAppForm.previous_residence_months.trim(),
        previous_city: creditAppForm.previous_city.trim(),
        previous_state: creditAppForm.previous_state.trim(),
        previous_zip: creditAppForm.previous_zip.trim(),
        previous_rent_mortgage: creditAppForm.previous_rent_mortgage.trim(),
      };
    }
    if (sections.includes('previous_employment')) {
      payload.previous_employment = {
        previous_employer: creditAppForm.previous_employer.trim(),
        previous_job_years: creditAppForm.previous_job_years.trim(),
        previous_job_months: creditAppForm.previous_job_months.trim(),
      };
    }
    return payload;
  };

  const validateCreditAppForm = (sections) => {
    if (sections.includes('landlord') && (!creditAppForm.landlord_name.trim() || !creditAppForm.landlord_mobile.trim())) {
      return 'Please complete all landlord fields';
    }
    if (sections.includes('previous_residential')) {
      const fields = [
        creditAppForm.previous_street_address,
        creditAppForm.previous_residence_years,
        creditAppForm.previous_residence_months,
        creditAppForm.previous_city,
        creditAppForm.previous_state,
        creditAppForm.previous_zip,
        creditAppForm.previous_rent_mortgage,
      ];
      if (fields.some((v) => !String(v).trim())) {
        return 'Please complete all previous residential fields';
      }
    }
    if (sections.includes('previous_employment')) {
      if (!creditAppForm.previous_employer.trim() || !creditAppForm.previous_job_years.trim() || !creditAppForm.previous_job_months.trim()) {
        return 'Please complete all previous employment fields';
      }
    }
    return null;
  };

  useEffect(() => {
    if (showAddDlModal && driverLicenseInfo) {
      setDlForm({
        driver_license_number: '',
        dl_issuing_county: driverLicenseInfo.dl_issuing_county || '',
        dl_issuing_state: driverLicenseInfo.dl_issuing_state || '',
        dl_expiry_date: driverLicenseInfo.dl_expiry_date || '',
        date_of_birth: driverLicenseInfo.date_of_birth || '',
      });
    }
  }, [showAddDlModal, driverLicenseInfo]);

  useEffect(() => {
    if (showRequestDlModal || showRequestCreditAppModal || showRequestOtherIncomeModal) {
      setRequestEmail(
        deal?.customer_email
        || standaloneCustomer?.email
        || customerInfo?.leads?.[0]?.email
        || '',
      );
    }
  }, [showRequestDlModal, showRequestCreditAppModal, showRequestOtherIncomeModal, deal, standaloneCustomer, customerInfo]);

  useEffect(() => {
    if (showAddCreditAppModal) {
      populateCreditAppFormFromInfo(creditAppSupplement);
    }
  }, [showAddCreditAppModal, creditAppSupplement]);

  useEffect(() => {
    if (showAddOtherIncomeModal) {
      setOtherIncomeForm(populateOtherIncomeFormFromInfo(otherIncomeInfo));
    }
  }, [showAddOtherIncomeModal, otherIncomeInfo]);

  const handleSaveDriverLicense = async () => {
    if (!dlForm.driver_license_number || !dlForm.dl_issuing_county || !dlForm.dl_issuing_state
      || !dlForm.dl_expiry_date || !dlForm.date_of_birth) {
      toast.error('Please complete all driver license fields');
      return;
    }
    setSubmitting(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/deals/${dealId}/customer-info/driver-license`,
        dlForm,
        { headers },
      );
      toast.success('Driver license information saved');
      setShowAddDlModal(false);
      setShowAddMenu(false);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save driver license information');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRequestDriverLicense = async () => {
    if (!requestEmail.trim()) {
      toast.error('Customer email is required');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/deals/${dealId}/customer-info/driver-license/request`,
        { customer_email: requestEmail.trim() },
        { headers },
      );
      toast.success('Driver license request email sent');
      setShowRequestDlModal(false);
      setShowRequestMenu(false);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send request email');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveCreditAppSupplement = async () => {
    if (!selectedCreditAppSections.length) {
      toast.error('Select at least one credit application section');
      return;
    }
    const validationError = validateCreditAppForm(selectedCreditAppSections);
    if (validationError) {
      toast.error(validationError);
      return;
    }
    setSubmitting(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/deals/${dealId}/customer-info/credit-application-supplement`,
        buildCreditAppPayload(selectedCreditAppSections),
        { headers },
      );
      toast.success('Credit application information saved');
      setShowAddCreditAppModal(false);
      setShowAddMenu(false);
      resetCreditAppSections();
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save credit application information');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRequestCreditAppSupplement = async () => {
    if (!selectedCreditAppSections.length) {
      toast.error('Select at least one credit application section');
      return;
    }
    if (!requestEmail.trim()) {
      toast.error('Customer email is required');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/deals/${dealId}/customer-info/credit-application-supplement/request`,
        { customer_email: requestEmail.trim(), sections: selectedCreditAppSections },
        { headers },
      );
      toast.success('Credit application request email sent');
      setShowRequestCreditAppModal(false);
      setShowRequestMenu(false);
      resetCreditAppSections();
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send request email');
    } finally {
      setSubmitting(false);
    }
  };

  const openAddCreditAppFlow = () => {
    if (!selectedCreditAppSections.length) {
      toast.error('Select at least one section to add');
      return;
    }
    populateCreditAppFormFromInfo(creditAppSupplement);
    setShowAddCreditAppMenu(false);
    setShowAddMenu(false);
    setShowAddCreditAppModal(true);
  };

  const openRequestCreditAppFlow = () => {
    if (!selectedCreditAppSections.length) {
      toast.error('Select at least one section to request');
      return;
    }
    setShowRequestCreditAppMenu(false);
    setShowRequestMenu(false);
    setShowRequestCreditAppModal(true);
  };

  const openAddOtherIncomeFlow = () => {
    setOtherIncomeForm(populateOtherIncomeFormFromInfo(otherIncomeInfo));
    setShowAddMenu(false);
    setShowAddOtherIncomeModal(true);
  };

  const openEditCreditAppCard = () => {
    setCreditAppSections({
      landlord: true,
      previous_residential: true,
      previous_employment: true,
    });
    populateCreditAppFormFromInfo(creditAppSupplement);
    setShowAddCreditAppModal(true);
  };

  const openStandaloneEdit = () => {
    setStandaloneForm({
      name: standaloneCustomer?.name || deal?.customer_name || '',
      email: standaloneCustomer?.email || '',
      phone: standaloneCustomer?.phone || '',
      address: standaloneCustomer?.address || '',
      city: standaloneCustomer?.city || '',
      state: standaloneCustomer?.state || '',
      zip: standaloneCustomer?.zip || '',
    });
    setShowStandaloneEdit(true);
  };

  const handleSaveStandaloneCustomer = async () => {
    setSubmitting(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/deals/${dealId}/customer-info/standalone`,
        standaloneForm,
        { headers },
      );
      toast.success('Customer information updated');
      setShowStandaloneEdit(false);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update customer information');
    } finally {
      setSubmitting(false);
    }
  };

  const openRequestOtherIncomeFlow = () => {
    setShowRequestMenu(false);
    setShowRequestOtherIncomeModal(true);
  };

  const handleSaveOtherIncome = async (markIncomeDependent = false) => {
    const validationError = validateOtherIncomeForm(otherIncomeForm);
    if (validationError) {
      toast.error(validationError);
      return;
    }
    setSubmitting(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/deals/${dealId}/customer-info/other-income`,
        buildOtherIncomePayload(otherIncomeForm, markIncomeDependent),
        { headers },
      );
      toast.success('Other income information saved');
      setShowAddOtherIncomeModal(false);
      setShowAddMenu(false);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save other income information');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRequestOtherIncome = async (markIncomeDependent = false) => {
    if (!requestEmail.trim()) {
      toast.error('Customer email is required');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/deals/${dealId}/customer-info/other-income/request`,
        { customer_email: requestEmail.trim(), ...(markIncomeDependent ? { mark_income_dependent: true } : {}) },
        { headers },
      );
      toast.success('Other income request email sent');
      setShowRequestOtherIncomeModal(false);
      setShowRequestMenu(false);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send request email');
    } finally {
      setSubmitting(false);
    }
  };

  const renderCreditAppSectionCheckboxes = () => (
    <div className="mt-3 ml-2 pl-3 border-l border-violet-500/30 space-y-2" data-testid="credit-app-section-checkboxes">
      {CREDIT_APP_SECTION_OPTIONS.map((opt) => (
        <label key={opt.key} className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={Boolean(creditAppSections[opt.key])}
            onChange={(e) => setCreditAppSections((prev) => ({ ...prev, [opt.key]: e.target.checked }))}
            className="accent-violet-500"
            data-testid={`credit-app-section-${opt.key}`}
          />
          <span className="text-sm font-['Manrope'] text-white/80">{opt.label}</span>
        </label>
      ))}
    </div>
  );

  const renderCreditAppFormFields = (sections) => (
    <div className="space-y-5">
      {sections.includes('landlord') && (
        <div className="space-y-3 border border-white/10 p-4">
          <h4 className="text-xs font-['Unbounded'] text-white/70">Current Landlord Info</h4>
          <div>
            <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">Landlord Name *</label>
            <input type="text" value={creditAppForm.landlord_name} onChange={(e) => setCreditAppForm({ ...creditAppForm, landlord_name: e.target.value })} className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50" />
          </div>
          <div>
            <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">Landlord Mobile Number *</label>
            <input type="tel" value={creditAppForm.landlord_mobile} onChange={(e) => setCreditAppForm({ ...creditAppForm, landlord_mobile: e.target.value })} className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50" />
          </div>
        </div>
      )}
      {sections.includes('previous_residential') && (
        <div className="space-y-3 border border-white/10 p-4">
          <h4 className="text-xs font-['Unbounded'] text-white/70">Previous Residential Info</h4>
          <div>
            <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">Previous Street Address *</label>
            <input type="text" value={creditAppForm.previous_street_address} onChange={(e) => setCreditAppForm({ ...creditAppForm, previous_street_address: e.target.value })} className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">Years Stayed *</label>
              <input type="number" min="0" value={creditAppForm.previous_residence_years} onChange={(e) => setCreditAppForm({ ...creditAppForm, previous_residence_years: e.target.value })} className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50" />
            </div>
            <div>
              <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">Months Stayed *</label>
              <input type="number" min="0" max="11" value={creditAppForm.previous_residence_months} onChange={(e) => setCreditAppForm({ ...creditAppForm, previous_residence_months: e.target.value })} className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">City *</label>
              <input type="text" value={creditAppForm.previous_city} onChange={(e) => setCreditAppForm({ ...creditAppForm, previous_city: e.target.value })} className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50" />
            </div>
            <div>
              <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">State *</label>
              <select value={creditAppForm.previous_state} onChange={(e) => setCreditAppForm({ ...creditAppForm, previous_state: e.target.value })} className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50 [&>option]:bg-gray-900">
                <option value="">Select state...</option>
                {US_STATES.map((st) => <option key={st} value={st}>{st}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">ZIP *</label>
              <input type="text" value={creditAppForm.previous_zip} onChange={(e) => setCreditAppForm({ ...creditAppForm, previous_zip: e.target.value })} className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50" />
            </div>
            <div>
              <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">Monthly Rent/Mortgage *</label>
              <input type="number" min="0" value={creditAppForm.previous_rent_mortgage} onChange={(e) => setCreditAppForm({ ...creditAppForm, previous_rent_mortgage: e.target.value })} className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50" />
            </div>
          </div>
        </div>
      )}
      {sections.includes('previous_employment') && (
        <div className="space-y-3 border border-white/10 p-4">
          <h4 className="text-xs font-['Unbounded'] text-white/70">Previous Employment Info</h4>
          <div>
            <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">Previous Employer *</label>
            <input type="text" value={creditAppForm.previous_employer} onChange={(e) => setCreditAppForm({ ...creditAppForm, previous_employer: e.target.value })} className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">Employment Years *</label>
              <input type="number" min="0" value={creditAppForm.previous_job_years} onChange={(e) => setCreditAppForm({ ...creditAppForm, previous_job_years: e.target.value })} className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50" />
            </div>
            <div>
              <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">Employment Months *</label>
              <input type="number" min="0" max="11" value={creditAppForm.previous_job_months} onChange={(e) => setCreditAppForm({ ...creditAppForm, previous_job_months: e.target.value })} className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50" />
            </div>
          </div>
        </div>
      )}
    </div>
  );

  if (!customerInfo) {
    return (
      <div className="p-12 text-center text-white/40 font-['Manrope']" data-testid="customer-information-tab">
        Loading customer information...
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="customer-information-tab">
      <div className="bg-white/5 border border-white/10 p-5">
        <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
          <div>
            <h3 className="text-sm font-['Unbounded'] font-semibold text-white/80 mb-2 flex items-center gap-2">
              <User className="w-4 h-4 text-blue-400" />
              Customer on Deal
            </h3>
            <p className="text-lg font-['Manrope'] font-semibold text-white">{customerInfo.customer_name || deal.customer_name}</p>
            <p className="text-xs font-['Manrope'] text-white/40 mt-1">
              {leads.length > 0
                ? `${leads.length} linked lead record${leads.length === 1 ? '' : 's'} with saved customer data`
                : 'Add or request additional customer details required for closing documents'}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => { setShowAddMenu(true); setShowRequestMenu(false); }}
              data-testid="add-more-customer-info-btn"
              className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-['Manrope'] font-semibold"
            >
              <Plus className="w-4 h-4" />
              Add More Customer Info
            </button>
            <button
              type="button"
              onClick={() => { setShowRequestMenu(true); setShowAddMenu(false); }}
              data-testid="request-more-customer-info-btn"
              className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/15 text-white text-sm font-['Manrope'] font-medium border border-white/10"
            >
              <Send className="w-4 h-4" />
              Request More Customer Info
            </button>
          </div>
        </div>
      </div>

      {pendingRequest && (
        <div className="bg-amber-500/5 border border-amber-500/25 px-4 py-3 text-sm font-['Manrope'] text-amber-300/90" data-testid="pending-dl-request-banner">
          Driver license details requested — awaiting customer response at {pendingRequest.customer_email}.
        </div>
      )}

      {pendingCreditAppRequest && (
        <div className="bg-violet-500/5 border border-violet-500/25 px-4 py-3 text-sm font-['Manrope'] text-violet-300/90" data-testid="pending-credit-app-request-banner">
          Credit application supplemental info requested — awaiting customer response at {pendingCreditAppRequest.customer_email}
          {(pendingCreditAppRequest.requested_sections || []).length > 0 && (
            <span className="text-white/50"> ({pendingCreditAppRequest.requested_sections.join(', ').replace(/_/g, ' ')})</span>
          )}.
        </div>
      )}

      {pendingOtherIncomeRequest && (
        <div className="bg-amber-500/5 border border-amber-500/25 px-4 py-3 text-sm font-['Manrope'] text-amber-300/90" data-testid="pending-other-income-request-banner">
          Other income information requested — awaiting customer response at {pendingOtherIncomeRequest.customer_email}.
        </div>
      )}

      <DriverLicenseInfoCard
        info={driverLicenseInfo}
        dealId={dealId}
        onEdit={() => { setShowAddDlModal(true); }}
      />
      {isFinancingApplicant && (
        <CreditAppSupplementInfoCard
          info={creditAppSupplement}
          onEdit={openEditCreditAppCard}
        />
      )}
      {isIncomeDependentApplicant && (
        <OtherIncomeInfoCard
          info={otherIncomeInfo}
          onEdit={openAddOtherIncomeFlow}
        />
      )}

      {leads.length === 0 && !standaloneCustomer && !driverLicenseInfo && (
        <div className="bg-white/5 border border-white/10 p-8 text-center">
          <User className="w-10 h-10 text-white/20 mx-auto mb-3" />
          <p className="text-white/60 font-['Manrope'] text-sm">No linked lead records yet. Use the buttons above to add or request driver license information.</p>
        </div>
      )}

      {standaloneCustomer && (
        <StandaloneCustomerCard customer={standaloneCustomer} onEdit={openStandaloneEdit} />
      )}

      {leads.filter((lead) => lead.type !== 'trade_in').map((lead) => (
        <div key={`${lead.type}-${lead.id}`} className="relative bg-white/5 border border-white/10 p-6">
          <CardEditButton onClick={() => setEditingLead(lead)} testId={`edit-lead-card-${lead.id}`} />
          <div className="flex flex-wrap items-center gap-3 mb-5 pb-4 border-b border-white/10 pr-10">
            <span className={`px-3 py-1 text-xs font-['Manrope'] border ${getLeadStatusColor(lead.status)}`}>
              {getLeadTypeLabel(lead.type)}
            </span>
            <span className={`px-3 py-1 text-xs font-['Manrope'] border ${getLeadStatusColor(lead.status)}`}>
              {lead.status.replace(/_/g, ' ').toUpperCase()}
            </span>
            <span className="text-xs font-['Manrope'] text-white/30">
              {LINKED_VIA_LABELS[lead.linked_via] || lead.linked_via}
            </span>
          </div>
          <h4 className="text-base font-['Unbounded'] font-semibold text-white mb-4">{lead.name}</h4>
          <LeadDetailsContent
            lead={lead}
            showVehicleInterest={false}
            mergePersonalFrom={mergePersonal && lead.type === 'financing' ? (tradeInLead.details?.personalInfo || {}) : null}
          />
        </div>
      ))}

      {!financingLead && tradeInLead?.details?.personalInfo && (
        <div className="relative bg-white/5 border border-white/10 p-6">
          <CardEditButton onClick={() => setEditingLead(tradeInLead)} testId={`edit-lead-card-${tradeInLead.id}`} />
          <h4 className="text-base font-['Unbounded'] font-semibold text-white mb-4">Personal Information</h4>
          <LeadDetailsContent
            lead={tradeInLead}
            showContactHeader
            showTradeInVehicle={false}
            showDescription={false}
          />
        </div>
      )}

      {showAddMenu && (
        <div className={adminOverlay('z-[65]')} data-testid="add-customer-info-menu">
          <div className="bg-[#0a0a0a] border border-white/10 w-full max-w-md p-5">
            <h3 className="text-base font-['Unbounded'] font-semibold text-white mb-4">Add More Customer Info</h3>
            <button
              type="button"
              onClick={() => { setShowAddMenu(false); setShowAddDlModal(true); if (!driverLicenseInfo) setDlForm(emptyDriverLicenseForm); }}
              data-testid="add-driver-license-option"
              className="w-full text-left px-4 py-3 border border-white/10 hover:bg-white/5 text-sm font-['Manrope'] text-white mb-3"
            >
              Add Driver License Related Information
            </button>
            {isFinancingApplicant && (
              <div className="mb-3 border border-violet-500/20 p-3">
                <button
                  type="button"
                  onClick={() => setShowAddCreditAppMenu((v) => !v)}
                  data-testid="add-credit-app-option"
                  className="w-full text-left text-sm font-['Manrope'] text-white flex items-center justify-between"
                >
                  <span>Add Additional Information for Credit Application</span>
                  <ChevronRight className={`w-4 h-4 transition-transform ${showAddCreditAppMenu ? 'rotate-90' : ''}`} />
                </button>
                {showAddCreditAppMenu && (
                  <>
                    {renderCreditAppSectionCheckboxes()}
                    <button
                      type="button"
                      onClick={openAddCreditAppFlow}
                      data-testid="continue-add-credit-app-btn"
                      className="mt-3 w-full px-3 py-2 bg-violet-600 hover:bg-violet-500 text-white text-sm font-['Manrope'] font-semibold"
                    >
                      Continue to Add
                    </button>
                  </>
                )}
              </div>
            )}
            {isIncomeDependentApplicant && (
              <button
                type="button"
                onClick={openAddOtherIncomeFlow}
                data-testid="add-other-income-option"
                className="w-full text-left px-4 py-3 border border-amber-500/20 hover:bg-white/5 text-sm font-['Manrope'] text-white mb-3"
              >
                Add Additional Info About Other Income
              </button>
            )}
            <button type="button" onClick={() => { setShowAddMenu(false); setShowAddCreditAppMenu(false); resetCreditAppSections(); }} className="text-sm font-['Manrope'] text-white/50 hover:text-white">Cancel</button>
          </div>
        </div>
      )}

      {showRequestMenu && (
        <div className={adminOverlay('z-[65]')} data-testid="request-customer-info-menu">
          <div className="bg-[#0a0a0a] border border-white/10 w-full max-w-md p-5">
            <h3 className="text-base font-['Unbounded'] font-semibold text-white mb-4">Request More Customer Info</h3>
            <button
              type="button"
              onClick={() => { setShowRequestMenu(false); setShowRequestDlModal(true); }}
              data-testid="request-driver-license-option"
              className="w-full text-left px-4 py-3 border border-white/10 hover:bg-white/5 text-sm font-['Manrope'] text-white mb-3"
            >
              Request Driver License Details
            </button>
            {isFinancingApplicant && (
              <div className="mb-3 border border-violet-500/20 p-3">
                <button
                  type="button"
                  onClick={() => setShowRequestCreditAppMenu((v) => !v)}
                  data-testid="request-credit-app-option"
                  className="w-full text-left text-sm font-['Manrope'] text-white flex items-center justify-between"
                >
                  <span>Request Additional Information for Credit Application</span>
                  <ChevronRight className={`w-4 h-4 transition-transform ${showRequestCreditAppMenu ? 'rotate-90' : ''}`} />
                </button>
                {showRequestCreditAppMenu && (
                  <>
                    {renderCreditAppSectionCheckboxes()}
                    <button
                      type="button"
                      onClick={openRequestCreditAppFlow}
                      data-testid="continue-request-credit-app-btn"
                      className="mt-3 w-full px-3 py-2 bg-violet-600 hover:bg-violet-500 text-white text-sm font-['Manrope'] font-semibold"
                    >
                      Continue to Request
                    </button>
                  </>
                )}
              </div>
            )}
            {isIncomeDependentApplicant && (
              <button
                type="button"
                onClick={openRequestOtherIncomeFlow}
                data-testid="request-other-income-option"
                className="w-full text-left px-4 py-3 border border-amber-500/20 hover:bg-white/5 text-sm font-['Manrope'] text-white mb-3"
              >
                Request Additional Info About Other Income
              </button>
            )}
            <button type="button" onClick={() => { setShowRequestMenu(false); setShowRequestCreditAppMenu(false); resetCreditAppSections(); }} className="text-sm font-['Manrope'] text-white/50 hover:text-white">Cancel</button>
          </div>
        </div>
      )}

      {showAddDlModal && (
        <div className={adminOverlay('z-[70]')} data-testid="add-driver-license-modal">
          <div className="bg-[#0a0a0a] border border-white/10 w-full max-w-lg max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-white/10">
              <h3 className="text-base font-['Unbounded'] font-semibold text-white">Driver License Related Information</h3>
              <button type="button" onClick={() => setShowAddDlModal(false)} className="text-white/40 hover:text-white text-xl">&times;</button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">Driver License Number # *</label>
                <input type="text" value={dlForm.driver_license_number} onChange={(e) => setDlForm({ ...dlForm, driver_license_number: e.target.value })} data-testid="admin-dl-number" className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50" />
              </div>
              <div>
                <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">DL Issuing County *</label>
                <input type="text" value={dlForm.dl_issuing_county} onChange={(e) => setDlForm({ ...dlForm, dl_issuing_county: e.target.value })} data-testid="admin-dl-county" className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50" />
              </div>
              <div>
                <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">DL Issuing State *</label>
                <select value={dlForm.dl_issuing_state} onChange={(e) => setDlForm({ ...dlForm, dl_issuing_state: e.target.value })} data-testid="admin-dl-state" className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50 [&>option]:bg-gray-900">
                  <option value="">Select state...</option>
                  {US_STATES.map((st) => <option key={st} value={st}>{st}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">DL Expiring Date *</label>
                <input type="date" value={dlForm.dl_expiry_date} onChange={(e) => setDlForm({ ...dlForm, dl_expiry_date: e.target.value })} data-testid="admin-dl-expiry" className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50" />
              </div>
              <div>
                <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">Date Of Birth *</label>
                <input type="date" value={dlForm.date_of_birth} onChange={(e) => setDlForm({ ...dlForm, date_of_birth: e.target.value })} data-testid="admin-dob" className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50" />
              </div>
            </div>
            <div className="flex justify-end gap-3 p-5 border-t border-white/10">
              <button type="button" onClick={() => setShowAddDlModal(false)} className="px-4 py-2 border border-white/10 text-white/60 text-sm font-['Manrope']">Cancel</button>
              <button type="button" onClick={handleSaveDriverLicense} disabled={submitting} data-testid="save-driver-license-btn" className="px-5 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-['Manrope'] font-semibold disabled:opacity-50">
                {submitting ? 'Saving...' : 'Save Information'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showRequestDlModal && (
        <div className={adminOverlay('z-[70]')} data-testid="request-driver-license-modal">
          <div className="bg-[#0a0a0a] border border-white/10 w-full max-w-md p-5">
            <h3 className="text-base font-['Unbounded'] font-semibold text-white mb-2">Request Driver License Details</h3>
            <p className="text-xs font-['Manrope'] text-white/50 mb-4">
              An email will be sent with a secure form for the customer to submit their driver license information.
            </p>
            <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">Customer Email *</label>
            <input
              type="email"
              value={requestEmail}
              onChange={(e) => setRequestEmail(e.target.value)}
              data-testid="request-dl-email"
              className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50 mb-5"
            />
            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => setShowRequestDlModal(false)} className="px-4 py-2 border border-white/10 text-white/60 text-sm font-['Manrope']">Cancel</button>
              <button type="button" onClick={handleRequestDriverLicense} disabled={submitting} data-testid="send-dl-request-btn" className="px-5 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-['Manrope'] font-semibold disabled:opacity-50">
                {submitting ? 'Sending...' : 'Send Request'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddCreditAppModal && (
        <div className={adminOverlay('z-[70]')} data-testid="add-credit-app-modal">
          <div className="bg-[#0a0a0a] border border-white/10 w-full max-w-lg max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-white/10">
              <h3 className="text-base font-['Unbounded'] font-semibold text-white">Credit Application — Additional Information</h3>
              <button type="button" onClick={() => setShowAddCreditAppModal(false)} className="text-white/40 hover:text-white text-xl">&times;</button>
            </div>
            <div className="p-5">
              {renderCreditAppFormFields(selectedCreditAppSections)}
            </div>
            <div className="flex justify-end gap-3 p-5 border-t border-white/10">
              <button type="button" onClick={() => setShowAddCreditAppModal(false)} className="px-4 py-2 border border-white/10 text-white/60 text-sm font-['Manrope']">Cancel</button>
              <button type="button" onClick={handleSaveCreditAppSupplement} disabled={submitting} data-testid="save-credit-app-supplement-btn" className="px-5 py-2 bg-violet-600 hover:bg-violet-500 text-white text-sm font-['Manrope'] font-semibold disabled:opacity-50">
                {submitting ? 'Saving...' : 'Save Information'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showRequestCreditAppModal && (
        <div className={adminOverlay('z-[70]')} data-testid="request-credit-app-modal">
          <div className="bg-[#0a0a0a] border border-white/10 w-full max-w-md p-5">
            <h3 className="text-base font-['Unbounded'] font-semibold text-white mb-2">Request Credit Application Information</h3>
            <p className="text-xs font-['Manrope'] text-white/50 mb-3">
              An email will be sent with a secure form for the selected sections:
            </p>
            <ul className="text-xs font-['Manrope'] text-violet-300/90 mb-4 list-disc list-inside">
              {selectedCreditAppSections.map((key) => (
                <li key={key}>{CREDIT_APP_SECTION_OPTIONS.find((o) => o.key === key)?.label || key}</li>
              ))}
            </ul>
            <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">Customer Email *</label>
            <input
              type="email"
              value={requestEmail}
              onChange={(e) => setRequestEmail(e.target.value)}
              data-testid="request-credit-app-email"
              className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50 mb-5"
            />
            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => setShowRequestCreditAppModal(false)} className="px-4 py-2 border border-white/10 text-white/60 text-sm font-['Manrope']">Cancel</button>
              <button type="button" onClick={handleRequestCreditAppSupplement} disabled={submitting} data-testid="send-credit-app-request-btn" className="px-5 py-2 bg-violet-600 hover:bg-violet-500 text-white text-sm font-['Manrope'] font-semibold disabled:opacity-50">
                {submitting ? 'Sending...' : 'Send Request'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddOtherIncomeModal && (
        <div className={adminOverlay('z-[70]')} data-testid="add-other-income-modal">
          <div className="bg-[#0a0a0a] border border-white/10 w-full max-w-lg max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-white/10">
              <h3 className="text-base font-['Unbounded'] font-semibold text-white">Additional Info About Other Income</h3>
              <button type="button" onClick={() => setShowAddOtherIncomeModal(false)} className="text-white/40 hover:text-white text-xl">&times;</button>
            </div>
            <div className="p-5">
              <OtherIncomeFormFields form={otherIncomeForm} setForm={setOtherIncomeForm} />
            </div>
            <div className="flex justify-end gap-3 p-5 border-t border-white/10">
              <button type="button" onClick={() => setShowAddOtherIncomeModal(false)} className="px-4 py-2 border border-white/10 text-white/60 text-sm font-['Manrope']">Cancel</button>
              <button type="button" onClick={() => handleSaveOtherIncome(false)} disabled={submitting} data-testid="save-other-income-btn" className="px-5 py-2 bg-amber-600 hover:bg-amber-500 text-white text-sm font-['Manrope'] font-semibold disabled:opacity-50">
                {submitting ? 'Saving...' : 'Save Information'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showRequestOtherIncomeModal && (
        <div className={adminOverlay('z-[70]')} data-testid="request-other-income-modal">
          <div className="bg-[#0a0a0a] border border-white/10 w-full max-w-md p-5">
            <h3 className="text-base font-['Unbounded'] font-semibold text-white mb-2">Request Other Income Information</h3>
            <p className="text-xs font-['Manrope'] text-white/50 mb-4">
              An email will be sent with a secure form for the customer to submit their other income details.
            </p>
            <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">Customer Email *</label>
            <input
              type="email"
              value={requestEmail}
              onChange={(e) => setRequestEmail(e.target.value)}
              data-testid="request-other-income-email"
              className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50 mb-5"
            />
            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => setShowRequestOtherIncomeModal(false)} className="px-4 py-2 border border-white/10 text-white/60 text-sm font-['Manrope']">Cancel</button>
              <button type="button" onClick={() => handleRequestOtherIncome(false)} disabled={submitting} data-testid="send-other-income-request-btn" className="px-5 py-2 bg-amber-600 hover:bg-amber-500 text-white text-sm font-['Manrope'] font-semibold disabled:opacity-50">
                {submitting ? 'Sending...' : 'Send Request'}
              </button>
            </div>
          </div>
        </div>
      )}

      <LeadEditModal
        isOpen={Boolean(editingLead)}
        lead={editingLead}
        token={token}
        hideStatus
        hideDelete
        title="Edit Customer Information"
        saveUrl={editingLead ? `${BACKEND_URL}/api/admin/deals/${dealId}/customer-info/lead/${editingLead.id}` : undefined}
        onClose={() => setEditingLead(null)}
        onSaved={() => { setEditingLead(null); onRefresh(); }}
      />

      {showStandaloneEdit && (
        <div className={adminOverlay('z-[70]')} data-testid="edit-standalone-customer-modal">
          <div className="bg-[#0a0a0a] border border-white/10 w-full max-w-lg">
            <div className="flex items-center justify-between p-5 border-b border-white/10">
              <h3 className="text-base font-['Unbounded'] font-semibold text-white">Edit Customer Information</h3>
              <button type="button" onClick={() => setShowStandaloneEdit(false)} className="text-white/40 hover:text-white text-xl">&times;</button>
            </div>
            <div className="p-5 space-y-3">
              <input placeholder="Name" value={standaloneForm.name} onChange={(e) => setStandaloneForm((f) => ({ ...f, name: e.target.value }))} className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope']" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <input placeholder="Email" type="email" value={standaloneForm.email} onChange={(e) => setStandaloneForm((f) => ({ ...f, email: e.target.value }))} className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope']" />
                <input placeholder="Phone" value={standaloneForm.phone} onChange={(e) => setStandaloneForm((f) => ({ ...f, phone: e.target.value }))} className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope']" />
              </div>
              <input placeholder="Address" value={standaloneForm.address} onChange={(e) => setStandaloneForm((f) => ({ ...f, address: e.target.value }))} className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope']" />
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <input placeholder="City" value={standaloneForm.city} onChange={(e) => setStandaloneForm((f) => ({ ...f, city: e.target.value }))} className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope']" />
                <input placeholder="State" value={standaloneForm.state} onChange={(e) => setStandaloneForm((f) => ({ ...f, state: e.target.value }))} className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope']" />
                <input placeholder="ZIP" value={standaloneForm.zip} onChange={(e) => setStandaloneForm((f) => ({ ...f, zip: e.target.value }))} className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope']" />
              </div>
            </div>
            <div className="flex justify-end gap-3 p-5 border-t border-white/10">
              <button type="button" onClick={() => setShowStandaloneEdit(false)} className="px-4 py-2 border border-white/10 text-white/60 text-sm font-['Manrope']">Cancel</button>
              <button type="button" onClick={handleSaveStandaloneCustomer} disabled={submitting} className="px-5 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-['Manrope'] font-semibold disabled:opacity-50">
                {submitting ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function vehicleDisplayLabel(v) {
  if (!v) return '';
  return [v.year, v.make, v.model, v.trim].filter(Boolean).join(' ');
}

const TRADE_IN_FEATURES = [
  '4x4', 'Adjustable Pedals', 'Air Conditioning', 'Air Conditioned Seats', 'Alarm System', 'Alloy Wheels',
  'AM/FM', 'Anti-Lock Brakes', 'Auto Climate Control', 'Back Up Sonar', 'Bed Cover', 'Bedliner',
  'Bucket Seats', 'CD', 'CD Changer', 'Convertible Top', 'Cruise Control', 'Diesel',
  'Driver Airbag', 'Extra Cab', 'Fog Lights', 'Heated Seats', 'Keyless Entry', 'Leather Seats',
  'Luggage Rack', 'Memory Seats', 'Navigation', 'Passenger Airbag', 'Power Doors', 'Power Locks',
  'Power Mirrors', 'Power Seats', 'Power Tailgate', 'Power Windows', 'Quad Seats', 'Rear Air',
  'Rear Defroster', 'Rear Spoiler', 'Running Boards', 'Satellite Radio', 'Side Airbags', 'Sun Roof',
  'Third Row Seat', 'Tilt Wheel', 'Tinted Windows', 'Tool Box', 'Tow Package', 'Traction Control',
  'TV/DVD', 'TV/VCR', 'Woodgrain',
];
const TRADE_IN_TRANSMISSIONS = ['Automatic', 'Manual', 'CVT', 'Semi-Automatic', 'Other'];
const TRADE_IN_CYLINDERS = ['3', '4', '5', '6', '8', '10', '12', 'Electric', 'Other'];

const emptyTradeInVehicleForm = () => ({
  year: '',
  make: '',
  model: '',
  trim: '',
  vin: '',
  mileage: '',
  transmission: '',
  colour: '',
  body_style: '',
  cylinders: '',
  financed: 'No',
  financedWithWhom: '',
  payOffAmount: '',
  accidents: 'None',
  vehicleDescription: '',
  vehicleFeatures: [],
});

function AddTradeInVehicleModal({ open, submitting, form, setForm, onClose, onSave }) {
  if (!open) return null;
  const inputCls = "w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50";
  const labelCls = "block text-xs font-['Manrope'] text-white/60 mb-1.5";
  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const toggleFeature = (feature) => {
    setForm((prev) => ({
      ...prev,
      vehicleFeatures: prev.vehicleFeatures.includes(feature)
        ? prev.vehicleFeatures.filter((item) => item !== feature)
        : [...prev.vehicleFeatures, feature],
    }));
  };

  return (
    <div className={adminOverlay('z-[70]')} data-testid="add-trade-in-vehicle-modal">
      <div className="bg-[#0a0a0a] border border-white/10 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <h3 className="text-base font-['Unbounded'] font-semibold text-white">Add Trade-In Vehicle</h3>
          <button type="button" onClick={onClose} className="text-white/40 hover:text-white text-xl">&times;</button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-xs font-['Manrope'] text-white/40">
            Enter the customer’s trade-in vehicle. It will be linked to this deal.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className={labelCls}>Year *</label>
              <input type="number" min="1990" value={form.year} onChange={(e) => setField('year', e.target.value)} className={inputCls} data-testid="trade-in-year" />
            </div>
            <div>
              <label className={labelCls}>Make *</label>
              <input value={form.make} onChange={(e) => setField('make', e.target.value)} className={inputCls} data-testid="trade-in-make" />
            </div>
            <div>
              <label className={labelCls}>Model *</label>
              <input value={form.model} onChange={(e) => setField('model', e.target.value)} className={inputCls} data-testid="trade-in-model" />
            </div>
            <div>
              <label className={labelCls}>Trim</label>
              <input value={form.trim} onChange={(e) => setField('trim', e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>VIN</label>
              <input value={form.vin} onChange={(e) => setField('vin', e.target.value)} maxLength={17} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Mileage</label>
              <input type="number" min="0" value={form.mileage} onChange={(e) => setField('mileage', e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Transmission</label>
              <select value={form.transmission} onChange={(e) => setField('transmission', e.target.value)} className={`${inputCls} [&>option]:bg-gray-900`}>
                <option value="">Select...</option>
                {TRADE_IN_TRANSMISSIONS.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Color</label>
              <input value={form.colour} onChange={(e) => setField('colour', e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Body Style</label>
              <input value={form.body_style} onChange={(e) => setField('body_style', e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Cylinders</label>
              <select value={form.cylinders} onChange={(e) => setField('cylinders', e.target.value)} className={`${inputCls} [&>option]:bg-gray-900`}>
                <option value="">Select...</option>
                {TRADE_IN_CYLINDERS.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Accidents</label>
              <select value={form.accidents} onChange={(e) => setField('accidents', e.target.value)} className={`${inputCls} [&>option]:bg-gray-900`}>
                <option value="None">None</option>
                <option value="1">1</option>
                <option value="2">2</option>
                <option value="3+">3+</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Financed</label>
              <select value={form.financed} onChange={(e) => setField('financed', e.target.value)} className={`${inputCls} [&>option]:bg-gray-900`}>
                <option value="No">No</option>
                <option value="Yes">Yes</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Financed With</label>
              <input value={form.financedWithWhom} onChange={(e) => setField('financedWithWhom', e.target.value)} disabled={form.financed !== 'Yes'} className={`${inputCls} disabled:opacity-40`} />
            </div>
            <div>
              <label className={labelCls}>Payoff Amount</label>
              <input type="number" min="0" value={form.payOffAmount} onChange={(e) => setField('payOffAmount', e.target.value)} disabled={form.financed !== 'Yes'} className={`${inputCls} disabled:opacity-40`} />
            </div>
          </div>
          <div>
            <label className={labelCls}>Features</label>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 max-h-40 overflow-y-auto pr-1">
              {TRADE_IN_FEATURES.map((feature) => {
                const selected = form.vehicleFeatures.includes(feature);
                return (
                  <button
                    key={feature}
                    type="button"
                    onClick={() => toggleFeature(feature)}
                    className={`text-left px-2 py-1.5 text-[11px] font-['Manrope'] border ${selected ? 'bg-orange-500/15 text-orange-300 border-orange-500/30' : 'bg-white/5 text-white/60 border-white/10'}`}
                  >
                    {feature}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <label className={labelCls}>Description</label>
            <textarea value={form.vehicleDescription} onChange={(e) => setField('vehicleDescription', e.target.value)} rows={3} className={inputCls} />
          </div>
        </div>
        <div className="flex justify-end gap-3 p-5 border-t border-white/10">
          <button type="button" onClick={onClose} className="px-4 py-2 border border-white/10 text-white/60 text-sm font-['Manrope']">Cancel</button>
          <button
            type="button"
            onClick={onSave}
            disabled={submitting}
            className="px-5 py-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-['Manrope'] font-semibold disabled:opacity-50"
            data-testid="save-trade-in-vehicle-btn"
          >
            {submitting ? 'Saving...' : 'Add Trade-In Vehicle'}
          </button>
        </div>
      </div>
    </div>
  );
}

function VehicleInformationTab({ deal, dealId, token, onRefresh, tradeInLead, onVehicleLinked }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [editingTradeIn, setEditingTradeIn] = useState(false);
  const [showAddTradeIn, setShowAddTradeIn] = useState(false);
  const [tradeInForm, setTradeInForm] = useState(emptyTradeInVehicleForm);
  const [savingTradeIn, setSavingTradeIn] = useState(false);
  const [vehicle, setVehicle] = useState(null);
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [selectedId, setSelectedId] = useState(deal?.vehicle_id || '');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const [vehRes, listRes] = await Promise.all([
          deal?.vehicle_id
            ? axios.get(`${BACKEND_URL}/api/vehicles/${deal.vehicle_id}`).catch(() => ({ data: null }))
            : Promise.resolve({ data: null }),
          axios.get(`${BACKEND_URL}/api/vehicles`),
        ]);
        if (cancelled) return;
        setVehicle(vehRes.data);
        setInventory(Array.isArray(listRes.data) ? listRes.data : []);
        setSelectedId(deal?.vehicle_id || '');
      } catch (err) {
        if (!cancelled) toast.error('Failed to load vehicle information');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [deal?.vehicle_id, token]);

  const handleLinkVehicle = async () => {
    if (!selectedId) {
      toast.error('Choose a vehicle to link');
      return;
    }
    if (selectedId === deal?.vehicle_id) {
      setShowPicker(false);
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.put(
        `${BACKEND_URL}/api/admin/deals/${dealId}/vehicle`,
        { vehicle_id: selectedId },
        { headers },
      );
      toast.success('Deal vehicle updated');
      const linkedVehicle = data.vehicle || inventory.find((v) => v.id === selectedId) || null;
      setVehicle(linkedVehicle);
      setShowPicker(false);
      onRefresh?.();
      onVehicleLinked?.(linkedVehicle);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update linked vehicle');
    } finally {
      setSaving(false);
    }
  };

  const handleAddTradeInVehicle = async () => {
    if (!tradeInForm.year.trim() || !tradeInForm.make.trim() || !tradeInForm.model.trim()) {
      toast.error('Year, make, and model are required');
      return;
    }
    setSavingTradeIn(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/deals/${dealId}/trade-in-vehicle`,
        {
          ...tradeInForm,
          year: tradeInForm.year.trim(),
          make: tradeInForm.make.trim(),
          model: tradeInForm.model.trim(),
        },
        { headers },
      );
      toast.success('Trade-in vehicle added to this deal');
      setShowAddTradeIn(false);
      setTradeInForm(emptyTradeInVehicleForm());
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add trade-in vehicle');
    } finally {
      setSavingTradeIn(false);
    }
  };

  if (loading) {
    return (
      <div className="p-12 text-center text-white/40 font-['Manrope']" data-testid="vehicle-information-tab">
        Loading vehicle information...
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="vehicle-information-tab">
      <div className="relative bg-white/5 border border-white/10 p-6">
        <CardEditButton onClick={() => setShowPicker((v) => !v)} testId="edit-deal-vehicle-btn" />
        <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4 pr-10">
          <div>
            <h3 className="text-sm font-['Unbounded'] font-semibold text-white/80 mb-2 flex items-center gap-2">
              <Car className="w-4 h-4 text-purple-400" />
              Vehicle on Deal
            </h3>
            <p className="text-lg font-['Manrope'] font-semibold text-white">
              {vehicleDisplayLabel(vehicle) || 'No vehicle linked'}
            </p>
            {vehicle?.stockNumber && (
              <p className="text-xs font-['Manrope'] text-white/40 mt-1">Stock #{vehicle.stockNumber}</p>
            )}
          </div>
          {vehicle && (
            <div className="flex items-baseline gap-2 lg:text-right">
              <span className="text-xs font-['Manrope'] text-white/45">Initial Quote</span>
              <p className="text-xl font-['Unbounded'] font-bold text-white">
                {vehicle.price_not_decided ? 'Price TBD' : `$${Number(vehicle.price || 0).toLocaleString()}`}
              </p>
            </div>
          )}
        </div>

        {showPicker && (
          <div className="mt-5 pt-4 border-t border-white/10 space-y-3" data-testid="deal-vehicle-picker">
            <label className="block text-xs font-['Manrope'] text-white/60">
              Choose a vehicle to link to this deal
            </label>
            <select
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
              className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50 [&>option]:bg-gray-900"
              data-testid="deal-vehicle-select"
            >
              <option value="">Select from inventory...</option>
              {inventory.map((v) => (
                <option key={v.id} value={v.id}>
                  {[v.year, v.make, v.model, v.trim].filter(Boolean).join(' ')}
                  {v.price != null ? ` — $${Number(v.price).toLocaleString()}` : ''}
                  {v.vin ? ` · ${v.vin}` : ''}
                  {v.status ? ` (${v.status})` : ''}
                </option>
              ))}
            </select>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => { setShowPicker(false); setSelectedId(deal?.vehicle_id || ''); }}
                className="px-4 py-2 border border-white/10 text-white/60 text-sm font-['Manrope']"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleLinkVehicle}
                disabled={saving || !selectedId}
                className="px-5 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-['Manrope'] font-semibold disabled:opacity-50"
                data-testid="confirm-deal-vehicle-btn"
              >
                {saving ? 'Linking...' : 'Link Vehicle'}
              </button>
            </div>
          </div>
        )}
      </div>

      {!vehicle ? (
        <div className="bg-white/5 border border-white/10 p-8 text-center">
          <Car className="w-10 h-10 text-white/20 mx-auto mb-3" />
          <p className="text-white/60 font-['Manrope'] text-sm">
            No vehicle is linked to this deal. Use the pencil icon to choose one from inventory.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white/5 border border-white/10 p-5">
            <h4 className="text-sm font-['Unbounded'] font-semibold text-white/80 mb-4">Details</h4>
            <div className="space-y-3">
              <InfoRow label="Year" value={vehicle.year} />
              <InfoRow label="Make" value={vehicle.make} />
              <InfoRow label="Model" value={vehicle.model} />
              <InfoRow label="Trim" value={vehicle.trim} />
              <InfoRow label="Stock Number" value={vehicle.stockNumber} />
              <InfoRow label="VIN" value={vehicle.vin} mono />
              <InfoRow label="Status" value={vehicle.status} />
              <InfoRow label="Condition" value={vehicle.condition} />
            </div>
          </div>
          <div className="bg-white/5 border border-white/10 p-5">
            <h4 className="text-sm font-['Unbounded'] font-semibold text-white/80 mb-4">Specs</h4>
            <div className="space-y-3">
              <InfoRow label="Mileage" value={vehicle.mileage != null ? `${Number(vehicle.mileage).toLocaleString()} mi` : ''} />
              <InfoRow label="Price" value={vehicle.price_not_decided ? 'Price TBD' : (vehicle.price != null ? `$${Number(vehicle.price).toLocaleString()}` : '')} highlight />
              <InfoRow label="Exterior Color" value={vehicle.exterior_color} />
              <InfoRow label="Interior Color" value={vehicle.interior_color} />
              <InfoRow label="Fuel Type" value={vehicle.fuel_type} />
              <InfoRow label="Transmission" value={vehicle.transmission} />
              <InfoRow label="Engine" value={vehicle.engine} />
              <InfoRow label="Body Type" value={vehicle.body_type} />
              <InfoRow label="Drivetrain" value={vehicle.drivetrain} />
              <InfoRow label="Cylinders" value={vehicle.cylinders} />
              <InfoRow label="City MPG" value={vehicle.city_mpg} />
              <InfoRow label="Highway MPG" value={vehicle.hwy_mpg} />
            </div>
          </div>
          {Array.isArray(vehicle.features) && vehicle.features.length > 0 && (
            <div className="bg-white/5 border border-white/10 p-5 lg:col-span-2">
              <h4 className="text-sm font-['Unbounded'] font-semibold text-white/80 mb-3">Features</h4>
              <div className="flex flex-wrap gap-2">
                {vehicle.features.map((f) => (
                  <span key={f} className="px-2.5 py-1 text-[11px] font-['Manrope'] bg-white/5 border border-white/10 text-white/70">
                    {f}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {!tradeInLead && (
        <button
          type="button"
          onClick={() => { setTradeInForm(emptyTradeInVehicleForm()); setShowAddTradeIn(true); }}
          data-testid="add-trade-in-vehicle-btn"
          className="w-full flex items-center justify-center gap-2 px-4 py-3 border border-dashed border-orange-500/40 bg-orange-500/5 hover:bg-orange-500/10 text-orange-300 text-sm font-['Manrope'] font-semibold"
        >
          <Plus className="w-4 h-4" />
          Add a Vehicle to Trade in
        </button>
      )}

      {(tradeInLead?.details?.vehicleInfo || tradeInLead?.details?.vehicle || tradeInLead?.details?.description) && (
        <div className="relative bg-white/5 border border-white/10 p-6" data-testid="trade-in-vehicle-card">
          <CardEditButton onClick={() => setEditingTradeIn(true)} testId="edit-trade-in-vehicle-btn" />
          <h3 className="text-sm font-['Unbounded'] font-semibold text-white/80 mb-1 flex items-center gap-2 pr-10">
            <Car className="w-4 h-4 text-orange-400" />
            Trade-In Vehicle
          </h3>
          <p className="text-xs font-['Manrope'] text-white/40 mb-5">
            From the linked trade-in submission
          </p>
          {(tradeInLead.details?.vehicleInfo || tradeInLead.details?.vehicle) && (
            <TradeInVehicleFields lead={tradeInLead} />
          )}
          {tradeInLead.details?.description && (
            <div className={(tradeInLead.details?.vehicleInfo || tradeInLead.details?.vehicle) ? 'mt-5 pt-5 border-t border-white/10' : ''}>
              <label className="text-sm font-['Manrope'] text-white/60 mb-1 block">Description</label>
              <p className="text-white font-['Manrope'] bg-white/5 p-3 border border-white/10">
                {tradeInLead.details.description}
              </p>
            </div>
          )}
        </div>
      )}

      <AddTradeInVehicleModal
        open={showAddTradeIn}
        submitting={savingTradeIn}
        form={tradeInForm}
        setForm={setTradeInForm}
        onClose={() => setShowAddTradeIn(false)}
        onSave={handleAddTradeInVehicle}
      />

      <LeadEditModal
        isOpen={editingTradeIn}
        lead={tradeInLead}
        token={token}
        hideStatus
        hideDelete
        title="Edit Trade-In Vehicle"
        saveUrl={tradeInLead ? `${BACKEND_URL}/api/admin/deals/${dealId}/customer-info/lead/${tradeInLead.id}` : undefined}
        onClose={() => setEditingTradeIn(false)}
        onSaved={() => { setEditingTradeIn(false); onRefresh?.(); }}
      />
    </div>
  );
}

const DEFAULT_DEAL_FEES = {
  documentary_fee: 358,
  title_and_registration_fee: 316,
  ert_fee: 35,
  sales_tax_rate: 0.085,
};

const formatUsdAmount = (n) => {
  if (n == null || n === '') return '-';
  const num = Number(n);
  if (Number.isNaN(num)) return '-';
  return `$${num.toLocaleString()}`;
};

const taxRateToPercent = (rate) => {
  const parsed = Number(rate);
  if (Number.isNaN(parsed)) return 8.5;
  return parsed > 1 ? parsed : parsed * 100;
};

const resolveDealFees = (deal) => {
  const breakdown = deal?.fee_breakdown || deal?.cash_fee_breakdown || null;
  const sale = Number(deal?.sale_price) || 0;
  if (breakdown) {
    const rate = breakdown.sales_tax_rate != null
      ? (Number(breakdown.sales_tax_rate) > 1 ? Number(breakdown.sales_tax_rate) / 100 : Number(breakdown.sales_tax_rate))
      : DEFAULT_DEAL_FEES.sales_tax_rate;
    return {
      documentary_fee: breakdown.documentary_fee ?? DEFAULT_DEAL_FEES.documentary_fee,
      title_and_registration_fee: breakdown.title_and_registration_fee ?? DEFAULT_DEAL_FEES.title_and_registration_fee,
      ert_fee: breakdown.ert_fee ?? 0,
      sales_tax_rate: rate,
      sales_tax: breakdown.sales_tax ?? Math.round(sale * rate),
    };
  }
  return {
    documentary_fee: DEFAULT_DEAL_FEES.documentary_fee,
    title_and_registration_fee: DEFAULT_DEAL_FEES.title_and_registration_fee,
    ert_fee: DEFAULT_DEAL_FEES.ert_fee,
    sales_tax_rate: DEFAULT_DEAL_FEES.sales_tax_rate,
    sales_tax: Math.round(sale * DEFAULT_DEAL_FEES.sales_tax_rate),
  };
};

const resolveTradeInPayoff = (deal, tradeInLead) => {
  if (deal?.trade_in_payoff_amount != null && deal.trade_in_payoff_amount !== '') {
    return Number(deal.trade_in_payoff_amount) || 0;
  }
  const raw = tradeInLead?.details?.vehicleInfo?.payOffAmount
    ?? tradeInLead?.details?.payOffAmount
    ?? tradeInLead?.details?.vehicleInfo?.payoffAmount
    ?? 0;
  const parsed = parseInt(String(raw).replace(/[^0-9]/g, ''), 10);
  return Number.isNaN(parsed) ? 0 : parsed;
};

/** Sale + sales tax + documentary + ERT + title/reg + trade-in payoff. Remaining subtracts trade-in value and down payment. */
const dealAmountSummary = (deal, tradeInLead) => {
  const fees = resolveDealFees(deal);
  const sale = Number(deal?.sale_price) || 0;
  const payoff = resolveTradeInPayoff(deal, tradeInLead);
  const totalDue = sale
    + (Number(fees.sales_tax) || 0)
    + (Number(fees.documentary_fee) || 0)
    + (Number(fees.ert_fee) || 0)
    + (Number(fees.title_and_registration_fee) || 0)
    + payoff;
  const remainingDue = Math.max(
    0,
    totalDue - (Number(deal?.trade_in_value) || 0) - (Number(deal?.down_payment) || 0),
  );
  return { totalDue, remainingDue };
};

const pricingFormFromDeal = (deal, tradeInLead) => {
  const fees = resolveDealFees(deal);
  return {
    sale_price: deal?.sale_price != null ? String(deal.sale_price) : '',
    down_payment: deal?.down_payment != null ? String(deal.down_payment) : '',
    trade_in_value: deal?.trade_in_value != null ? String(deal.trade_in_value) : '',
    trade_in_payoff_amount: String(resolveTradeInPayoff(deal, tradeInLead)),
    documentary_fee: String(fees.documentary_fee),
    title_and_registration_fee: String(fees.title_and_registration_fee),
    ert_fee: String(fees.ert_fee),
    sales_tax_percent: String(taxRateToPercent(fees.sales_tax_rate)),
    amount_requested: deal?.amount_requested != null ? String(deal.amount_requested) : '',
    amount_financed: deal?.amount_financed != null ? String(deal.amount_financed) : '',
    retail_tax_rate: deal?.retail_tax_rate != null ? String(deal.retail_tax_rate) : '',
    months_to_payoff: deal?.months_to_payoff != null ? String(deal.months_to_payoff) : '',
    monthly_payment: deal?.monthly_payment != null ? String(deal.monthly_payment) : '',
    last_monthly_payment: deal?.last_monthly_payment != null ? String(deal.last_monthly_payment) : '',
    payment_due_date: parseApiDateForForm(deal?.payment_due_date),
    tax_override: Boolean(deal?.sales_tax_lookup?.source === 'override'),
    tax_override_reason: deal?.sales_tax_lookup?.override_reason || '',
  };
};

const formatDisplayDate = (value) => formatDateMdY(value);

const resolveFinancingTerms = (deal) => {
  const amountFinanced = deal?.amount_financed ?? deal?.credits_financed ?? null;
  const retailTaxRate = deal?.retail_tax_rate ?? null;
  const months = deal?.months_to_payoff ?? null;
  const hasFinanceInputs = amountFinanced || retailTaxRate || months;
  const financeCharge = deal?.finance_charge != null
    ? Number(deal.finance_charge)
    : (hasFinanceInputs ? computeRetailFinanceCharge(amountFinanced, retailTaxRate, months) : null);
  const totalCreditsDue = deal?.total_credits_due != null
    ? Number(deal.total_credits_due)
    : (hasFinanceInputs ? (Number(amountFinanced) || 0) + (financeCharge || 0) : null);
  return {
    amount_requested: deal?.amount_requested ?? null,
    amount_financed: amountFinanced,
    retail_tax_rate: retailTaxRate,
    finance_charge: financeCharge,
    total_credits_due: totalCreditsDue,
    months_to_payoff: months,
    monthly_payment: deal?.monthly_payment ?? null,
    payment_due_date: deal?.payment_due_date || '',
    last_monthly_payment: deal?.last_monthly_payment ?? null,
  };
};

const parseOptionalNumber = (value) => {
  if (value === '' || value == null) return 0;
  const num = Number(value);
  return Number.isNaN(num) ? 0 : Math.max(0, num);
};

const parseMoneyField = (value) => {
  if (value === '' || value == null) return 0;
  const num = Number(value);
  return Number.isNaN(num) ? 0 : Math.max(0, Math.round(num));
};

/* ===== OVERVIEW TAB ===== */
function OverviewTab({ deal, dealId, token, onRefresh, pricingEditRequest, onPricingEditRequestHandled, tradeInLead }) {
  const { user } = useAuth();
  const isMainAdmin = Boolean(user?.is_bootstrap_admin);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [editing, setEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showSelectLien, setShowSelectLien] = useState(false);
  const [showAddLien, setShowAddLien] = useState(false);
  const [lienHolders, setLienHolders] = useState([]);
  const [lienLoading, setLienLoading] = useState(false);
  const [lienSaving, setLienSaving] = useState(false);
  const [lienForm, setLienForm] = useState({
    id_number: '', name: '', street_address: '', city: '', state: '', zip: '', phone: '',
  });
  const [form, setForm] = useState(() => pricingFormFromDeal(deal, tradeInLead));
  const financeSourceKeyRef = useRef(null);
  const fees = resolveDealFees(deal);
  const financingTerms = resolveFinancingTerms(deal);
  const isFinancingDeal = dealIsFinancingApplicant(deal);
  const savedAmounts = dealAmountSummary(deal, tradeInLead);

  useEffect(() => {
    if (!pricingEditRequest) return;
    const next = pricingFormFromDeal(deal, tradeInLead);
    if (pricingEditRequest.salePrice != null && pricingEditRequest.salePrice !== '') {
      next.sale_price = String(pricingEditRequest.salePrice);
    }
    financeSourceKeyRef.current = null;
    setForm(next);
    setEditing(true);
    onPricingEditRequestHandled?.();
  }, [pricingEditRequest, deal, tradeInLead, onPricingEditRequestHandled]);

  const previewTax = Math.round(
    (parseMoneyField(form.sale_price)) * (Math.max(0, parseFloat(form.sales_tax_percent) || 0) / 100)
  );
  const previewTradeInValue = parseMoneyField(form.trade_in_value);
  const previewDownPayment = parseMoneyField(form.down_payment);
  const previewSummary = dealAmountSummary({
    sale_price: parseMoneyField(form.sale_price),
    down_payment: previewDownPayment,
    trade_in_value: previewTradeInValue,
    trade_in_payoff_amount: parseMoneyField(form.trade_in_payoff_amount),
    fee_breakdown: {
      documentary_fee: parseMoneyField(form.documentary_fee),
      title_and_registration_fee: parseMoneyField(form.title_and_registration_fee),
      ert_fee: parseMoneyField(form.ert_fee),
      sales_tax: previewTax,
      sales_tax_rate: Math.max(0, parseFloat(form.sales_tax_percent) || 0) / 100,
    },
  });
  const previewTotalDue = previewSummary.totalDue;
  const previewRemainingDue = previewSummary.remainingDue;
  const previewFinanceCharge = computeRetailFinanceCharge(
    form.amount_financed,
    form.retail_tax_rate,
    form.months_to_payoff,
  );
  const previewTotalCreditsDue = (parseOptionalNumber(form.amount_financed)) + previewFinanceCharge;
  const financeSourceKey = `${form.amount_financed}|${form.retail_tax_rate}|${form.months_to_payoff}|${previewTotalCreditsDue}`;

  useEffect(() => {
    if (!editing || !isFinancingDeal) return;
    const applySplit = (forceEmptyOnly) => {
      const split = computeInstallmentSplit(previewTotalCreditsDue, form.months_to_payoff);
      if (!split) return;
      setForm((prev) => {
        if (forceEmptyOnly && prev.monthly_payment && prev.last_monthly_payment) return prev;
        if (prev.monthly_payment === split.monthly && prev.last_monthly_payment === split.final) return prev;
        return { ...prev, monthly_payment: split.monthly, last_monthly_payment: split.final };
      });
    };

    if (financeSourceKeyRef.current === null) {
      financeSourceKeyRef.current = financeSourceKey;
      applySplit(true);
      return;
    }
    if (financeSourceKeyRef.current === financeSourceKey) return;
    financeSourceKeyRef.current = financeSourceKey;
    applySplit(false);
  }, [editing, isFinancingDeal, financeSourceKey, previewTotalCreditsDue, form.months_to_payoff]);

  const openEdit = () => {
    financeSourceKeyRef.current = null;
    setForm(pricingFormFromDeal(deal, tradeInLead));
    setEditing(true);
  };

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleSavePricing = async () => {
    if (form.tax_override && !String(form.tax_override_reason || '').trim()) {
      toast.error('A reason is required for a sales-tax override');
      return;
    }
    setSubmitting(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/deals/${dealId}`,
        {
          sale_price: parseMoneyField(form.sale_price),
          down_payment: parseMoneyField(form.down_payment),
          trade_in_value: parseMoneyField(form.trade_in_value),
          trade_in_payoff_amount: parseMoneyField(form.trade_in_payoff_amount),
          documentary_fee: parseMoneyField(form.documentary_fee),
          title_and_registration_fee: parseMoneyField(form.title_and_registration_fee),
          ert_fee: parseMoneyField(form.ert_fee),
          sales_tax_rate: Math.max(0, parseFloat(form.sales_tax_percent) || 0),
          ...(form.tax_override && form.tax_override_reason.trim()
            ? { sales_tax_override_reason: form.tax_override_reason.trim() }
            : {}),
          ...(isFinancingDeal ? {
            amount_requested: parseOptionalNumber(form.amount_requested),
            amount_financed: parseOptionalNumber(form.amount_financed),
            retail_tax_rate: parseOptionalNumber(form.retail_tax_rate),
            months_to_payoff: Math.round(parseOptionalNumber(form.months_to_payoff)),
            monthly_payment: parseOptionalNumber(form.monthly_payment),
            last_monthly_payment: parseOptionalNumber(form.last_monthly_payment),
            payment_due_date: form.payment_due_date || '',
          } : {}),
        },
        { headers },
      );
      toast.success('Pricing updated');
      setEditing(false);
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update pricing');
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = "w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50";

  const loadLienHolders = async () => {
    setLienLoading(true);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/admin/lien-holders`, { headers });
      setLienHolders(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load lien holders');
    } finally {
      setLienLoading(false);
    }
  };

  const openSelectLien = async () => {
    setShowSelectLien(true);
    await loadLienHolders();
  };

  const handleSelectLien = async (holderId) => {
    setLienSaving(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/deals/${dealId}/lien-holder`,
        { lien_holder_id: holderId },
        { headers },
      );
      toast.success('Lien holder linked to this financing deal');
      setShowSelectLien(false);
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to link lien holder');
    } finally {
      setLienSaving(false);
    }
  };

  const handleAddLien = async () => {
    if (!lienForm.id_number.trim() || !lienForm.name.trim()) {
      toast.error('Lien holder name and ID number are required');
      return;
    }
    setLienSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/lien-holders`, {
        id_number: lienForm.id_number.trim(),
        name: lienForm.name.trim(),
        street_address: lienForm.street_address.trim(),
        city: lienForm.city.trim(),
        state: lienForm.state.trim(),
        zip: lienForm.zip.trim(),
        phone: lienForm.phone.trim(),
      }, { headers });
      toast.success('Lien holder added');
      setShowAddLien(false);
      setLienForm({ id_number: '', name: '', street_address: '', city: '', state: '', zip: '', phone: '' });
      if (showSelectLien) await loadLienHolders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add lien holder');
    } finally {
      setLienSaving(false);
    }
  };

  const selectedLien = deal.lien_holder || null;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="overview-tab">
      <div className="bg-white/5 border border-white/10 p-5">
        <h3 className="text-sm font-['Unbounded'] font-semibold text-white/80 mb-4 flex items-center gap-2">
          <User className="w-4 h-4 text-blue-400" />
          Deal Information
        </h3>
        <div className="space-y-3">
          <InfoRow label="Customer" value={deal.customer_name} />
          {deal.payment_type && (
            <InfoRow label="Purchase Type" value={PAYMENT_TYPE_LABELS[deal.payment_type] || deal.payment_type} highlight />
          )}
          {deal.customer_email && <InfoRow label="Email" value={deal.customer_email} />}
          {deal.customer_phone && <InfoRow label="Phone" value={deal.customer_phone} />}
          {(deal.customer_address || deal.customer_city) && (
            <InfoRow
              label="Address"
              value={[deal.customer_address, deal.customer_address_line2, deal.customer_city, deal.customer_state, deal.customer_zip].filter(Boolean).join(', ')}
            />
          )}
          <InfoRow label="Language" value={deal.language === 'es' ? 'Spanish' : 'English'} />
          <InfoRow label="Created" value={formatDate(deal.created_at)} />
          <InfoRow label="Updated" value={formatDate(deal.updated_at)} />
          {deal.delivered_at && <InfoRow label="Delivered" value={formatDate(deal.delivered_at)} />}
          {deal.tax_filing_deadline_at && deal.status === 'delivered' && (
            <InfoRow label="Tax Filing Deadline" value={formatDate(deal.tax_filing_deadline_at)} highlight />
          )}
          {deal.taxes_filed_at && <InfoRow label="Taxes Filed" value={formatDate(deal.taxes_filed_at)} highlight />}
        </div>
      </div>

      <div className="relative bg-white/5 border border-white/10 p-5" data-testid="deal-pricing-card">
        <CardEditButton onClick={openEdit} testId="edit-deal-pricing-btn" />
        <h3 className="text-sm font-['Unbounded'] font-semibold text-white/80 mb-4 flex items-center gap-2 pr-10">
          <DollarSign className="w-4 h-4 text-green-400" />
          Pricing
        </h3>
        <div className="space-y-3">
          <InfoRow label="Sale Price" value={formatUsdAmount(deal.sale_price)} highlight />
          <InfoRow label="Down Payment" value={formatUsdAmount(deal.down_payment)} />
          <InfoRow label="Trade-in Value" value={formatUsdAmount(deal.trade_in_value ?? 0)} />
          <InfoRow
            label="Trade-in Vehicle Payoff"
            value={formatUsdAmount(resolveTradeInPayoff(deal, tradeInLead))}
          />
          <div className="border-t border-white/10 pt-3 space-y-3">
            <InfoRow
              label="Sales Tax"
              value={`${formatUsdAmount(fees.sales_tax)} (${taxRateToPercent(fees.sales_tax_rate)}%)`}
            />
            {deal.sales_tax_lookup && (
              <InfoRow
                label="Tax Jurisdiction"
                value={[
                  deal.sales_tax_lookup.tax_region_name,
                  deal.sales_tax_lookup.buyer_zip || deal.sales_tax_lookup.zip_code,
                  deal.sales_tax_lookup.source && deal.sales_tax_lookup.source !== 'database'
                    ? `(${deal.sales_tax_lookup.source})`
                    : '',
                ].filter(Boolean).join(' ') || '-'}
              />
            )}
            <InfoRow label="Documentary Fee" value={formatUsdAmount(fees.documentary_fee)} />
            <InfoRow label="ERT Fee" value={formatUsdAmount(fees.ert_fee)} />
            <InfoRow label="Title and Registration Fee" value={formatUsdAmount(fees.title_and_registration_fee)} />
            <InfoRow label="Total Due" value={formatUsdAmount(savedAmounts.totalDue)} highlight testId="deal-pricing-total-due" />
            <InfoRow label="Remaining Due" value={formatUsdAmount(savedAmounts.remainingDue)} highlight testId="deal-pricing-remaining-due" />
          </div>
          {isFinancingDeal && (
            <div className="border-t border-white/10 pt-3 space-y-3" data-testid="financing-terms-display">
              <InfoRow label="Amount Requested" value={formatUsdAmount(financingTerms.amount_requested)} />
              <InfoRow label="Amount Financed" value={formatUsdAmount(financingTerms.amount_financed)} />
              <InfoRow
                label="Finance Tax Rate"
                value={financingTerms.retail_tax_rate != null && financingTerms.retail_tax_rate !== ''
                  ? `${financingTerms.retail_tax_rate}%`
                  : '-'}
              />
              <InfoRow label="Finance Charge Amount" value={formatUsdAmount(financingTerms.finance_charge)} />
              <InfoRow label="Total Credits Due" value={formatUsdAmount(financingTerms.total_credits_due)} />
              <InfoRow
                label="Number of Months to Pay"
                value={financingTerms.months_to_payoff != null ? String(financingTerms.months_to_payoff) : '-'}
              />
              <InfoRow label="Monthly Payment Amount" value={formatUsdAmount(financingTerms.monthly_payment)} />
              <InfoRow label="Payment Due Date" value={formatDisplayDate(financingTerms.payment_due_date)} />
              <InfoRow label="Final Installment" value={formatUsdAmount(financingTerms.last_monthly_payment)} />
            </div>
          )}
        </div>
      </div>

      {editing && (
        <div className={adminOverlay('z-[70]')} data-testid="edit-deal-pricing-modal">
          <div className="bg-[#0a0a0a] border border-white/10 w-full max-w-lg max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-white/10">
              <h3 className="text-base font-['Unbounded'] font-semibold text-white">Edit Pricing</h3>
              <button type="button" onClick={() => setEditing(false)} className="text-white/40 hover:text-white text-xl">&times;</button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">Sale Price</label>
                  <input type="number" min="0" value={form.sale_price} onChange={(e) => setField('sale_price', e.target.value)} className={inputCls} data-testid="edit-sale-price" />
                </div>
                <div>
                  <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">Down Payment</label>
                  <input type="number" min="0" value={form.down_payment} onChange={(e) => setField('down_payment', e.target.value)} className={inputCls} data-testid="edit-down-payment" />
                </div>
                <div>
                  <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">Trade-in Value</label>
                  <input type="number" min="0" value={form.trade_in_value} onChange={(e) => setField('trade_in_value', e.target.value)} className={inputCls} data-testid="edit-trade-in-value" />
                </div>
                <div>
                  <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">Trade-in Vehicle Payoff</label>
                  <input type="number" min="0" value={form.trade_in_payoff_amount} onChange={(e) => setField('trade_in_payoff_amount', e.target.value)} className={inputCls} data-testid="edit-trade-in-payoff" />
                </div>
              </div>

              <div className="border-t border-white/10 pt-4 space-y-3">
                <p className="text-[11px] font-['Unbounded'] text-white/50 uppercase tracking-wider">Deal Fees</p>
                <div>
                  <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">Documentary Fee</label>
                  <input type="number" min="0" value={form.documentary_fee} onChange={(e) => setField('documentary_fee', e.target.value)} className={inputCls} data-testid="edit-documentary-fee" />
                </div>
                <SalesTaxRatePanel
                  token={token}
                  state={deal.customer_state}
                  zip={deal.customer_zip}
                  city={deal.customer_city}
                  salesTaxPercent={form.sales_tax_percent}
                  onRateChange={(value) => setField('sales_tax_percent', value)}
                  override={form.tax_override}
                  onOverrideChange={(value) => setField('tax_override', value)}
                  overrideReason={form.tax_override_reason}
                  onOverrideReasonChange={(value) => setField('tax_override_reason', value)}
                  testIdPrefix="edit"
                  autoApply={false}
                />
                <div className="flex justify-end">
                  <span className="text-sm font-['Manrope'] text-white/50 tabular-nums">{formatUsdAmount(previewTax)}</span>
                </div>
                <div>
                  <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">ERT Fee</label>
                  <input type="number" min="0" value={form.ert_fee} onChange={(e) => setField('ert_fee', e.target.value)} className={inputCls} data-testid="edit-ert-fee" />
                </div>
                <div>
                  <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">Title and Registration Fee</label>
                  <input type="number" min="0" value={form.title_and_registration_fee} onChange={(e) => setField('title_and_registration_fee', e.target.value)} className={inputCls} data-testid="edit-title-reg-fee" />
                </div>
                <div className="border-t border-white/10 pt-3 space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-['Manrope'] font-semibold text-white">Total Due</span>
                    <span className="text-base font-['Manrope'] font-bold text-emerald-400 tabular-nums" data-testid="edit-pricing-total-due">
                      {formatUsdAmount(previewTotalDue)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs font-['Manrope'] text-white/50">Trade-in Value</span>
                    <span className="text-sm font-['Manrope'] text-white/70 tabular-nums" data-testid="edit-pricing-trade-in-credit">
                      −{formatUsdAmount(previewTradeInValue)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs font-['Manrope'] text-white/50">Down Payment</span>
                    <span className="text-sm font-['Manrope'] text-white/70 tabular-nums" data-testid="edit-pricing-down-payment-credit">
                      −{formatUsdAmount(previewDownPayment)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center border-t border-white/10 pt-2">
                    <span className="text-sm font-['Manrope'] font-semibold text-white">Remaining Due</span>
                    <span className="text-base font-['Manrope'] font-bold text-white tabular-nums" data-testid="edit-pricing-remaining-due">
                      {formatUsdAmount(previewRemainingDue)}
                    </span>
                  </div>
                </div>
              </div>

              {isFinancingDeal && (
                <div className="border-t border-white/10 pt-4 space-y-3" data-testid="edit-financing-terms">
                  <p className="text-[11px] font-['Unbounded'] text-white/50 uppercase tracking-wider">Financing Terms</p>
                  <div>
                    <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">Amount Requested</label>
                    <input type="number" min="0" step="0.01" value={form.amount_requested} onChange={(e) => setField('amount_requested', e.target.value)} className={inputCls} data-testid="edit-amount-requested" />
                  </div>
                  <div>
                    <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">Amount Financed</label>
                    <input type="number" min="0" step="0.01" value={form.amount_financed} onChange={(e) => setField('amount_financed', e.target.value)} className={inputCls} data-testid="edit-amount-financed" />
                  </div>
                  <div>
                    <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">Finance Tax Rate %</label>
                    <input type="number" min="0" step="0.01" value={form.retail_tax_rate} onChange={(e) => setField('retail_tax_rate', e.target.value)} className={inputCls} data-testid="edit-retail-tax-rate" />
                  </div>
                  <div className="grid grid-cols-2 gap-3 p-3 border border-white/10 bg-white/5">
                    <div>
                      <p className="text-[11px] font-['Manrope'] text-white/40 mb-0.5">Finance Charge Amount</p>
                      <p className="text-sm font-['Manrope'] text-white/80 tabular-nums">{formatUsdAmount(previewFinanceCharge)}</p>
                    </div>
                    <div>
                      <p className="text-[11px] font-['Manrope'] text-white/40 mb-0.5">Total Credits Due</p>
                      <p className="text-sm font-['Manrope'] text-white/80 tabular-nums">{formatUsdAmount(previewTotalCreditsDue)}</p>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">Number of Months to Pay</label>
                    <input type="number" min="0" step="1" value={form.months_to_payoff} onChange={(e) => setField('months_to_payoff', e.target.value)} className={inputCls} data-testid="edit-months-to-payoff" />
                  </div>
                  <div>
                    <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">Monthly Payment Amount</label>
                    <input type="number" min="0" step="0.01" value={form.monthly_payment} onChange={(e) => setField('monthly_payment', e.target.value)} className={inputCls} data-testid="edit-monthly-payment" />
                  </div>
                  <div>
                    <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">Payment Due Date</label>
                    <input type="date" value={form.payment_due_date} onChange={(e) => setField('payment_due_date', e.target.value)} className={`${inputCls} [color-scheme:dark]`} data-testid="edit-payment-due-date" />
                  </div>
                  <div>
                    <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">Final Installment</label>
                    <input type="number" min="0" step="0.01" value={form.last_monthly_payment} onChange={(e) => setField('last_monthly_payment', e.target.value)} className={inputCls} data-testid="edit-last-monthly-payment" />
                  </div>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 p-5 border-t border-white/10">
              <button type="button" onClick={() => setEditing(false)} className="px-4 py-2 border border-white/10 text-white/60 text-sm font-['Manrope']">Cancel</button>
              <button
                type="button"
                onClick={handleSavePricing}
                disabled={submitting}
                className="px-5 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-['Manrope'] font-semibold disabled:opacity-50"
                data-testid="save-deal-pricing-btn"
              >
                {submitting ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {isFinancingDeal && (
        <div className="bg-white/5 border border-white/10 p-5 lg:col-span-2" data-testid="lien-holder-card">
          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-4">
            <h3 className="text-sm font-['Unbounded'] font-semibold text-white/80 flex items-center gap-2">
              <Building2 className="w-4 h-4 text-cyan-400" />
              Lien Holder / Lender Information
            </h3>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={openSelectLien}
                className="px-3 py-2 bg-cyan-600 hover:bg-cyan-500 text-white text-xs font-['Manrope'] font-semibold"
                data-testid="select-lien-holder-btn"
              >
                Select Lienholder/Lender
              </button>
              {isMainAdmin && (
                <button
                  type="button"
                  onClick={() => setShowAddLien(true)}
                  className="px-3 py-2 bg-blue-600 hover:bg-blue-500 text-white text-xs font-['Manrope'] font-semibold"
                  data-testid="add-lien-holder-btn"
                >
                  Add Lien Holder/Lender
                </button>
              )}
            </div>
          </div>
          {selectedLien ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <InfoRow label="ID Number" value={selectedLien.id_number} />
              <InfoRow label="Name" value={selectedLien.name} highlight />
              <InfoRow label="Full Address" value={selectedLien.full_address} />
              <InfoRow label="Phone" value={selectedLien.phone} />
            </div>
          ) : (
            <p className="text-sm font-['Manrope'] text-white/45">
              No lien holder selected for this financing application.
            </p>
          )}
        </div>
      )}

      {showSelectLien && (
        <div className={adminOverlay('z-[70]')} data-testid="select-lien-holder-modal">
          <div className="bg-[#0a0a0a] border border-white/10 w-full max-w-lg max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-white/10">
              <h3 className="text-base font-['Unbounded'] font-semibold text-white">Select Lienholder/Lender</h3>
              <button type="button" onClick={() => setShowSelectLien(false)} className="text-white/40 hover:text-white text-xl">&times;</button>
            </div>
            <div className="p-5 space-y-2">
              {lienLoading ? (
                <p className="text-sm font-['Manrope'] text-white/40">Loading lien holders...</p>
              ) : lienHolders.length === 0 ? (
                <p className="text-sm font-['Manrope'] text-white/45">
                  No lien holders saved yet.{isMainAdmin ? ' Use Add Lien Holder/Lender to create one.' : ' Ask the main admin to add one.'}
                </p>
              ) : lienHolders.map((holder) => (
                <button
                  key={holder.id}
                  type="button"
                  disabled={lienSaving}
                  onClick={() => handleSelectLien(holder.id)}
                  className={`w-full text-left px-4 py-3 border hover:bg-white/5 ${deal.lien_holder_id === holder.id ? 'border-cyan-500/50 bg-cyan-500/10' : 'border-white/10'}`}
                >
                  <p className="text-sm font-['Manrope'] font-semibold text-white">{holder.name}</p>
                  <p className="text-xs font-['Manrope'] text-white/45 mt-0.5">ID {holder.id_number} · {holder.full_address}</p>
                  {holder.phone && <p className="text-xs font-['Manrope'] text-white/35 mt-0.5">{holder.phone}</p>}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {showAddLien && isMainAdmin && (
        <div className={adminOverlay('z-[75]')} data-testid="add-lien-holder-modal">
          <div className="bg-[#0a0a0a] border border-white/10 w-full max-w-lg max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-white/10">
              <h3 className="text-base font-['Unbounded'] font-semibold text-white">Add Lien Holder/Lender</h3>
              <button type="button" onClick={() => setShowAddLien(false)} className="text-white/40 hover:text-white text-xl">&times;</button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">ID Number *</label>
                <input value={lienForm.id_number} onChange={(e) => setLienForm((f) => ({ ...f, id_number: e.target.value }))} className={inputCls} data-testid="lien-id-number" />
              </div>
              <div>
                <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">Name *</label>
                <input value={lienForm.name} onChange={(e) => setLienForm((f) => ({ ...f, name: e.target.value }))} className={inputCls} data-testid="lien-name" />
              </div>
              <div>
                <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">Street Address</label>
                <input value={lienForm.street_address} onChange={(e) => setLienForm((f) => ({ ...f, street_address: e.target.value }))} className={inputCls} data-testid="lien-street" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">City</label>
                  <input value={lienForm.city} onChange={(e) => setLienForm((f) => ({ ...f, city: e.target.value }))} className={inputCls} />
                </div>
                <div>
                  <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">State</label>
                  <select value={lienForm.state} onChange={(e) => setLienForm((f) => ({ ...f, state: e.target.value }))} className={`${inputCls} [&>option]:bg-gray-900`}>
                    <option value="">Select...</option>
                    {US_STATES.map((st) => <option key={st} value={st}>{st}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">ZIP</label>
                  <input value={lienForm.zip} onChange={(e) => setLienForm((f) => ({ ...f, zip: e.target.value }))} className={inputCls} />
                </div>
              </div>
              <div>
                <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">Phone Number</label>
                <input value={lienForm.phone} onChange={(e) => setLienForm((f) => ({ ...f, phone: e.target.value }))} className={inputCls} />
              </div>
            </div>
            <div className="flex justify-end gap-3 p-5 border-t border-white/10">
              <button type="button" onClick={() => setShowAddLien(false)} className="px-4 py-2 border border-white/10 text-white/60 text-sm font-['Manrope']">Cancel</button>
              <button type="button" onClick={handleAddLien} disabled={lienSaving} className="px-5 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-['Manrope'] font-semibold disabled:opacity-50">
                {lienSaving ? 'Saving...' : 'Save Lien Holder'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function InfoRow({ label, value, mono, highlight, testId }) {
  return (
    <div className="flex justify-between items-start gap-4">
      <span className="text-xs font-['Manrope'] text-white/40 shrink-0">{label}</span>
      <span data-testid={testId} className={`text-sm font-['Manrope'] text-right ${highlight ? 'text-white font-semibold' : 'text-white/80'} ${mono ? 'font-mono text-xs' : ''}`}>
        {value || '-'}
      </span>
    </div>
  );
}

const formatPurgeDate = (isoDate) => {
  if (!isoDate) return '-';
  return formatDate(isoDate);
};

const resolveDocumentDisplayStatus = (doc) => {
  if (doc?.display_status) return doc.display_status;
  if (doc?.esign_status === 'signed' || doc?.signed_pdf) return 'signed';
  if (doc?.esign_status === 'sent' || doc?.status === 'awaiting_signature') return 'awaiting_signature';
  return doc?.status || 'draft';
};

const NEXT_JOB_DOCUMENT_STATUSES = new Set(['approval_pending', 'approved', 'ready_to_close', 'funded']);

const nextDealJob = (deal, customerInfo, documentGroups) => {
  if (!deal?.vehicle_id) return 'Next: link a vehicle.';
  if (deal.status === 'delivered' && !deal.taxes_filed_at) return 'Next: file ST-556.';
  if (deal.status === 'filed_taxes' || deal.status === 'archived') return '';
  const docs = flattenDealDocuments(documentGroups || {});
  if (docs.some((doc) => resolveDocumentDisplayStatus(doc) === 'awaiting_signature')) {
    return 'Next: finish signatures.';
  }
  const packetCount = documentGroups?.packets?.length || 0;
  const singleCount = documentGroups?.single_templates?.length || 0;
  if (packetCount + singleCount === 0 && NEXT_JOB_DOCUMENT_STATUSES.has(deal.status)) {
    return 'Next: generate documents.';
  }
  const hasLicense = Boolean(customerInfo?.driver_license_info);
  const licenseRequested = Boolean(customerInfo?.pending_driver_license_request);
  if (customerInfo?.is_financing_applicant && !hasLicense && !licenseRequested) {
    return 'Next: collect the driver license.';
  }
  return '';
};

const DOCUMENT_STATUS_LABELS = {
  draft: 'Draft',
  awaiting_signature: 'Awaiting Signature',
  signed: 'Signed',
};

const DOCUMENT_STATUS_STYLES = {
  draft: 'bg-green-500/15 text-green-400 border-green-500/30',
  awaiting_signature: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  signed: 'bg-cyan-500/15 text-cyan-400 border-cyan-500/30',
};

const formatDocumentStatusLabel = (doc) => {
  const key = resolveDocumentDisplayStatus(doc);
  return DOCUMENT_STATUS_LABELS[key] || key.replace(/_/g, ' ');
};

const getDocumentStatusStyle = (doc) => {
  const key = resolveDocumentDisplayStatus(doc);
  return DOCUMENT_STATUS_STYLES[key] || DOCUMENT_STATUS_STYLES.draft;
};

const canSendDocumentForSignature = (doc) => {
  if (doc?.esign_send_allowed === false) return false;
  if (doc?.supports_esign === false) return false;
  if (doc?.esign_status === 'sent' || doc?.esign_status === 'signed') return false;
  if (doc?.signed_pdf) return false;
  const status = resolveDocumentDisplayStatus(doc);
  return status !== 'awaiting_signature' && status !== 'signed';
};

const openDocumentPdfInNewTab = async (doc, headers) => {
  const tab = window.open('', '_blank');
  if (!tab) {
    toast.error('Pop-up blocked. Allow pop-ups to view the document.');
    return;
  }
  try {
    const res = await axios.get(`${BACKEND_URL}/api/admin/documents/${doc.id}/view`, {
      headers,
      responseType: 'blob',
    });
    const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
    tab.location.href = url;
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (err) {
    tab.close();
    toast.error('Failed to open document');
  }
};

const describeGenerationConflict = (conflict) => {
  if (conflict.kind === 'packet') {
    return `${conflict.packet_label} packet (${conflict.document_count} document${conflict.document_count === 1 ? '' : 's'})`;
  }
  return `${conflict.template_label || formatDocumentTemplateLabel(conflict.template_name)}`;
};

/* ===== SEND FOR SIGNATURE MODAL ===== */
function SendForSignatureModal({ doc, token, onClose, onSuccess }) {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [info, setInfo] = useState(null);
  const [customerEmail, setCustomerEmail] = useState('');
  const [coBuyerEmail, setCoBuyerEmail] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${BACKEND_URL}/api/admin/documents/${doc.id}/esign-send-info`, { headers })
      .then((res) => {
        if (cancelled) return;
        setInfo(res.data);
        setCustomerEmail(res.data.customer_email || '');
        setCoBuyerEmail(res.data.co_buyer_email || '');
      })
      .catch((err) => {
        if (cancelled) return;
        toast.error(err.response?.data?.detail || 'Failed to load e-signature details');
        onClose();
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [doc.id, headers, onClose]);

  const handleSubmit = async () => {
    const email = customerEmail.trim();
    if (!email) {
      toast.error('Customer email is required');
      return;
    }
    if (info?.co_buyer_required && !coBuyerEmail.trim()) {
      toast.error('Co-buyer email is required for this document');
      return;
    }
    setSubmitting(true);
    try {
      const params = { customer_email: email };
      const coEmail = coBuyerEmail.trim();
      if (coEmail) params.co_buyer_email = coEmail;
      await axios.post(
        `${BACKEND_URL}/api/admin/documents/${doc.id}/send-for-signature`,
        null,
        { headers, params },
      );
      onSuccess();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : detail?.message || 'Failed to send for e-signature');
    } finally {
      setSubmitting(false);
    }
  };

  const showCoBuyerField = Boolean(info?.co_buyer_required || info?.has_co_buyer);

  return (
    <div className={adminOverlay('z-[60]')} data-testid="send-for-signature-modal">
      <div className="bg-[#0a0a0a] border border-white/10 w-full max-w-md">
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <h2 className="text-lg font-['Unbounded'] font-bold text-white flex items-center gap-2">
            <FileSignature className="w-5 h-5 text-purple-400" />
            Send for E-Signature
          </h2>
          <button type="button" onClick={onClose} className="text-white/40 hover:text-white text-xl">&times;</button>
        </div>

        <div className="p-5 space-y-4">
          <p className="text-sm font-['Manrope'] text-white/60">
            {formatDocumentTemplateLabel(doc.template_name)}
          </p>

          {loading ? (
            <p className="text-sm font-['Manrope'] text-white/40">Loading...</p>
          ) : !info?.can_send ? (
            <p className="text-sm font-['Manrope'] text-amber-400/90 border border-amber-500/20 bg-amber-500/5 px-3 py-2">
              This document cannot be sent for e-signature.
            </p>
          ) : (
            <>
              <div>
                <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">Customer Email *</label>
                <input
                  type="email"
                  value={customerEmail}
                  onChange={(e) => setCustomerEmail(e.target.value)}
                  placeholder="customer@example.com"
                  data-testid="esign-customer-email-input"
                  className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-purple-500/50"
                />
              </div>
              {showCoBuyerField && (
                <div>
                  <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">
                    Co-Buyer Email{info?.co_buyer_required ? ' *' : ''}
                  </label>
                  <input
                    type="email"
                    value={coBuyerEmail}
                    onChange={(e) => setCoBuyerEmail(e.target.value)}
                    placeholder="cobuyer@example.com"
                    data-testid="esign-co-buyer-email-input"
                    className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-purple-500/50"
                  />
                  {info?.co_buyer_required && (
                    <p className="text-[11px] font-['Manrope'] text-white/35 mt-1">
                      This document requires a co-buyer signature.
                    </p>
                  )}
                </div>
              )}
              <p className="text-xs font-['Manrope'] text-white/40 border border-white/10 bg-white/5 px-3 py-2">
                Signing links will be emailed to the recipient(s). The document status will update to Awaiting Signature, then Signed when complete.
              </p>
            </>
          )}
        </div>

        <div className="flex justify-end gap-3 p-5 border-t border-white/10">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-white/10 text-white/60 text-sm font-['Manrope'] hover:bg-white/5 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={loading || submitting || !info?.can_send}
            data-testid="esign-send-submit-btn"
            className="px-5 py-2 bg-purple-500 hover:bg-purple-600 text-white text-sm font-['Manrope'] font-semibold transition-colors disabled:opacity-50"
          >
            {submitting ? 'Sending...' : 'Send for Signature'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ===== DOCUMENTS TAB ===== */
function DocumentsTab({ documentGroups, token, onRefresh, dealId, onEdit, onGenerate }) {
  const headers = { Authorization: `Bearer ${token}` };
  const { packets, single_templates: singleTemplates, total_count: totalCount } = documentGroups;
  const [expandedPackets, setExpandedPackets] = useState({});
  const [deletingId, setDeletingId] = useState(null);
  const [deletingBatchId, setDeletingBatchId] = useState(null);
  const [signDoc, setSignDoc] = useState(null);

  const handleDownload = async (doc) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/documents/${doc.id}/download`, {
        headers,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${doc.template_name || 'document'}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Failed to download document');
    }
  };

  const handleView = (doc) => openDocumentPdfInNewTab(doc, headers);

  const togglePacket = (batchId) => {
    setExpandedPackets((prev) => ({ ...prev, [batchId]: !prev[batchId] }));
  };

  const handleDeleteDocument = async (doc) => {
    if (!window.confirm(`Move "${formatDocumentTemplateLabel(doc.template_name)}" to Recently Deleted Documents?`)) {
      return;
    }
    setDeletingId(doc.id);
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/documents/${doc.id}`, { headers });
      toast.success('Document moved to Recently Deleted Documents');
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete document');
    } finally {
      setDeletingId(null);
    }
  };

  const handleDeletePacket = async (packet) => {
    if (!window.confirm(`Move the entire "${packet.packet_label}" packet to Recently Deleted Documents?`)) {
      return;
    }
    setDeletingBatchId(packet.batch_id);
    try {
      await axios.delete(
        `${BACKEND_URL}/api/admin/deals/${dealId}/documents/packets/${encodeURIComponent(packet.batch_id)}`,
        { headers },
      );
      toast.success('Packet moved to Recently Deleted Documents');
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete packet');
    } finally {
      setDeletingBatchId(null);
    }
  };

  if (totalCount === 0) {
    return (
      <div data-testid="documents-tab">
        <div className="bg-white/5 border border-white/10 p-12 text-center">
          <FileText className="w-10 h-10 text-white/15 mx-auto mb-3" />
          <p className="text-white/40 font-['Manrope'] text-sm">No documents generated yet for this deal.</p>
          {onGenerate && (
            <button
              type="button"
              onClick={onGenerate}
              data-testid="documents-empty-generate-btn"
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-['Manrope'] font-semibold"
            >
              <Printer className="w-4 h-4" />
              Generate Documents
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="documents-tab" className="space-y-6">
      {signDoc && (
        <SendForSignatureModal
          doc={signDoc}
          token={token}
          onClose={() => setSignDoc(null)}
          onSuccess={() => {
            setSignDoc(null);
            onRefresh?.();
            toast.success('Document sent for e-signature');
          }}
        />
      )}

      <section data-testid="generated-packets-section">
        <div className="flex items-center gap-2 mb-3">
          <Package className="w-4 h-4 text-blue-400" />
          <h3 className="text-sm font-['Unbounded'] font-semibold text-white">Generated Packets</h3>
          <span className="text-[11px] font-['Manrope'] text-white/35">({packets.length})</span>
        </div>
        {packets.length === 0 ? (
          <div className="bg-white/5 border border-white/10 px-4 py-6 text-center">
            <p className="text-white/35 font-['Manrope'] text-sm">No packet batches generated yet.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {packets.map((packet) => {
              const expanded = expandedPackets[packet.batch_id] ?? false;
              return (
                <div key={packet.batch_id} className="bg-white/5 border border-white/10 overflow-hidden" data-testid={`packet-batch-${packet.batch_id}`}>
                  <div className="flex items-center gap-2 px-4 py-3 hover:bg-white/[0.03] transition-colors">
                    <button
                      type="button"
                      onClick={() => togglePacket(packet.batch_id)}
                      className="flex flex-1 items-center gap-3 min-w-0 text-left"
                    >
                      <ChevronRight className={`w-4 h-4 text-white/40 shrink-0 ${expanded ? 'rotate-90' : ''}`} />
                      <div className="min-w-0">
                        <p className="font-['Manrope'] text-sm text-white font-medium truncate">{packet.packet_label}</p>
                        <p className="font-['Manrope'] text-[11px] text-white/40 mt-0.5">
                          {formatDate(packet.generated_at)}
                          {packet.requested_language ? ` · ${packet.requested_language.toUpperCase()}` : ''}
                          {` · ${packet.document_count} document${packet.document_count === 1 ? '' : 's'}`}
                        </p>
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeletePacket(packet)}
                      disabled={deletingBatchId === packet.batch_id}
                      className="p-1.5 hover:bg-red-500/10 transition-colors disabled:opacity-50"
                      title="Delete packet"
                    >
                      <Trash2 className="w-4 h-4 text-red-400" />
                    </button>
                  </div>
                  {expanded && (
                    <DealDocumentsTable
                      documents={packet.documents}
                      onView={handleView}
                      onDownload={handleDownload}
                      onEdit={onEdit}
                      onSendForSignature={(doc) => setSignDoc(doc)}
                      onDelete={handleDeleteDocument}
                      deletingId={deletingId}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      <section data-testid="generated-single-templates-section">
        <div className="flex items-center gap-2 mb-3">
          <FileText className="w-4 h-4 text-purple-400" />
          <h3 className="text-sm font-['Unbounded'] font-semibold text-white">Generated Single Templates</h3>
          <span className="text-[11px] font-['Manrope'] text-white/35">({singleTemplates.length})</span>
        </div>
        {singleTemplates.length === 0 ? (
          <div className="bg-white/5 border border-white/10 px-4 py-6 text-center">
            <p className="text-white/35 font-['Manrope'] text-sm">No single-template documents generated yet.</p>
          </div>
        ) : (
          <div className="bg-white/5 border border-white/10 overflow-hidden">
            <DealDocumentsTable
              documents={singleTemplates}
              onView={handleView}
              onDownload={handleDownload}
              onEdit={onEdit}
              onSendForSignature={(doc) => setSignDoc(doc)}
              onDelete={handleDeleteDocument}
              deletingId={deletingId}
            />
          </div>
        )}
      </section>
    </div>
  );
}

function DealDocumentsTable({
  documents,
  onView,
  onDownload,
  onEdit,
  onSendForSignature,
  onDelete,
  deletingId,
  showDeletedMeta = false,
  deleteActionTitle = 'Delete document',
}) {
  return (
    <div className="overflow-x-auto">
    <table className="w-full min-w-[640px] border-t border-white/10">
      <thead className="bg-white/5 border-b border-white/10">
        <tr>
          {[
            'Template',
            'Language',
            'Generated',
            'Generated By',
            ...(showDeletedMeta ? ['Deleted', 'Permanent Removal'] : []),
            'Status',
            'Actions',
          ].map((h) => (
            <th key={h} className="px-4 py-2.5 text-left text-[11px] font-['Manrope'] font-semibold text-white/60 uppercase tracking-wider">{h}</th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-white/[0.06]">
        {documents.map((doc) => (
          <tr key={doc.id} className="hover:bg-white/[0.03]">
            <td className="px-4 py-2.5 font-['Manrope'] text-sm text-white">
              {formatDocumentTemplateLabel(doc.template_name)}
            </td>
            <td className="px-4 py-2.5 font-['Manrope'] text-sm text-white/60">{doc.language?.toUpperCase()}</td>
            <td className="px-4 py-2.5 font-['Manrope'] text-xs text-white/40">{formatDate(doc.generated_at)}</td>
            <td className="px-4 py-2.5 font-['Manrope'] text-sm text-white/70">
              {doc.salesperson_name?.trim() || '-'}
            </td>
            {showDeletedMeta && (
              <>
                <td className="px-4 py-2.5 font-['Manrope'] text-xs text-white/40">{formatDate(doc.deleted_at)}</td>
                <td className="px-4 py-2.5 font-['Manrope'] text-xs text-amber-400/80">{formatPurgeDate(doc.permanent_delete_after)}</td>
              </>
            )}
            <td className="px-4 py-2.5">
              <span className={`px-2 py-0.5 text-[11px] font-['Manrope'] border ${getDocumentStatusStyle(doc)}`}>
                {formatDocumentStatusLabel(doc)}
              </span>
            </td>
            <td className="px-4 py-2.5">
              <div className="flex items-center gap-1">
                {onView && (
                  <button
                    onClick={() => onView(doc)}
                    data-testid={`view-doc-${doc.id?.slice(0, 8)}`}
                    className="p-1.5 hover:bg-white/10 transition-colors"
                    title="View document"
                  >
                    <Eye className="w-4 h-4 text-sky-400" />
                  </button>
                )}
                <button
                  onClick={() => onDownload(doc)}
                  data-testid={`download-doc-${doc.id?.slice(0, 8)}`}
                  className="p-1.5 hover:bg-white/10 transition-colors"
                  title="Download PDF"
                >
                  <Download className="w-4 h-4 text-blue-400" />
                </button>
                {onSendForSignature && doc.supports_esign !== false && (
                  <button
                    onClick={() => canSendDocumentForSignature(doc) && onSendForSignature(doc)}
                    disabled={!canSendDocumentForSignature(doc)}
                    data-testid={`esign-doc-${doc.id?.slice(0, 8)}`}
                    className="p-1.5 hover:bg-purple-500/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                    title={
                      canSendDocumentForSignature(doc)
                        ? 'Send for e-signature'
                        : resolveDocumentDisplayStatus(doc) === 'signed'
                          ? 'Document is fully signed'
                          : 'Already sent for e-signature'
                    }
                  >
                    <FileSignature className="w-4 h-4 text-purple-400" />
                  </button>
                )}
                {onEdit && (
                  <button
                    onClick={() => onEdit(doc)}
                    data-testid={`edit-doc-${doc.id?.slice(0, 8)}`}
                    className="p-1.5 hover:bg-amber-500/10 transition-colors"
                    title="Edit document"
                  >
                    <Pencil className="w-4 h-4 text-amber-400" />
                  </button>
                )}
                {onDelete && (
                  <button
                    onClick={() => onDelete(doc)}
                    disabled={deletingId === doc.id}
                    data-testid={`delete-doc-${doc.id?.slice(0, 8)}`}
                    className="p-1.5 hover:bg-red-500/10 transition-colors disabled:opacity-50"
                    title={deleteActionTitle}
                  >
                    <Trash2 className="w-4 h-4 text-red-400" />
                  </button>
                )}
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
    </div>
  );
}

function RecentlyDeletedDocumentsTab({ documentGroups, retentionDays, token, onRefresh }) {
  const headers = { Authorization: `Bearer ${token}` };
  const { packets, single_templates: singleTemplates, total_count: totalCount } = documentGroups;
  const [expandedPackets, setExpandedPackets] = useState({});
  const [deletingId, setDeletingId] = useState(null);

  const handleDownload = async (doc) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/documents/${doc.id}/download`, {
        headers,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${doc.template_name || 'document'}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Failed to download document');
    }
  };

  const handleView = (doc) => openDocumentPdfInNewTab(doc, headers);

  const handlePermanentDelete = async (doc) => {
    if (!window.confirm(
      `Permanently delete "${formatDocumentTemplateLabel(doc.template_name)}"? This cannot be undone.`,
    )) {
      return;
    }
    setDeletingId(doc.id);
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/documents/${doc.id}/permanent`, { headers });
      toast.success('Document permanently deleted');
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to permanently delete document');
    } finally {
      setDeletingId(null);
    }
  };

  if (totalCount === 0) {
    return (
      <div data-testid="recently-deleted-tab">
        <div className="bg-white/5 border border-white/10 p-12 text-center">
          <Trash2 className="w-10 h-10 text-white/15 mx-auto mb-3" />
          <p className="text-white/40 font-['Manrope'] text-sm">No recently deleted documents for this deal.</p>
          <p className="text-white/25 font-['Manrope'] text-xs mt-2">
            Deleted documents are retained for {retentionDays} days before permanent removal.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="recently-deleted-tab" className="space-y-6">
      <p className="text-xs font-['Manrope'] text-white/40 border border-amber-500/20 bg-amber-500/5 px-3 py-2">
        Recently deleted documents are kept for {retentionDays} days, then permanently removed from the database.
      </p>

      <section>
        <div className="flex items-center gap-2 mb-3">
          <Package className="w-4 h-4 text-red-300" />
          <h3 className="text-sm font-['Unbounded'] font-semibold text-white">Deleted Packets</h3>
          <span className="text-[11px] font-['Manrope'] text-white/35">({packets.length})</span>
        </div>
        {packets.length === 0 ? (
          <div className="bg-white/5 border border-white/10 px-4 py-5 text-center text-white/35 text-sm font-['Manrope']">
            No deleted packets.
          </div>
        ) : (
          <div className="space-y-2">
            {packets.map((packet) => {
              const expanded = expandedPackets[packet.batch_id] ?? false;
              return (
                <div key={packet.batch_id} className="bg-white/5 border border-white/10 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setExpandedPackets((prev) => ({ ...prev, [packet.batch_id]: !prev[packet.batch_id] }))}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/[0.03] text-left"
                  >
                    <ChevronRight className={`w-4 h-4 text-white/40 shrink-0 ${expanded ? 'rotate-90' : ''}`} />
                    <div>
                      <p className="font-['Manrope'] text-sm text-white font-medium">{packet.packet_label}</p>
                      <p className="font-['Manrope'] text-[11px] text-white/40 mt-0.5">
                        Deleted {formatDate(packet.documents?.[0]?.deleted_at)}
                        {` · ${packet.document_count} document${packet.document_count === 1 ? '' : 's'}`}
                      </p>
                    </div>
                  </button>
                  {expanded && (
                    <DealDocumentsTable
                      documents={packet.documents}
                      showDeletedMeta
                      onView={handleView}
                      onDownload={handleDownload}
                      onDelete={handlePermanentDelete}
                      deletingId={deletingId}
                      deleteActionTitle="Permanently delete document"
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      <section>
        <div className="flex items-center gap-2 mb-3">
          <FileText className="w-4 h-4 text-red-300" />
          <h3 className="text-sm font-['Unbounded'] font-semibold text-white">Deleted Single Templates</h3>
          <span className="text-[11px] font-['Manrope'] text-white/35">({singleTemplates.length})</span>
        </div>
        {singleTemplates.length === 0 ? (
          <div className="bg-white/5 border border-white/10 px-4 py-5 text-center text-white/35 text-sm font-['Manrope']">
            No deleted single templates.
          </div>
        ) : (
          <div className="bg-white/5 border border-white/10 overflow-hidden">
            <DealDocumentsTable
              documents={singleTemplates}
              showDeletedMeta
              onView={handleView}
              onDownload={handleDownload}
              onDelete={handlePermanentDelete}
              deletingId={deletingId}
              deleteActionTitle="Permanently delete document"
            />
          </div>
        )}
      </section>
    </div>
  );
}

const TIMELINE_EVENT_PRESENTATION = {
  deal_created: { label: 'Deal Created', icon: Clock, color: 'text-blue-400' },
  document_generated: { label: 'Document Generated', icon: FileText, color: 'text-green-400' },
  document_edited: { label: 'Document Edited', icon: Pencil, color: 'text-amber-400' },
  document_overwritten: { label: 'Document Overwritten', icon: RefreshCw, color: 'text-orange-400' },
  document_deleted: { label: 'Document Deleted', icon: Trash2, color: 'text-red-400' },
  deal_status_changed: { label: 'Deal Status Changed', icon: RefreshCw, color: 'text-purple-400' },
  customer_info_updated: { label: 'Customer Information Updated', icon: Pencil, color: 'text-sky-400' },
  deal_vehicle_changed: { label: 'Vehicle Linked', icon: Car, color: 'text-purple-400' },
  customer_info_requested: { label: 'Customer Information Requested', icon: Send, color: 'text-amber-400' },
  customer_info_received: { label: 'Customer Information Received', icon: UserCheck, color: 'text-emerald-400' },
  esign_sent_buyer: { label: 'E-Sign Sent to Buyer', icon: Mail, color: 'text-purple-400' },
  esign_sent_co_buyer: { label: 'E-Sign Sent to Co-Buyer', icon: Mail, color: 'text-purple-300' },
  esign_buyer_completed: { label: 'Buyer E-Sign Completed', icon: UserCheck, color: 'text-emerald-400' },
  esign_co_buyer_completed: { label: 'Co-Buyer E-Sign Completed', icon: Users, color: 'text-emerald-300' },
  esign_fully_signed: { label: 'Fully Signed', icon: CheckCircle, color: 'text-cyan-400' },
};

const formatTimelineActor = (event) => {
  const actor = event.actor || event.metadata?.actor || {};
  const name = event.actor_name || actor.name || '';
  const email = event.actor_email || actor.email || '';
  if (name && email && name !== email) return `${name} · ${email}`;
  return name || email || '';
};

const formatTimelineEventDetail = (event) => {
  const meta = event.metadata || {};
  if (event.event_type === 'esign_sent_buyer' || event.event_type === 'esign_sent_co_buyer') {
    const parts = [];
    if (meta.template_name) parts.push(formatDocumentTemplateLabel(meta.template_name));
    if (meta.email) parts.push(meta.email);
    return parts.join(' · ');
  }
  if (meta.template_name) {
    return formatDocumentTemplateLabel(meta.template_name);
  }
  if (meta.email) {
    return meta.email;
  }
  if (event.event_type === 'deal_status_changed' && meta.from_status && meta.to_status) {
    const fromLabel = STATUS_PIPELINE.find((s) => s.value === meta.from_status)?.label
      || meta.from_status.replace(/_/g, ' ');
    const toLabel = STATUS_PIPELINE.find((s) => s.value === meta.to_status)?.label
      || meta.to_status.replace(/_/g, ' ');
    return `${fromLabel} → ${toLabel}`;
  }
  return event.description || '';
};

const mapTimelineEventForDisplay = (event) => {
  const preset = TIMELINE_EVENT_PRESENTATION[event.event_type] || {
    label: (event.event_type || 'Event').replace(/_/g, ' '),
    icon: Clock,
    color: 'text-white/50',
  };
  const detail = formatTimelineEventDetail(event);
  const desc = event.description || detail;
  return {
    id: event.id,
    date: event.occurred_at,
    label: preset.label,
    desc,
    detail: detail !== desc ? detail : '',
    actor: formatTimelineActor(event),
    icon: preset.icon,
    color: preset.color,
  };
};

/* ===== TIMELINE TAB ===== */
function TimelineTab({ events }) {
  const displayEvents = (events || [])
    .map(mapTimelineEventForDisplay)
    .sort((a, b) => new Date(b.date) - new Date(a.date));

  return (
    <div data-testid="timeline-tab">
      {displayEvents.length === 0 ? (
        <div className="bg-white/5 border border-white/10 p-12 text-center">
          <p className="text-white/40 font-['Manrope'] text-sm">No timeline events.</p>
        </div>
      ) : (
        <div className="space-y-0">
          {displayEvents.map((ev, i) => {
            const Icon = ev.icon;
            return (
              <div key={ev.id || i} className="flex gap-4 relative" data-testid={`timeline-event-${ev.id?.slice(0, 8) || i}`}>
                <div className="flex flex-col items-center">
                  <div className="w-8 h-8 flex items-center justify-center bg-white/5 border border-white/10 shrink-0">
                    <Icon className={`w-4 h-4 ${ev.color}`} />
                  </div>
                  {i < displayEvents.length - 1 && <div className="w-px flex-1 bg-white/10 my-1" />}
                </div>
                <div className="pb-6">
                  <p className="text-sm font-['Manrope'] font-medium text-white">{ev.label}</p>
                  <p className="text-xs font-['Manrope'] text-white/40">{ev.desc}</p>
                  {ev.detail && ev.detail !== ev.desc && (
                    <p className="text-[11px] font-['Manrope'] text-white/30 mt-0.5">{ev.detail}</p>
                  )}
                  {ev.actor && (
                    <p className="text-[11px] font-['Manrope'] text-white/35 mt-0.5">By {ev.actor}</p>
                  )}
                  <p className="text-[11px] font-['Manrope'] text-white/25 mt-0.5">{formatDate(ev.date)}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ===== GENERATE DOCUMENT MODAL ===== */
function GenerateDocumentModal({
  deal,
  dealId,
  customerInfo,
  templates,
  token,
  onClose,
  onSuccess,
  onCustomerInfoRefresh,
  editDocument = null,
}) {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const isEditMode = Boolean(editDocument);
  const [mode, setMode] = useState(isEditMode ? 'single' : 'packet');
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(() => createDefaultGenerateForm(deal));

  const [vehicleConditionOptions, setVehicleConditionOptions] = useState([]);
  const [vehicleConditionHint, setVehicleConditionHint] = useState('');
  const [duplicateDialog, setDuplicateDialog] = useState(null);
  const [editConfirmDialog, setEditConfirmDialog] = useState(null);
  const [showAddOtherIncomeModal, setShowAddOtherIncomeModal] = useState(false);
  const [showRequestOtherIncomeModal, setShowRequestOtherIncomeModal] = useState(false);
  const [otherIncomeForm, setOtherIncomeForm] = useState(emptyOtherIncomeForm);
  const [otherIncomeRequestEmail, setOtherIncomeRequestEmail] = useState('');
  const [otherIncomeSubmitting, setOtherIncomeSubmitting] = useState(false);

  useEffect(() => {
    if (editDocument) {
      setMode('single');
      setForm(buildFormFromGenerationInputs(editDocument.generation_inputs, editDocument, deal));
      if (!editDocument.generation_inputs) {
        toast.info('This document was generated before edit history was saved. Some fields may be empty.');
      }
    } else {
      setForm(createDefaultGenerateForm(deal));
    }
    setDuplicateDialog(null);
    setEditConfirmDialog(null);
  }, [editDocument, deal]);

  const set = useCallback((key, val) => setForm((p) => ({ ...p, [key]: val })), []);

  const availableTemplates = templates.filter(
    (t) => t.name !== INTERNAL_DOCUMENT_TEMPLATE || dealAllowsInternalDocuments(deal),
  );
  const templateSelectOptions = buildTemplateSelectOptions(availableTemplates);

  const resolvedTemplateName = mode === 'single' && form.template_name
    ? resolveTemplateNameForApi(form.template_name, form.language)
    : form.template_name;

  const languageOptions = mode === 'packet'
    ? [{ value: 'en', label: 'English' }, { value: 'es', label: 'Spanish' }]
    : getSingleTemplateLanguageOptions(form.template_name);
  const showLanguageDropdown = languageOptions.length > 1;

  const generateUi = resolveGenerateUiConfig(
    mode,
    form.packet_type,
    mode === 'single' ? resolvedTemplateName : form.template_name,
    deal,
    { includePhysicalDamageInsurance: form.include_physical_damage_insurance },
  );

  const retailFinanceCharge = computeRetailFinanceCharge(
    form.credits_financed,
    form.retail_tax_rate,
    form.months_to_payoff,
  );
  const retailTotalCreditsDue = (parseFloat(form.credits_financed) || 0) + retailFinanceCharge;

  useEffect(() => {
    if (!generateUi.showVehicleConditionFields) return;
    axios.get(`${BACKEND_URL}/api/admin/documents/vehicle-condition-options`, { headers })
      .then((res) => {
        setVehicleConditionOptions(res.data.options || []);
        setVehicleConditionHint(res.data.hint || '');
      })
      .catch(() => {
        setVehicleConditionOptions([]);
      });
  }, [generateUi.showVehicleConditionFields, headers]);

  const setDamageAnswer = (key, value) => {
    setForm((p) => ({ ...p, [key]: value }));
  };

  const toggleVehicleCondition = (label) => {
    setForm((p) => {
      const selected = p.vehicle_condition_issues;
      if (selected.includes(label)) {
        return { ...p, vehicle_condition_issues: selected.filter((item) => item !== label) };
      }
      const customSlotUsed = p.vehicle_condition_custom.trim() ? 1 : 0;
      if (selected.length + customSlotUsed >= VEHICLE_CONDITION_MAX) {
        toast.error(`Select up to ${VEHICLE_CONDITION_MAX} conditions total (predefined + custom).`);
        return p;
      }
      return { ...p, vehicle_condition_issues: [...selected, label] };
    });
  };

  const selectedConditionCount = form.vehicle_condition_issues.length + (form.vehicle_condition_custom.trim() ? 1 : 0);
  const customConditionDisabled = form.vehicle_condition_issues.length >= VEHICLE_CONDITION_MAX;

  const showReadyToCloseOptions = mode === 'packet' && form.packet_type === 'ready_to_close';
  const showClosingOptions = mode === 'packet' && form.packet_type === 'full_closing_packet';
  const internalDocsAllowed = dealAllowsInternalDocuments(deal);
  const availablePacketTypes = PACKET_TYPES.filter(
    (pt) => pt.value !== 'internal_documents' || internalDocsAllowed,
  );
  useEffect(() => {
    if (mode !== 'single' || !form.template_name) return;
    const opts = getSingleTemplateLanguageOptions(form.template_name);
    if (opts.length === 1 && form.language !== opts[0].value) {
      set('language', opts[0].value);
    }
  }, [mode, form.template_name, form.language, set]);

  const handleTemplateSelect = (value) => {
    setForm((prev) => {
      const next = { ...prev, template_name: value };
      if (value === SPANISH_ONLY_TEMPLATE) next.language = 'es';
      else if (!BILINGUAL_TEMPLATE_BASES[value]) next.language = 'en';
      return next;
    });
  };

  const defaultCustomerEmail = deal?.customer_email
    || customerInfo?.standalone_customer?.email
    || customerInfo?.leads?.[0]?.email
    || '';

  const openGenerateAddOtherIncome = () => {
    setOtherIncomeForm(populateOtherIncomeFormFromInfo(customerInfo?.other_income_info));
    setShowAddOtherIncomeModal(true);
  };

  const openGenerateRequestOtherIncome = () => {
    setOtherIncomeRequestEmail(defaultCustomerEmail);
    setShowRequestOtherIncomeModal(true);
  };

  const handleGenerateSaveOtherIncome = async () => {
    const validationError = validateOtherIncomeForm(otherIncomeForm);
    if (validationError) {
      toast.error(validationError);
      return;
    }
    setOtherIncomeSubmitting(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/deals/${dealId}/customer-info/other-income`,
        buildOtherIncomePayload(otherIncomeForm, true),
        { headers },
      );
      toast.success('Other income information saved');
      setShowAddOtherIncomeModal(false);
      onCustomerInfoRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save other income information');
    } finally {
      setOtherIncomeSubmitting(false);
    }
  };

  const handleGenerateRequestOtherIncome = async () => {
    if (!otherIncomeRequestEmail.trim()) {
      toast.error('Customer email is required');
      return;
    }
    setOtherIncomeSubmitting(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/deals/${dealId}/customer-info/other-income/request`,
        { customer_email: otherIncomeRequestEmail.trim(), mark_income_dependent: true },
        { headers },
      );
      toast.success('Other income request email sent');
      setShowRequestOtherIncomeModal(false);
      onCustomerInfoRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send request email');
    } finally {
      setOtherIncomeSubmitting(false);
    }
  };

  const submitGeneration = async (body, overwriteExisting = false) => {
    const res = await axios.post(
      `${BACKEND_URL}/api/admin/documents/generate`,
      { ...body, overwrite_existing: overwriteExisting },
      { headers },
    );
    if (res.data?.spanish_template_notice) {
      toast.info(res.data.spanish_template_notice, { duration: 10000 });
    }
    return res;
  };

  const handleDuplicateOverwrite = async () => {
    if (!duplicateDialog?.body) return;
    setSubmitting(true);
    try {
      await submitGeneration(duplicateDialog.body, true);
      setDuplicateDialog(null);
      onSuccess();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : detail?.message || 'Generation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditConfirmProceed = async () => {
    if (!editConfirmDialog?.body || !editDocument?.id) return;
    setSubmitting(true);
    try {
      await submitGeneration(
        { ...editConfirmDialog.body, replace_document_id: editDocument.id },
        true,
      );
      setEditConfirmDialog(null);
      onSuccess();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : detail?.message || 'Failed to update document');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    if (!isEditMode && mode === 'packet' && !form.packet_type) {
      toast.error('Select a packet type');
      return;
    }
    if (!isEditMode && mode === 'single' && !form.template_name) {
      toast.error('Select a template');
      return;
    }
    if (isEditMode && !form.template_name) {
      toast.error('Template could not be determined for this document');
      return;
    }
    if (
      (mode === 'packet' && form.packet_type === 'internal_documents') ||
      (mode === 'single' && form.template_name === INTERNAL_DOCUMENT_TEMPLATE)
    ) {
      if (!internalDocsAllowed) {
        toast.error('ST-556 internal documents are only available after the deal is marked Delivered');
        return;
      }
    }
    if (generateUi.showCreditApplicationFields) {
      const amount = parseFloat(form.amount_requested);
      if (!form.amount_requested || Number.isNaN(amount) || amount <= 0) {
        toast.error('Amount Requested is required');
        return;
      }
      if (!form.payment_date_desired) {
        toast.error('Payment Date Desired is required');
        return;
      }
    }
    if (generateUi.showDamageDisclosureFields) {
      const unanswered = DAMAGE_DISCLOSURE_QUESTIONS.find((q) => form[q.key] === null);
      if (unanswered) {
        toast.error(`Please select Yes or No for: ${unanswered.label}`);
        return;
      }
    }
    if (generateUi.showVehicleConditionFields) {
      const conditionError = validateVehicleConditionSelections(
        form.vehicle_condition_issues,
        form.vehicle_condition_custom,
      );
      if (conditionError) {
        toast.error(conditionError);
        return;
      }
    }
    if (generateUi.physicalDamageInsuranceFields) {
      if (!form.insurance_company_city.trim()) {
        toast.error('Insurance Company City is required');
        return;
      }
      if (!form.insurance_company_state.trim()) {
        toast.error('Insurance Company State is required');
        return;
      }
      if (!form.insurance_company_zip.trim()) {
        toast.error('Insurance Company Zip is required');
        return;
      }
    }
    if (generateUi.showRetailInstallmentFields) {
      const taxRate = parseFloat(form.retail_tax_rate);
      const amountFinanced = parseFloat(form.credits_financed);
      const months = parseInt(form.months_to_payoff, 10);
      const monthlyPayment = parseFloat(form.monthly_payment);
      const lastMonthlyPayment = parseFloat(form.last_monthly_payment);

      if (!form.retail_tax_rate || Number.isNaN(taxRate) || taxRate < 0) {
        toast.error('Annual Tax Percentage is required');
        return;
      }
      if (!form.credits_financed || Number.isNaN(amountFinanced) || amountFinanced <= 0) {
        toast.error('Amount Financed is required');
        return;
      }
      if (!form.payment_due_date) {
        toast.error('Monthly Payment Due Date is required');
        return;
      }
      if (!form.months_to_payoff || Number.isNaN(months) || months <= 0) {
        toast.error('Number of Payments is required');
        return;
      }
      if (!form.monthly_payment || Number.isNaN(monthlyPayment) || monthlyPayment <= 0) {
        toast.error('Monthly Payment Amount is required');
        return;
      }
      if (!form.last_monthly_payment || Number.isNaN(lastMonthlyPayment) || lastMonthlyPayment <= 0) {
        toast.error('Final Installment is required');
        return;
      }
    }
    setSubmitting(true);
    let body;
    try {
      body = {
        deal_id: deal.id,
        language: form.language,
        salesperson_name: generateUi.showSalesperson ? (form.salesperson_name || null) : null,
      };

      if (generateUi.showChargeErtFee) {
        body.charge_ert_fee = form.charge_ert_fee;
      }

      if (generateUi.showInsuranceFields) {
        const insurancePayload = {
          insurance_company: form.insurance_company || null,
          insurance_agent: form.insurance_agent || null,
          insurance_policy_number: form.insurance_policy_number || null,
          insurance_company_phone: form.insurance_company_phone || null,
          insurance_company_street_address: form.insurance_company_address || null,
        };
        if (generateUi.physicalDamageInsuranceFields) {
          insurancePayload.insurance_company_city_state_zip = formatInsuranceCompanyCityStateZip(
            form.insurance_company_city,
            form.insurance_company_state,
            form.insurance_company_zip,
          );
        }
        if (!generateUi.billOfSaleInsuranceFields) {
          Object.assign(insurancePayload, {
            fire_thief_cac_coverage: form.fire_thief_cac_coverage || null,
            collision_coverage: form.collision_coverage || null,
            comprehensive_coverage: form.comprehensive_coverage || null,
            insurance_start_date: form.insurance_start_date || null,
            insurance_end_date: form.insurance_end_date || null,
            insurance_deductible: form.insurance_deductible ? parseInt(form.insurance_deductible, 10) : null,
          });
        }
        Object.assign(body, insurancePayload);
      }

      if (generateUi.showWarrantyFields) {
        Object.assign(body, {
          warranty_type: form.warranty_type || null,
          warranty_level: form.warranty_level || null,
          manufacturer_warranty_still_applies: form.manufacturer_warranty_still_applies,
          manufacturer_used_vehicle_warranty_applies: form.manufacturer_used_vehicle_warranty_applies,
          other_used_vehicle_warranty_applies: form.other_used_vehicle_warranty_applies,
          service_contract: form.service_contract,
        });
      }

      if (generateUi.showCreditApplicationFields) {
        body.amount_requested = parseFloat(form.amount_requested);
        body.payment_date_desired = formatIsoDateForApi(form.payment_date_desired);
        body.income_dependent_on_other = Boolean(form.income_dependent_on_other);
      }

      if (generateUi.showVehicleConditionFields) {
        Object.assign(body, {
          vehicle_condition_issues: form.vehicle_condition_issues,
          vehicle_condition_custom: form.vehicle_condition_custom.trim() || null,
        });
      }

      if (generateUi.showDamageDisclosureFields) {
        Object.assign(body, {
          salvaged_vehicle: form.salvaged_vehicle,
          flood_damage: form.flood_damage,
          frame_damage: form.frame_damage,
          reconstructed_vehicle: form.reconstructed_vehicle,
          clean_title: form.clean_title,
        });
      }

      if (generateUi.showOdometerDisclosureFields) {
        Object.assign(body, {
          mileage_excess_of_mech_limit: form.mileage_excess_of_mech_limit,
          odometer_discrepancy: form.odometer_discrepancy,
        });
      }

      if (generateUi.showRetailInstallmentFields) {
        Object.assign(body, {
          retail_tax_rate: parseFloat(form.retail_tax_rate),
          credits_financed: parseFloat(form.credits_financed),
          payment_due_date: formatIsoDateForApi(form.payment_due_date),
          charge_ert_fee: form.charge_ert_fee,
          months_to_payoff: parseInt(form.months_to_payoff, 10),
          monthly_payment: parseFloat(form.monthly_payment),
          last_monthly_payment: parseFloat(form.last_monthly_payment),
          business_use: form.business_use,
          insurance_term: form.insurance_term.trim() || null,
          insurance_premium: form.insurance_premium ? parseFloat(form.insurance_premium) : null,
          credit_life_insurance: form.credit_life_insurance,
          credit_life_insurance_premium: form.credit_life_insurance && form.credit_life_insurance_premium
            ? parseFloat(form.credit_life_insurance_premium)
            : null,
          credit_disability_insurance: form.credit_disability_insurance,
          credit_disability_insurance_premium: form.credit_disability_insurance && form.credit_disability_insurance_premium
            ? parseFloat(form.credit_disability_insurance_premium)
            : null,
          other_insurance_type: form.other_insurance_type.trim() || null,
          other_insurance_premium: form.other_insurance_premium ? parseFloat(form.other_insurance_premium) : null,
          other_insurance_term: form.other_insurance_term.trim() || null,
        });
      }

      if (mode === 'packet') {
        body.packet_type = form.packet_type;
        if (form.packet_type === 'ready_to_close') {
          body.include_extended_warranty_offer = form.include_extended_warranty_offer;
          body.include_physical_damage_insurance = form.include_physical_damage_insurance;
        }
        if (form.packet_type === 'full_closing_packet') {
          body.include_buyers_guide = form.include_buyers_guide;
          body.include_consent_testimonial = form.include_consent_testimonial;
          body.include_power_of_attorney = form.include_power_of_attorney;
        }
      } else {
        body.template_name = resolveTemplateNameForApi(form.template_name, form.language);
      }

      if (isEditMode) {
        setEditConfirmDialog({ body });
        return;
      }

      await submitGeneration(body, false);
      onSuccess();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail?.code === 'duplicate_documents' && body) {
        setDuplicateDialog({ body, detail });
        return;
      }
      toast.error(typeof detail === 'string' ? detail : detail?.message || 'Generation failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
    <div className={adminOverlay('z-[60]')} data-testid="generate-doc-modal">
      <div className="bg-[#0a0a0a] border border-white/10 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-white/10 sticky top-0 bg-[#0a0a0a] z-10">
          <h2 className="text-lg font-['Unbounded'] font-bold text-white">
            {isEditMode ? 'Edit Document' : 'Generate Documents'}
          </h2>
          <button onClick={onClose} className="text-white/40 hover:text-white text-xl">&times;</button>
        </div>

        <div className="p-5 space-y-5">
          {!isEditMode && (
            <div className="flex gap-2">
              <button
                onClick={() => setMode('packet')}
                className={`px-4 py-2 text-sm font-['Manrope'] border transition-colors ${mode === 'packet' ? 'bg-blue-500/15 text-blue-400 border-blue-500/30' : 'bg-white/5 text-white/40 border-white/10'}`}
              >
                Packet (batch)
              </button>
              <button
                onClick={() => setMode('single')}
                className={`px-4 py-2 text-sm font-['Manrope'] border transition-colors ${mode === 'single' ? 'bg-blue-500/15 text-blue-400 border-blue-500/30' : 'bg-white/5 text-white/40 border-white/10'}`}
              >
                Single Template
              </button>
            </div>
          )}

          {isEditMode ? (
            <div>
              <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">Template</label>
              <p className="text-sm font-['Manrope'] text-white border border-white/10 bg-white/5 px-3 py-2.5">
                {formatDocumentTemplateLabel(editDocument?.template_name)}
              </p>
            </div>
          ) : mode === 'packet' ? (
            <div>
              <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">Packet Type *</label>
              <select
                value={form.packet_type}
                onChange={(e) => set('packet_type', e.target.value)}
                data-testid="packet-type-select"
                className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50 [&>option]:bg-gray-900"
              >
                <option value="">Select packet type...</option>
                {availablePacketTypes.map((pt) => (
                  <option key={pt.value} value={pt.value}>{pt.label} - {pt.desc}</option>
                ))}
              </select>
            </div>
          ) : (
            <div>
              <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">Template *</label>
              <select
                value={form.template_name}
                onChange={(e) => handleTemplateSelect(e.target.value)}
                data-testid="template-name-select"
                className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50 [&>option]:bg-gray-900"
              >
                <option value="">Select template...</option>
                {templateSelectOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          )}

          {/* Language + Salesperson */}
          {(showLanguageDropdown || generateUi.showSalesperson) && (
            <div className={`grid gap-3 ${showLanguageDropdown && generateUi.showSalesperson ? 'grid-cols-2' : 'grid-cols-1'}`}>
              {showLanguageDropdown && (
                <div>
                  <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">Language</label>
                  <select value={form.language} onChange={(e) => set('language', e.target.value)} data-testid="template-language-select" className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50 [&>option]:bg-gray-900">
                    {languageOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              )}
              {generateUi.showSalesperson && (
                <div>
                  <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">Salesperson Name</label>
                  <input type="text" value={form.salesperson_name} onChange={(e) => set('salesperson_name', e.target.value)} placeholder="John Doe" data-testid="salesperson-input" className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50" />
                </div>
              )}
            </div>
          )}

          {generateUi.showChargeErtFee && (
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.charge_ert_fee} onChange={(e) => set('charge_ert_fee', e.target.checked)} className="accent-blue-500" />
              <span className="text-sm font-['Manrope'] text-white/70">Charge ERT Fee ($35)</span>
            </label>
          )}

          {generateUi.showDamageDisclosureFields && (
            <div className="border-t border-white/10 pt-4 space-y-4">
              <h4 className="text-xs font-['Unbounded'] text-white/60 mb-1">Vehicle History</h4>
              <div className="space-y-3">
                {DAMAGE_DISCLOSURE_QUESTIONS.map((question) => (
                  <DamageDisclosureYesNoRow
                    key={question.key}
                    label={question.label}
                    value={form[question.key]}
                    onYes={() => setDamageAnswer(question.key, true)}
                    onNo={() => setDamageAnswer(question.key, false)}
                  />
                ))}
              </div>
            </div>
          )}

          {generateUi.showOdometerDisclosureFields && (
            <div className="border-t border-white/10 pt-4 space-y-3" data-testid="odometer-disclosure-fields">
              <h4 className="text-xs font-['Unbounded'] text-white/60 mb-1">Odometer Disclosure</h4>
              <ModalCheckbox
                label="Mileage Excess of Mechanical Limit *"
                checked={form.mileage_excess_of_mech_limit}
                onChange={(v) => set('mileage_excess_of_mech_limit', v)}
              />
              <ModalCheckbox
                label="Odometer Discrepancy *"
                checked={form.odometer_discrepancy}
                onChange={(v) => set('odometer_discrepancy', v)}
              />
            </div>
          )}

          {generateUi.showVehicleConditionFields && (
            <VehicleConditionFieldsSection
              options={vehicleConditionOptions}
              hint={vehicleConditionHint}
              selectedIssues={form.vehicle_condition_issues}
              customValue={form.vehicle_condition_custom}
              selectedCount={selectedConditionCount}
              customDisabled={customConditionDisabled}
              onToggle={toggleVehicleCondition}
              onCustomChange={(v) => set('vehicle_condition_custom', v)}
            />
          )}

          {generateUi.showRetailInstallmentFields && (
            <RetailInstallmentFieldsSection
              form={form}
              set={set}
              financeCharge={retailFinanceCharge}
              totalCreditsDue={retailTotalCreditsDue}
              formatUsd={formatUsd}
            />
          )}

          {generateUi.showCreditApplicationFields && (
            <div className="border-t border-white/10 pt-4">
              <h4 className="text-xs font-['Unbounded'] text-white/60 mb-3 flex items-center gap-2">
                <DollarSign className="w-3.5 h-3.5" />
                Credit Application
              </h4>
              <div className="grid grid-cols-2 gap-3">
                <ModalInput
                  label="Amount Requested *"
                  value={form.amount_requested}
                  onChange={(v) => set('amount_requested', v)}
                  type="number"
                  placeholder="0"
                  data-testid="amount-requested-input"
                />
                <div>
                  <label className="block text-xs font-['Manrope'] text-white/60 mb-1">Payment Date Desired *</label>
                  <input
                    type="date"
                    value={form.payment_date_desired}
                    onChange={(e) => set('payment_date_desired', e.target.value)}
                    data-testid="payment-date-desired-input"
                    className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2 font-['Manrope'] focus:outline-none focus:border-blue-500/50 [color-scheme:dark]"
                  />
                </div>
              </div>
              <div className="mt-3">
                <ModalCheckbox
                  label="Income Dependent on Someone Else"
                  checked={form.income_dependent_on_other}
                  onChange={(v) => set('income_dependent_on_other', v)}
                />
                <p className="text-[11px] font-['Manrope'] text-white/30 mt-1">
                  Marks income from alimony, child support, or separate maintenance on the credit application.
                  Co-buyer consent is selected automatically when a co-buyer is on the deal.
                </p>
                {form.income_dependent_on_other && (
                  <div className="mt-3 flex flex-wrap gap-2" data-testid="generate-other-income-actions">
                    <button
                      type="button"
                      onClick={openGenerateAddOtherIncome}
                      data-testid="generate-add-other-income-btn"
                      className="flex items-center gap-2 px-3 py-2 bg-amber-600/90 hover:bg-amber-500 text-white text-xs font-['Manrope'] font-semibold"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      Add Additional Info About Other Income
                    </button>
                    <button
                      type="button"
                      onClick={openGenerateRequestOtherIncome}
                      data-testid="generate-request-other-income-btn"
                      className="flex items-center gap-2 px-3 py-2 bg-white/10 hover:bg-white/15 text-white text-xs font-['Manrope'] font-medium border border-amber-500/30"
                    >
                      <Send className="w-3.5 h-3.5" />
                      Request Additional Info About Other Income
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {showReadyToCloseOptions && (
            <div className="border-t border-white/10 pt-4 space-y-2">
              <h4 className="text-xs font-['Unbounded'] text-white/60 mb-2">Optional Documents</h4>
              <ModalCheckbox label="Extended Warranty Offer" checked={form.include_extended_warranty_offer} onChange={(v) => set('include_extended_warranty_offer', v)} />
              <ModalCheckbox label="Physical Damage Insurance" checked={form.include_physical_damage_insurance} onChange={(v) => set('include_physical_damage_insurance', v)} />
              {form.language === 'es' && (
                <p className="text-[11px] font-['Manrope'] text-white/30">Spanish Transaction Format is included automatically when language is Spanish.</p>
              )}
            </div>
          )}

          {showClosingOptions && (
            <div className="border-t border-white/10 pt-4 space-y-2">
              <h4 className="text-xs font-['Unbounded'] text-white/60 mb-2">Optional Closing Documents</h4>
              <ModalCheckbox label="Buyer's Guide" checked={form.include_buyers_guide} onChange={(v) => set('include_buyers_guide', v)} />
              <ModalCheckbox label="Consent Testimonial" checked={form.include_consent_testimonial} onChange={(v) => set('include_consent_testimonial', v)} />
              <ModalCheckbox label="Power of Attorney" checked={form.include_power_of_attorney} onChange={(v) => set('include_power_of_attorney', v)} />
              {(deal.financing_id || deal.application_id) && (
                <p className="text-[11px] font-['Manrope'] text-white/30">Retail Installment Contract is included when this deal is linked to a financing application.</p>
              )}
            </div>
          )}

          {mode === 'packet' && form.packet_type === 'spot_delivery' && (
            <p className="text-xs font-['Manrope'] text-amber-400/80 border border-amber-500/20 bg-amber-500/5 px-3 py-2">
              Spot Delivery requires the Ready to Close packet to be generated first for this deal.
            </p>
          )}

          {!internalDocsAllowed && (
            <p className="text-xs font-['Manrope'] text-white/40 border border-white/10 bg-white/5 px-3 py-2">
              Internal Documents (ST-556) unlock when this deal status is set to Delivered.
            </p>
          )}

          {internalDocsAllowed && (form.packet_type === 'internal_documents' || form.template_name === INTERNAL_DOCUMENT_TEMPLATE) && (
            <p className="text-xs font-['Manrope'] text-emerald-400/90 border border-emerald-500/25 bg-emerald-500/5 px-3 py-2">
              Deal is Delivered — generate ST-556 before the 20-day tax filing deadline shown on the deal page.
            </p>
          )}

          {/* Insurance / warranty fields — template-specific */}
          {generateUi.showInsuranceFields && (
              <div className="border-t border-white/10 pt-4">
                <h4 className="text-xs font-['Unbounded'] text-white/60 mb-3 flex items-center gap-2">
                  <Shield className="w-3.5 h-3.5" />
                  Insurance Details
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  <ModalInput label="Company" value={form.insurance_company} onChange={(v) => set('insurance_company', v)} />
                  <ModalInput label="Agent" value={form.insurance_agent} onChange={(v) => set('insurance_agent', v)} />
                  <ModalInput label="Policy Number" value={form.insurance_policy_number} onChange={(v) => set('insurance_policy_number', v)} />
                  <ModalInput label="Company Phone" value={form.insurance_company_phone} onChange={(v) => set('insurance_company_phone', v)} />
                  <ModalInput label="Company Address" value={form.insurance_company_address} onChange={(v) => set('insurance_company_address', v)} className="col-span-2" />
                  {generateUi.physicalDamageInsuranceFields && (
                    <>
                      <ModalInput
                        label="Insurance Company City *"
                        value={form.insurance_company_city}
                        onChange={(v) => set('insurance_company_city', v)}
                        data-testid="insurance-company-city-input"
                      />
                      <ModalInput
                        label="Insurance Company State *"
                        value={form.insurance_company_state}
                        onChange={(v) => set('insurance_company_state', v)}
                        placeholder="e.g. IL"
                        data-testid="insurance-company-state-input"
                      />
                      <ModalInput
                        label="Insurance Company Zip *"
                        value={form.insurance_company_zip}
                        onChange={(v) => set('insurance_company_zip', v)}
                        data-testid="insurance-company-zip-input"
                      />
                    </>
                  )}
                  {!generateUi.billOfSaleInsuranceFields && (
                    <>
                      <ModalInput label="Start Date" value={form.insurance_start_date} onChange={(v) => set('insurance_start_date', v)} placeholder="MM/DD/YYYY" />
                      <ModalInput label="End Date" value={form.insurance_end_date} onChange={(v) => set('insurance_end_date', v)} placeholder="MM/DD/YYYY" />
                      <ModalInput label="Deductible ($)" value={form.insurance_deductible} onChange={(v) => set('insurance_deductible', v)} type="number" />
                    </>
                  )}
                </div>
                {!generateUi.billOfSaleInsuranceFields && (
                  <div className="flex gap-4 mt-3">
                    <ModalCheckbox label="Fire/Theft/CAC" checked={form.fire_thief_cac_coverage} onChange={(v) => set('fire_thief_cac_coverage', v)} />
                    <ModalCheckbox label="Collision" checked={form.collision_coverage} onChange={(v) => set('collision_coverage', v)} />
                    <ModalCheckbox label="Comprehensive" checked={form.comprehensive_coverage} onChange={(v) => set('comprehensive_coverage', v)} />
                  </div>
                )}
              </div>
          )}

          {generateUi.showWarrantyFields && (
              <div className="border-t border-white/10 pt-4">
                <h4 className="text-xs font-['Unbounded'] text-white/60 mb-3">Warranty Options</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">Warranty Type</label>
                    <select value={form.warranty_type} onChange={(e) => set('warranty_type', e.target.value)} className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50 [&>option]:bg-gray-900">
                      <option value="">None</option>
                      <option value="implied_only">Implied Only</option>
                      <option value="dealer_warranty">Dealer Warranty</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">Warranty Level</label>
                    <select value={form.warranty_level} onChange={(e) => set('warranty_level', e.target.value)} className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50 [&>option]:bg-gray-900">
                      <option value="">None</option>
                      <option value="full">Full</option>
                      <option value="limited">Limited</option>
                    </select>
                  </div>
                </div>
                <div className="flex flex-wrap gap-4 mt-3">
                  <ModalCheckbox label="Manufacturer Warranty Applies" checked={form.manufacturer_warranty_still_applies} onChange={(v) => set('manufacturer_warranty_still_applies', v)} />
                  <ModalCheckbox label="Mfr. Used Vehicle Warranty" checked={form.manufacturer_used_vehicle_warranty_applies} onChange={(v) => set('manufacturer_used_vehicle_warranty_applies', v)} />
                  <ModalCheckbox label="Other Used Vehicle Warranty" checked={form.other_used_vehicle_warranty_applies} onChange={(v) => set('other_used_vehicle_warranty_applies', v)} />
                  <ModalCheckbox label="Service Contract" checked={form.service_contract} onChange={(v) => set('service_contract', v)} />
                </div>
              </div>
          )}
        </div>

        <div className="flex justify-end gap-3 p-5 border-t border-white/10 sticky bottom-0 bg-[#0a0a0a]">
          <button onClick={onClose} className="px-4 py-2 border border-white/10 text-white/60 text-sm font-['Manrope'] hover:bg-white/5 transition-colors">Cancel</button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            data-testid="generate-submit-btn"
            className="px-5 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-['Manrope'] font-semibold transition-colors disabled:opacity-50"
          >
            {submitting
              ? (isEditMode ? 'Saving...' : 'Generating...')
              : (isEditMode ? 'Save Changes' : 'Generate')}
          </button>
        </div>
      </div>
    </div>

    {editConfirmDialog && (
      <div className={adminOverlay('z-[70]')} data-testid="edit-document-confirm-dialog">
        <div className="bg-[#0a0a0a] border border-blue-500/30 w-full max-w-lg p-5">
          <h3 className="text-base font-['Unbounded'] font-semibold text-blue-300 mb-2">Overwrite Document?</h3>
          <p className="text-sm font-['Manrope'] text-white/70 mb-5">
            A new document would be generated with these changes and overwrite the previously saved document.
          </p>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setEditConfirmDialog(null)}
              className="px-4 py-2 border border-white/10 text-white/70 text-sm font-['Manrope'] hover:bg-white/5"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleEditConfirmProceed}
              disabled={submitting}
              data-testid="edit-confirm-proceed-btn"
              className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-['Manrope'] font-semibold disabled:opacity-50"
            >
              {submitting ? 'Saving...' : 'Proceed'}
            </button>
          </div>
        </div>
      </div>
    )}

    {duplicateDialog && (
      <div className={adminOverlay('z-[70]')} data-testid="duplicate-documents-dialog">
        <div className="bg-[#0a0a0a] border border-amber-500/30 w-full max-w-lg p-5">
          <h3 className="text-base font-['Unbounded'] font-semibold text-amber-300 mb-2">Existing Documents Found</h3>
          <p className="text-sm font-['Manrope'] text-white/70 mb-4">
            {duplicateDialog.detail?.message || 'This deal already has documents that match your request.'}
          </p>
          <ul className="space-y-2 mb-5">
            {(duplicateDialog.detail?.conflicts || []).map((conflict, index) => (
              <li key={`${conflict.kind}-${conflict.template_name || conflict.packet_type}-${index}`} className="text-sm font-['Manrope'] text-white/80 border border-white/10 bg-white/5 px-3 py-2">
                {describeGenerationConflict(conflict)}
              </li>
            ))}
          </ul>
          <p className="text-xs font-['Manrope'] text-white/45 mb-5">
            Overwrite moves the existing document(s) or packet to Recently Deleted Documents, then generates the new version.
          </p>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setDuplicateDialog(null)}
              className="px-4 py-2 border border-white/10 text-white/70 text-sm font-['Manrope'] hover:bg-white/5"
            >
              Keep Existing
            </button>
            <button
              type="button"
              onClick={handleDuplicateOverwrite}
              disabled={submitting}
              data-testid="duplicate-overwrite-btn"
              className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-black text-sm font-['Manrope'] font-semibold disabled:opacity-50"
            >
              {submitting ? 'Overwriting...' : 'Overwrite'}
            </button>
          </div>
        </div>
      </div>
    )}

    {showAddOtherIncomeModal && (
      <div className={adminOverlay('z-[75]')} data-testid="generate-add-other-income-modal">
        <div className="bg-[#0a0a0a] border border-white/10 w-full max-w-lg max-h-[85vh] overflow-y-auto">
          <div className="flex items-center justify-between p-5 border-b border-white/10">
            <h3 className="text-base font-['Unbounded'] font-semibold text-white">Additional Info About Other Income</h3>
            <button type="button" onClick={() => setShowAddOtherIncomeModal(false)} className="text-white/40 hover:text-white text-xl">&times;</button>
          </div>
          <div className="p-5">
            <OtherIncomeFormFields form={otherIncomeForm} setForm={setOtherIncomeForm} />
          </div>
          <div className="flex justify-end gap-3 p-5 border-t border-white/10">
            <button type="button" onClick={() => setShowAddOtherIncomeModal(false)} className="px-4 py-2 border border-white/10 text-white/60 text-sm font-['Manrope']">Cancel</button>
            <button type="button" onClick={handleGenerateSaveOtherIncome} disabled={otherIncomeSubmitting} data-testid="generate-save-other-income-btn" className="px-5 py-2 bg-amber-600 hover:bg-amber-500 text-white text-sm font-['Manrope'] font-semibold disabled:opacity-50">
              {otherIncomeSubmitting ? 'Saving...' : 'Save Information'}
            </button>
          </div>
        </div>
      </div>
    )}

    {showRequestOtherIncomeModal && (
      <div className={adminOverlay('z-[75]')} data-testid="generate-request-other-income-modal">
        <div className="bg-[#0a0a0a] border border-white/10 w-full max-w-md p-5">
          <h3 className="text-base font-['Unbounded'] font-semibold text-white mb-2">Request Other Income Information</h3>
          <p className="text-xs font-['Manrope'] text-white/50 mb-4">
            An email will be sent with a secure form for the customer to submit their other income details.
          </p>
          <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">Customer Email *</label>
          <input
            type="email"
            value={otherIncomeRequestEmail}
            onChange={(e) => setOtherIncomeRequestEmail(e.target.value)}
            data-testid="generate-request-other-income-email"
            className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50 mb-5"
          />
          <div className="flex justify-end gap-3">
            <button type="button" onClick={() => setShowRequestOtherIncomeModal(false)} className="px-4 py-2 border border-white/10 text-white/60 text-sm font-['Manrope']">Cancel</button>
            <button type="button" onClick={handleGenerateRequestOtherIncome} disabled={otherIncomeSubmitting} data-testid="generate-send-other-income-request-btn" className="px-5 py-2 bg-amber-600 hover:bg-amber-500 text-white text-sm font-['Manrope'] font-semibold disabled:opacity-50">
              {otherIncomeSubmitting ? 'Sending...' : 'Send Request'}
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}

function RetailInstallmentFieldsSection({ form, set, financeCharge, totalCreditsDue, formatUsd }) {
  return (
    <div className="border-t border-white/10 pt-4 space-y-4" data-testid="retail-installment-fields">
      <h4 className="text-xs font-['Unbounded'] text-white/60 flex items-center gap-2">
        <DollarSign className="w-3.5 h-3.5" />
        Retail Installment Contract
      </h4>

      <div className="grid grid-cols-2 gap-3">
        <ModalInput
          label="Annual Tax Percentage *"
          value={form.retail_tax_rate}
          onChange={(v) => set('retail_tax_rate', v)}
          type="number"
          placeholder="e.g. 18.5"
          data-testid="retail-tax-rate-input"
        />
        <ModalInput
          label="Amount Financed *"
          value={form.credits_financed}
          onChange={(v) => set('credits_financed', v)}
          type="number"
          placeholder="0"
          data-testid="credits-financed-input"
        />
        <div>
          <label className="block text-xs font-['Manrope'] text-white/60 mb-1">Monthly Payment Due Date *</label>
          <input
            type="date"
            value={form.payment_due_date}
            onChange={(e) => set('payment_due_date', e.target.value)}
            data-testid="payment-due-date-input"
            className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2 font-['Manrope'] focus:outline-none focus:border-blue-500/50 [color-scheme:dark]"
          />
        </div>
        <ModalInput
          label="Number of Payments *"
          value={form.months_to_payoff}
          onChange={(v) => set('months_to_payoff', v)}
          type="number"
          placeholder="e.g. 60"
          data-testid="months-to-payoff-input"
        />
        <ModalInput
          label="Monthly Payment Amount *"
          value={form.monthly_payment}
          onChange={(v) => set('monthly_payment', v)}
          type="number"
          placeholder="0"
          data-testid="monthly-payment-input"
        />
        <ModalInput
          label="Final Installment *"
          value={form.last_monthly_payment}
          onChange={(v) => set('last_monthly_payment', v)}
          type="number"
          placeholder="0"
          data-testid="last-monthly-payment-input"
        />
      </div>

      <div className="grid grid-cols-2 gap-3 p-3 border border-white/10 bg-white/5">
        <div>
          <p className="text-[11px] font-['Manrope'] text-white/40 mb-0.5">Finance Charge (calculated)</p>
          <p className="text-sm font-['Manrope'] text-white/80 tabular-nums" data-testid="finance-charge-display">
            {formatUsd(financeCharge)}
          </p>
        </div>
        <div>
          <p className="text-[11px] font-['Manrope'] text-white/40 mb-0.5">Total Due (calculated)</p>
          <p className="text-sm font-['Manrope'] text-white/80 tabular-nums" data-testid="total-credits-due-display">
            {formatUsd(totalCreditsDue)}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-4">
        <ModalCheckbox
          label="Include ERT Fee ($35)"
          checked={form.charge_ert_fee}
          onChange={(v) => set('charge_ert_fee', v)}
        />
        <ModalCheckbox
          label="Business Use"
          checked={form.business_use}
          onChange={(v) => set('business_use', v)}
        />
      </div>

      <div className="border-t border-white/10 pt-4 space-y-3">
        <h5 className="text-[11px] font-['Unbounded'] text-white/50 flex items-center gap-2">
          <Shield className="w-3 h-3" />
          Optional Insurance (Retail Contract)
        </h5>
        <div className="grid grid-cols-2 gap-3">
          <ModalInput label="Insurance Term" value={form.insurance_term} onChange={(v) => set('insurance_term', v)} placeholder="e.g. 60 months" />
          <ModalInput label="Insurance Premium ($)" value={form.insurance_premium} onChange={(v) => set('insurance_premium', v)} type="number" />
        </div>
        <div className="space-y-2">
          <ModalCheckbox
            label="Credit Life Insurance"
            checked={form.credit_life_insurance}
            onChange={(v) => set('credit_life_insurance', v)}
          />
          {form.credit_life_insurance && (
            <ModalInput
              label="Credit Life Insurance Premium ($)"
              value={form.credit_life_insurance_premium}
              onChange={(v) => set('credit_life_insurance_premium', v)}
              type="number"
            />
          )}
        </div>
        <div className="space-y-2">
          <ModalCheckbox
            label="Credit Disability Insurance"
            checked={form.credit_disability_insurance}
            onChange={(v) => set('credit_disability_insurance', v)}
          />
          {form.credit_disability_insurance && (
            <ModalInput
              label="Credit Disability Insurance Premium ($)"
              value={form.credit_disability_insurance_premium}
              onChange={(v) => set('credit_disability_insurance_premium', v)}
              type="number"
            />
          )}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <ModalInput label="Other Insurance Type" value={form.other_insurance_type} onChange={(v) => set('other_insurance_type', v)} />
          <ModalInput label="Other Insurance Premium ($)" value={form.other_insurance_premium} onChange={(v) => set('other_insurance_premium', v)} type="number" />
          <ModalInput label="Other Insurance Term" value={form.other_insurance_term} onChange={(v) => set('other_insurance_term', v)} className="col-span-2" />
        </div>
      </div>
    </div>
  );
}

function VehicleConditionFieldsSection({
  options,
  hint,
  selectedIssues,
  customValue,
  selectedCount,
  customDisabled,
  onToggle,
  onCustomChange,
}) {
  return (
    <div className="border-t border-white/10 pt-4">
      <h4 className="text-xs font-['Unbounded'] text-white/60 mb-2">Vehicle Condition</h4>
      <p className="text-[11px] font-['Manrope'] text-white/35 mb-3">
        Optional — select up to {VEHICLE_CONDITION_MAX} predefined options and/or one custom line.
        {hint ? ` ${hint}` : ''}
        {' '}({selectedCount}/{VEHICLE_CONDITION_MAX} selected)
      </p>
      <div className="space-y-2">
        {options.map((option) => {
          const checked = selectedIssues.includes(option);
          const atLimit = selectedCount >= VEHICLE_CONDITION_MAX;
          return (
            <ModalCheckbox
              key={option}
              label={option}
              checked={checked}
              disabled={!checked && atLimit}
              onChange={() => onToggle(option)}
            />
          );
        })}
      </div>
      <div className="mt-3">
        <ModalInput
          label={`Custom Condition${customDisabled ? ' (unavailable — 3 predefined selected)' : ''}`}
          value={customValue}
          onChange={onCustomChange}
          placeholder="One-line custom condition"
          maxLength={VEHICLE_CONDITION_CUSTOM_MAX}
          disabled={customDisabled}
        />
      </div>
    </div>
  );
}

function DamageDisclosureYesNoRow({ label, value, onYes, onNo }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 py-1">
      <span className="text-sm font-['Manrope'] text-white/80">{label}</span>
      <div className="flex gap-4">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={value === true}
            onChange={() => onYes()}
            className="accent-emerald-500"
          />
          <span className="text-xs font-['Manrope'] text-white/60">Yes</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={value === false}
            onChange={() => onNo()}
            className="accent-red-500"
          />
          <span className="text-xs font-['Manrope'] text-white/60">No</span>
        </label>
      </div>
    </div>
  );
}

function ModalInput({ label, value, onChange, type = 'text', placeholder, className = '', ...rest }) {
  return (
    <div className={className}>
      <label className="block text-xs font-['Manrope'] text-white/60 mb-1">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder || label}
        className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2 font-['Manrope'] focus:outline-none focus:border-blue-500/50"
        {...rest}
      />
    </div>
  );
}

function ModalCheckbox({ label, checked, onChange, disabled = false }) {
  return (
    <label className={`flex items-center gap-2 ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="accent-blue-500"
      />
      <span className="text-xs font-['Manrope'] text-white/60">{label}</span>
    </label>
  );
}
